import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Inbox, Send, FileText, Trash2, Star, Archive, RefreshCcw, Plus, Search, Tag, AlertCircle, Loader2, CheckCircle2, X, Sparkles, Zap, ExternalLink, Download, Paperclip, Reply, ReplyAll, Forward, Upload, Bold, Italic, Underline, List, ListOrdered, Link2, RemoveFormatting, Database, ArrowLeft, PenSquare, Clock, Pencil, ShieldAlert, Megaphone, Newspaper, Ban, ListFilter, ChevronDown, ChevronRight, Eye, EyeOff, MoreHorizontal, ArchiveRestore, Image as IconoImagen } from 'lucide-react';
import VisorDocumento from '../components/VisorDocumento';
import { revisarNovedades } from '../components/guia/novedades';
import useEsMovil from '../hooks/useEsMovil';
import useCapaAtras from '../hooks/useCapaAtras';
import DOMPurify from 'dompurify';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import api, { emailsAPI, facturasAPI, presupuestosAPI, clientesAPI, pendientesAPI, ayudaIaAPI, getAuthToken } from '../api/api';
import AyudaIAPanel from '../components/ayudaIa/AyudaIAPanel';
import PendientesPanel, { ModalPendiente, fechaManana, fechaLunesQueViene, formatoFechaCorta } from '../components/emails/PendientesPanel';
import SelectorBuzones, {
  EtiquetaBuzones, leerSeleccionBuzones, normalizarSeleccionBuzones, serializarSeleccionBuzones,
} from '../components/emails/SelectorBuzones';

// Hook para que todos los enlaces del correo abran en pestaña nueva de forma segura.
// Se registra una sola vez a nivel de módulo.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const sanitizarHtmlEmail = (html) => DOMPurify.sanitize(html || '', {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['style', 'form', 'input', 'button'],
  FORBID_ATTR: ['srcset'],
  ADD_ATTR: ['target'],
});

// Reescribe los src="cid:CONTENT-ID" (imágenes inline del propio mensaje MIME,
// que el navegador no sabe resolver) hacia el endpoint del backend que las
// recupera vía IMAP. Se aplica ANTES de sanitizar: el resultado son URLs
// https/relativas normales que DOMPurify deja pasar sin problema.
const resolverImagenesCid = (html, emailId) => {
  if (!html || !emailId || !/cid:/i.test(html)) return html || '';
  const token = getAuthToken() || '';
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  return html.replace(
    /(<img\b[^>]*?\bsrc\s*=\s*)(['"])cid:([^'"]+)\2/gi,
    (_m, prefijo, comilla, cid) => {
      const url = `${backend}/api/emails/${emailId}/inline?cid=${encodeURIComponent(cid)}&token=${encodeURIComponent(token)}`;
      return `${prefijo}${comilla}${url}${comilla}`;
    }
  );
};

const escaparHtml = (t) => String(t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Cuerpo COMPLETO del correo original para citarlo o reenviarlo: HTML saneado
// sin imágenes cid (no viajan en el mensaje nuevo y el endpoint /inline lleva
// token) o, si solo hay texto, el texto entero con sus saltos de línea.
const cuerpoOriginalHtml = (detalle) => {
  if (detalle?.body_html) {
    const sinCid = detalle.body_html.replace(/<img\b[^>]*\bsrc\s*=\s*(['"])cid:[^'"]*\1[^>]*>/gi, '');
    return sanitizarHtmlEmail(sinCid);
  }
  return '<p>' + escaparHtml(detalle?.body_text || detalle?.snippet || '').replace(/\n/g, '<br>') + '</p>';
};

// Cita para responder: "El <fecha>, <remitente> escribió:" + el hilo entero.
// Antes se citaba solo el texto plano recortado a 2.000 caracteres (y nada si el
// correo venía solo en HTML), y se perdía la parte de abajo del hilo.
const citaRespuestaHtml = (detalle) => {
  if (!detalle) return '';
  const de = detalle.remitente_nombre ? `${detalle.remitente_nombre} <${detalle.remitente}>` : (detalle.remitente || '');
  let fecha = '';
  try {
    fecha = new Date(detalle.fecha).toLocaleString('es-ES', { dateStyle: 'full', timeStyle: 'short' });
  } catch (e) { fecha = ''; }
  return '<br><br>'
    + `<div style="color:#555;font-size:13px">El ${escaparHtml(fecha)}, ${escaparHtml(de)} escribió:</div>`
    + `<blockquote style="margin:0 0 0 .8ex;border-left:2px solid #ccc;padding-left:1ex">${cuerpoOriginalHtml(detalle)}</blockquote>`;
};

const CARPETAS = [
  { id: 'iamail', label: 'IAmail', icon: Sparkles, destacado: true },
  { id: 'bandeja', label: 'Bandeja', icon: Inbox },
  { id: 'destacados', label: 'Destacados', icon: Star },
  { id: 'enviados', label: 'Enviados', icon: Send },
  { id: 'borradores', label: 'Borradores', icon: FileText, contador: true, guia: 'iamail-borradores' },
  { id: 'archivados', label: 'Archivados', icon: Archive },
  { id: 'backups', label: 'Backups', icon: Database },
  { id: 'spam', label: 'Spam', icon: ShieldAlert, filtro: true },
  { id: 'publicidad', label: 'Publicidad', icon: Megaphone, filtro: true },
  { id: 'boletines', label: 'Boletines', icon: Newspaper, filtro: true },
  { id: 'papelera', label: 'Papelera', icon: Trash2 },
];

const INTENCION_LABEL = {
  solicitud_presupuesto: 'Solicitudes de presupuesto',
  solicitud_factura: 'Solicitudes de factura',
  pregunta_pago: 'Consultas de pago',
  cambio_evento: 'Cambios en eventos',
  confirmacion: 'Confirmaciones',
  consulta_comercial: 'Consultas comerciales',
  interno: 'Interno',
  publicidad: 'Publicidad',
  otro: 'Otros',
  sin_clasificar: 'Sin clasificar',
  // Categorías del clasificador que el panel usa de fallback cuando el correo
  // no tiene `analisis_ia.intencion`.
  factura_soportada: 'Facturas recibidas',
  respuesta_cliente: 'Respuestas de cliente',
};

// Reparto de los chips por intención del panel IAmail en dos filas. Todo lo
// que no es comercial cae en Administración (incluidos "Otros", publicidad y
// sin clasificar: ahí se esconde trabajo de administración).
const INTENCIONES_COMERCIAL = ['solicitud_presupuesto', 'cambio_evento', 'respuesta_cliente', 'consulta_comercial'];
const ORDEN_ADMINISTRACION = [
  'solicitud_factura', 'factura_soportada', 'pregunta_pago', 'confirmacion', 'interno', 'otro', 'publicidad', 'sin_clasificar',
];

// Fecha corta de las listas del panel: solo la hora si es de hoy.
const fechaPanel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  return mismoDia
    ? d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Qué propone hacer la IA con un correo (para la lista "Con acciones").
const accionSugeridaDe = (e) => {
  const analisis = e?.analisis_ia || {};
  const interna = (analisis.accion_interna || '').trim();
  if (interna) return interna;
  const primera = (analisis.acciones_sugeridas || []).find((a) => a && (a.titulo || a.descripcion));
  return (primera?.titulo || primera?.descripcion || '').trim();
};

// Normaliza un valor de `por_intencion` (el backend antiguo mandaba un número).
const contadorIntencion = (v) => (typeof v === 'number' ? { propios: v, en_copia: 0 } : { propios: v?.propios || 0, en_copia: v?.en_copia || 0 });

const CATEGORIAS_IA = [
  { id: 'solicitud_presupuesto', label: 'Solicitudes de presupuesto', color: 'bg-green-100 text-green-800' },
  { id: 'cambio_evento', label: 'Cambios en eventos', color: 'bg-amber-100 text-amber-800' },
  { id: 'respuesta_cliente', label: 'Respuestas de cliente', color: 'bg-blue-100 text-blue-800' },
  { id: 'factura_soportada', label: 'Facturas recibidas', color: 'bg-purple-100 text-purple-800' },
  { id: 'interno', label: 'Interno', color: 'bg-gray-100 text-gray-800' },
  { id: 'publicidad', label: 'Publicidad', color: 'bg-red-100 text-red-800' },
  { id: 'otro', label: 'Otro', color: 'bg-slate-100 text-slate-800' },
];

// Correos en los que solo vamos en copia: el "Para" es de otro compañero, así
// que no reclaman nuestra atención, salvo que la IA detecte que piden algo a
// administración (facturas, datos fiscales, pagos...).
const EtiquetasCopia = ({ correo, clase = 'text-[10px]' }) => {
  if (!correo?.en_copia) return null;
  const paraAdmin = Boolean(correo.analisis_ia?.para_administracion);
  return (
    <>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${clase} bg-gray-100 text-gray-600`}>
        En copia
      </span>
      {paraAdmin && (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full ${clase} bg-amber-100 text-amber-800`}
          title={correo.analisis_ia?.motivo_administracion || 'La IA cree que pide algo a administración'}
        >
          Para administración
        </span>
      )}
    </>
  );
};

const formateaFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  if (mismoDia) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
};

const esPrevisualizable = (mime) => {
  if (!mime) return false;
  return (
    mime === 'application/pdf' ||
    mime.startsWith('image/') ||
    mime.startsWith('text/') ||
    mime === 'application/json'
  );
};

const FIRMAS_EMAIL = {
  alfonso: {
    id: 'alfonso',
    nombre: 'Alfonso Palomares',
  },
  administracion: {
    id: 'administracion',
    nombre: 'Administración',
  },
  ninguna: {
    id: 'ninguna',
    nombre: 'Sin firma',
  },
};

const firmaPorCuenta = (emailCuenta = '') => {
  const e = (emailCuenta || '').toLowerCase();
  if (e.startsWith('administracion@') || e.startsWith('admin@')) return 'administracion';
  if (e.startsWith('a.palomares@') || e.startsWith('alfonso@')) return 'alfonso';
  return 'alfonso';
};

const blobABase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const idx = String(result).indexOf(',');
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const formatoTamano = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Buzones elegidos en la bandeja y última cuenta usada para escribir: se recuerdan
// en el navegador (comodidad por usuario; si no hay almacenamiento, se ignora).
const LS_BUZON = 'emails_buzon';
const LS_ULTIMA_CUENTA = 'emails_ultima_cuenta';
const leerLS = (clave) => {
  try { return localStorage.getItem(clave); } catch (e) { return null; }
};
const guardarLS = (clave, valor) => {
  try {
    if (valor == null) localStorage.removeItem(clave);
    else localStorage.setItem(clave, valor);
  } catch (e) { /* almacenamiento bloqueado: ignorar */ }
};

const claveBorradorComposer = (c) =>
  c?.clave_borrador || `email_borrador_${c?.cuenta_id || 'x'}_${c?.in_reply_to_message_id || 'nuevo'}`;

// Huella del editor para saber si se ha cambiado algo desde que se abrió
// (al cerrar con cambios se pregunta si guardar el borrador).
const huellaComposer = (c) => JSON.stringify([
  c?.cuenta_id, c?.para || [], c?.cc || [], c?.bcc || [], c?.asunto || '', c?.body_html || '',
  (c?.adjuntos || []).map((a) => a.nombre), c?.firma_id || '',
]);
const composerModificado = (c) => Boolean(c) && huellaComposer(c) !== c.huella_inicial;

const guardarComposerLocal = (c) => {
  try {
    localStorage.setItem(claveBorradorComposer(c), JSON.stringify({
      para: c.para || [],
      cc: c.cc || [],
      bcc: c.bcc || [],
      asunto: c.asunto || '',
      body_html: c.body_html || '',
      firma_id: c.firma_id || null,
      ts: Date.now(),
    }));
  } catch (e) { /* almacenamiento lleno o bloqueado: ignorar */ }
};

// Cuerpo de un borrador del servidor para el editor: saneado y sin imágenes
// cid (no viajan en el mensaje nuevo). La marca de la firma
// (data-firma-duetto) se conserva: ahí vuelve a ir la firma al enviar.
const cuerpoBorradorHtml = (html) => sanitizarHtmlEmail(
  (html || '').replace(/<img\b[^>]*\bsrc\s*=\s*(['"])cid:[^'"]*\1[^>]*>/gi, '')
) || '<p></p>';

// Lo que se manda al servidor al enviar o guardar el borrador.
const datosComposer = (c) => ({
  cuenta_id: c.cuenta_id,
  para: c.para || [],
  cc: c.cc || [],
  bcc: c.bcc || [],
  asunto: c.asunto || '',
  body_html: c.body_html || '',
  firma_id: c.firma_id || 'ninguna',
  thread_id: c.thread_id,
  in_reply_to_message_id: c.in_reply_to_message_id,
  references: c.references || [],
  factura_id: c.factura_id || undefined,
  presupuesto_id: c.presupuesto_id || undefined,
  adjuntos_inline: (c.adjuntos || []).map((a) => ({
    nombre: a.nombre,
    mime_type: a.mime_type,
    contenido_base64: a.contenido_base64,
  })),
  borrador_id: c.borrador_id || undefined,
  borrador_message_id: c.borrador_message_id || undefined,
});

const CUENTA_DEFAULTS = {
  email: '',
  nombre_cuenta: '',
  imap_host: 'mail.duettocatering.com',
  imap_port: 993,
  imap_ssl: true,
  smtp_host: 'mail.duettocatering.com',
  smtp_port: 465,
  smtp_ssl: true,
  smtp_starttls: false,
  usuario_imap: '',
  password: '',
  carpeta_entrada: 'INBOX',
  carpeta_enviados: 'Sent',
  carpeta_papelera: 'Trash',
};

const CAMPOS_CONFIG_CUENTA = [
  'nombre_cuenta', 'imap_host', 'imap_port', 'imap_ssl', 'smtp_host', 'smtp_port', 'smtp_ssl',
  'smtp_starttls', 'usuario_imap', 'carpeta_entrada', 'carpeta_enviados', 'carpeta_papelera',
];

// Alta y edición de una cuenta de correo. En edición, cada bloque se habilita
// según lo que el usuario puede hacer: credenciales y servidores solo el
// propietario; quién accede, el propietario o dirección; el propietario, solo
// dirección.
const FormularioCuenta = ({ abierto, cuenta = null, onCerrar, onGuardada }) => {
  const edicion = !!cuenta;
  const [form, setForm] = useState(CUENTA_DEFAULTS);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [disponibles, setDisponibles] = useState({ usuarios: [], es_direccion: false, usuario_actual: null });
  const [propietario, setPropietario] = useState('');
  const [acceso, setAcceso] = useState([]);
  const [prueba, setPrueba] = useState(null); // { cargando } | resultado de /probar

  useEffect(() => {
    if (!abierto) return undefined;
    setError(null);
    setPrueba(null);
    if (cuenta) {
      const valores = {};
      Object.keys(CUENTA_DEFAULTS).forEach((k) => { valores[k] = cuenta[k] ?? CUENTA_DEFAULTS[k]; });
      setForm({ ...valores, password: '' });
      setPropietario(cuenta.usuario_id || '');
      setAcceso(cuenta.usuarios_acceso || []);
    } else {
      setForm(CUENTA_DEFAULTS);
      setPropietario('');
      setAcceso([]);
    }
    let cancelado = false;
    emailsAPI.usuariosDisponibles()
      .then(({ data }) => {
        if (cancelado || !data) return;
        setDisponibles(data);
        if (!cuenta) setPropietario(data.usuario_actual || '');
      })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [abierto, cuenta]);

  if (!abierto) return null;

  const puedeConfigurar = !edicion || !!cuenta.puede_configurar;
  const puedeAcceso = !edicion || !!cuenta.puede_gestionar_acceso;
  const puedePropietario = edicion ? !!cuenta.puede_cambiar_propietario : !!disponibles.es_direccion;
  const usuarios = disponibles.usuarios || [];

  const actualizar = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));
  const coincide = (entrada, u) => entrada === u.id
    || (!!u.email && String(entrada || '').toLowerCase() === u.email.toLowerCase());
  const tieneAcceso = (u) => acceso.some((a) => coincide(a, u));
  const alternarAcceso = (u) => {
    setAcceso((prev) => (prev.some((a) => coincide(a, u))
      ? prev.filter((a) => !coincide(a, u))
      : [...prev, u.id]));
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      if (!edicion) {
        const payload = { ...form, usuarios_acceso: acceso };
        if (!payload.usuario_imap) payload.usuario_imap = payload.email;
        if (propietario && propietario !== disponibles.usuario_actual) payload.usuario_id = propietario;
        const { data } = await emailsAPI.crearCuenta(payload);
        onGuardada(data);
      } else {
        const cambios = {};
        if (puedeConfigurar) {
          CAMPOS_CONFIG_CUENTA.forEach((k) => { cambios[k] = form[k]; });
          if (form.password) cambios.password = form.password;
        }
        if (puedeAcceso) cambios.usuarios_acceso = acceso;
        if (puedePropietario && propietario && propietario !== cuenta.usuario_id) cambios.usuario_id = propietario;
        await emailsAPI.actualizarCuenta(cuenta.id, cambios);
        onGuardada(null);
      }
      onCerrar();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo guardar la cuenta');
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    setPrueba({ cargando: true });
    try {
      const { data } = await emailsAPI.probarCuenta(cuenta.id);
      setPrueba(data);
    } catch (err) {
      setPrueba({ errores: { general: err.response?.data?.detail || 'No se pudo probar la cuenta' } });
    }
  };

  const bloqueado = !puedeConfigurar;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={guardar} className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{edicion ? `Cuenta ${cuenta.email}` : 'Conectar cuenta de correo'}</h2>
          <button type="button" onClick={onCerrar} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        {!edicion && (
          <p className="text-sm text-gray-600 mb-4">
            Introduce los datos IMAP/SMTP que te da tu hosting (cPanel, Plesk, Exchange…).
            Valores típicos para cPanel: IMAP en <code>mail.tudominio.com:993</code> con SSL y SMTP en <code>:465</code> con SSL.
          </p>
        )}
        {edicion && bloqueado && (
          <p className="text-sm text-gray-600 mb-4">
            Solo el propietario ({cuenta.propietario_nombre || 'otro usuario'}) puede cambiar los datos de conexión.
            {puedeAcceso ? ' Tú puedes cambiar quién accede a este buzón.' : ''}
          </p>
        )}

        {error && (
          <div className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Campo label="Correo" required>
            <input type="email" value={form.email} onChange={(e) => actualizar('email', e.target.value)} className={inputCls} placeholder="administracion@duettocatering.com" required disabled={edicion} />
          </Campo>
          <Campo label="Nombre mostrado">
            <input type="text" value={form.nombre_cuenta || ''} onChange={(e) => actualizar('nombre_cuenta', e.target.value)} className={inputCls} placeholder="Administración Duetto" disabled={bloqueado} />
          </Campo>
          <Campo label="Contraseña" required={!edicion}>
            <input type="password" value={form.password} onChange={(e) => actualizar('password', e.target.value)} className={inputCls} required={!edicion} disabled={bloqueado} placeholder={edicion ? 'Dejar vacío para no cambiarla' : ''} autoComplete="new-password" />
          </Campo>
          <Campo label="Usuario IMAP">
            <input type="text" value={form.usuario_imap || ''} onChange={(e) => actualizar('usuario_imap', e.target.value)} className={inputCls} placeholder="(por defecto, el correo)" disabled={bloqueado} />
          </Campo>
        </div>

        <h3 className="mt-5 mb-2 text-sm font-semibold text-gray-700">IMAP (entrada)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Campo label="Host" required>
            <input type="text" value={form.imap_host} onChange={(e) => actualizar('imap_host', e.target.value)} className={inputCls} required disabled={bloqueado} />
          </Campo>
          <Campo label="Puerto" required>
            <input type="number" value={form.imap_port} onChange={(e) => actualizar('imap_port', Number(e.target.value))} className={inputCls} required disabled={bloqueado} />
          </Campo>
          <Campo label="Conexión">
            <select value={form.imap_ssl ? 'ssl' : 'plain'} onChange={(e) => actualizar('imap_ssl', e.target.value === 'ssl')} className={inputCls} disabled={bloqueado}>
              <option value="ssl">SSL / TLS</option>
              <option value="plain">Sin cifrar</option>
            </select>
          </Campo>
        </div>

        <h3 className="mt-5 mb-2 text-sm font-semibold text-gray-700">SMTP (salida)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Campo label="Host" required>
            <input type="text" value={form.smtp_host} onChange={(e) => actualizar('smtp_host', e.target.value)} className={inputCls} required disabled={bloqueado} />
          </Campo>
          <Campo label="Puerto" required>
            <input type="number" value={form.smtp_port} onChange={(e) => actualizar('smtp_port', Number(e.target.value))} className={inputCls} required disabled={bloqueado} />
          </Campo>
          <Campo label="Conexión">
            <select
              value={form.smtp_ssl && !form.smtp_starttls ? 'ssl' : form.smtp_starttls ? 'starttls' : 'plain'}
              onChange={(e) => {
                const v = e.target.value;
                actualizar('smtp_ssl', v === 'ssl');
                actualizar('smtp_starttls', v === 'starttls');
              }}
              className={inputCls}
              disabled={bloqueado}
            >
              <option value="ssl">SSL / TLS (puerto 465)</option>
              <option value="starttls">STARTTLS (puerto 587)</option>
              <option value="plain">Sin cifrar</option>
            </select>
          </Campo>
        </div>

        <h3 className="mt-5 mb-2 text-sm font-semibold text-gray-700">Quién usa este buzón</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Campo label="Propietario">
            <select
              value={propietario}
              onChange={(e) => setPropietario(e.target.value)}
              className={inputCls}
              disabled={!puedePropietario}
              data-testid="cuenta-propietario"
            >
              {!usuarios.some((u) => u.id === propietario) && propietario && (
                <option value={propietario}>{(edicion && cuenta.propietario_nombre) || propietario}</option>
              )}
              {usuarios.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </Campo>
          <div>
            <span className="text-xs font-medium text-gray-600">También lo ven</span>
            <div className="mt-1 space-y-1" data-testid="cuenta-acceso">
              {usuarios.length === 0 && <div className="text-xs text-gray-400">Cargando…</div>}
              {usuarios.map((u) => {
                const esProp = u.id === propietario;
                return (
                  <label key={u.id} className={`flex items-center gap-2 text-sm ${!puedeAcceso || esProp ? 'text-gray-400' : 'text-gray-700'}`}>
                    <input
                      type="checkbox"
                      checked={esProp || tieneAcceso(u)}
                      disabled={!puedeAcceso || esProp}
                      onChange={() => alternarAcceso(u)}
                    />
                    {u.nombre}{esProp ? ' (propietario)' : ''}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Quien tiene acceso ve los correos del buzón en IAmail y en la ficha del presupuesto y puede responder desde él.
          Los datos de conexión solo los cambia el propietario.
        </p>

        {edicion && puedeConfigurar && (
          <div className="mt-4 text-sm">
            <button type="button" onClick={probar} className="px-3 py-1.5 border rounded-lg hover:bg-gray-50 inline-flex items-center gap-2" disabled={prueba?.cargando}>
              {prueba?.cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              Probar la conexión guardada
            </button>
            {prueba && !prueba.cargando && (
              <div className="mt-2 text-xs space-y-0.5">
                {'imap' in prueba && <div className={prueba.imap ? 'text-green-700' : 'text-red-700'}>IMAP: {prueba.imap ? 'correcto' : (prueba.errores?.imap || 'error')}</div>}
                {'smtp' in prueba && <div className={prueba.smtp ? 'text-green-700' : 'text-red-700'}>SMTP: {prueba.smtp ? 'correcto' : (prueba.errores?.smtp || 'error')}</div>}
                {prueba.errores?.general && <div className="text-red-700">{prueba.errores.general}</div>}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancelar</button>
          <button type="submit" disabled={guardando || (edicion && !puedeConfigurar && !puedeAcceso && !puedePropietario)} className="px-4 py-2 text-sm bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary disabled:opacity-60 inline-flex items-center gap-2">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {edicion ? 'Guardar' : 'Probar y guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary';
const Campo = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600">{label}{required && <span className="text-red-500">*</span>}</span>
    <div className="mt-1">{children}</div>
  </label>
);

// Carpetas del filtro automático de spam, publicidad y boletines.
const CARPETAS_FILTRO = ['spam', 'publicidad', 'boletines'];

// Chip con la regla que movió el correo ("Publicidad: mailing sin relación").
const ChipFiltro = ({ correo, clase = 'text-[10px]' }) => {
  const ca = correo?.carpeta_auto;
  if (!ca?.carpeta) return null;
  const color = ca.carpeta === 'spam'
    ? 'bg-red-50 text-red-700 border-red-100'
    : ca.carpeta === 'publicidad'
      ? 'bg-orange-50 text-orange-700 border-orange-100'
      : 'bg-indigo-50 text-indigo-700 border-indigo-100';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${clase} ${color}`} title="Movido por el filtro automático">
      {ca.etiqueta || ca.carpeta}
    </span>
  );
};

const textoALista = (texto) => (texto || '')
  .split(/[\n,;]+/)
  .map((x) => x.trim())
  .filter(Boolean);

// Pantalla mínima para ver y editar las listas del filtro.
const ModalListasFiltro = ({ abierto, onCerrar }) => {
  const [datos, setDatos] = useState(null);
  const [blanca, setBlanca] = useState('');
  const [bandeja, setBandeja] = useState('');
  const [negra, setNegra] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);

  useEffect(() => {
    if (!abierto) return undefined;
    let cancelado = false;
    setError(null);
    setOk(null);
    (async () => {
      try {
        const { data } = await emailsAPI.listasFiltro();
        if (cancelado) return;
        setDatos(data);
        setBlanca((data.lista_blanca || []).join('\n'));
        setBandeja((data.siempre_bandeja || []).join('\n'));
        setNegra((data.lista_negra || []).join('\n'));
      } catch (e) {
        if (!cancelado) setError(e.response?.data?.detail || 'No se pudieron cargar las listas');
      }
    })();
    return () => { cancelado = true; };
  }, [abierto]);

  if (!abierto) return null;

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    setOk(null);
    try {
      const { data } = await emailsAPI.guardarListasFiltro({
        lista_blanca: textoALista(blanca),
        siempre_bandeja: textoALista(bandeja),
        lista_negra: textoALista(negra),
      });
      setBlanca((data.lista_blanca || []).join('\n'));
      setBandeja((data.siempre_bandeja || []).join('\n'));
      setNegra((data.lista_negra || []).join('\n'));
      setOk('Listas guardadas');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron guardar las listas');
    } finally {
      setGuardando(false);
    }
  };

  const recalcular = async () => {
    try {
      const { data } = await emailsAPI.recalcularConocidos();
      setDatos((prev) => ({ ...(prev || {}), ...data }));
      setOk('Dominios conocidos recalculados');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron recalcular los dominios conocidos');
    }
  };

  const areaCls = 'w-full h-40 px-3 py-2 text-xs font-mono border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary';
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold">Filtro de spam y publicidad</h2>
          <button type="button" onClick={onCerrar} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Una entrada por línea: un dominio (vale para sus subdominios) o una dirección completa.
          Los remitentes con relación real (clientes, contactos, proveedores, a quien hemos escrito) ya cuentan como conocidos
          {datos ? ` (${datos.dominios_conocidos || 0} dominios y ${datos.direcciones_conocidas || 0} direcciones)` : ''}.
        </p>
        {error && (
          <div className="mb-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        {ok && (
          <div className="mb-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <CheckCircle2 className="w-4 h-4" /> {ok}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs font-semibold text-gray-700">Lista blanca</span>
            <span className="block text-[11px] text-gray-500 mb-1">Servicios que usamos. Nunca van a Spam ni a Publicidad; sus envíos masivos van a Boletines salvo que pidan algo.</span>
            <textarea value={blanca} onChange={(e) => setBlanca(e.target.value)} className={areaCls} data-testid="filtro-lista-blanca" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-gray-700">Siempre en la bandeja</span>
            <span className="block text-[11px] text-gray-500 mb-1">El filtro no los mueve nunca (se añaden al devolver a la bandeja un boletín).</span>
            <textarea value={bandeja} onChange={(e) => setBandeja(e.target.value)} className={areaCls} data-testid="filtro-siempre-bandeja" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-gray-700">Lista negra</span>
            <span className="block text-[11px] text-gray-500 mb-1">Siempre a Spam (se añaden con el botón Es spam).</span>
            <textarea value={negra} onChange={(e) => setNegra(e.target.value)} className={areaCls} data-testid="filtro-lista-negra" />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <button type="button" onClick={recalcular} className="px-3 py-2 text-sm rounded-lg border text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2">
            <RefreshCcw className="w-4 h-4" /> Recalcular dominios conocidos
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onCerrar} className="px-4 py-2 text-sm rounded-lg border text-gray-700 hover:bg-gray-50">Cerrar</button>
            <button type="button" onClick={guardar} disabled={guardando} className="px-4 py-2 text-sm bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary disabled:opacity-60 inline-flex items-center gap-2">
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RecipientChips =({ label, value = [], onChange, placeholder }) => {
  const [draft, setDraft] = useState('');

  const agregar = (raw) => {
    const limpio = (raw || '').trim().replace(/^<|>$/g, '');
    if (!limpio) return;
    if (value.includes(limpio)) return;
    onChange([...value, limpio]);
  };

  const quitar = (email) => onChange(value.filter((e) => e !== email));

  const onKeyDown = (e) => {
    if (['Enter', 'Tab', ',', ';'].includes(e.key)) {
      if (draft.trim()) {
        e.preventDefault();
        agregar(draft);
        setDraft('');
      } else if (e.key === 'Tab') {
        // dejar tab navegar
      } else {
        e.preventDefault();
      }
    } else if (e.key === 'Backspace' && !draft && value.length) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  const onBlur = () => {
    if (draft.trim()) { agregar(draft); setDraft(''); }
  };

  const onPaste = (e) => {
    const texto = e.clipboardData.getData('text');
    if (!/[,;\s]/.test(texto)) return;
    e.preventDefault();
    const partes = texto.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const next = [...value];
    partes.forEach((p) => { if (!next.includes(p)) next.push(p); });
    onChange(next);
  };

  return (
    <div className="flex items-start gap-2">
      <label className="text-xs text-gray-500 w-16 pt-1.5 shrink-0">{label}</label>
      <div className="flex-1 min-h-[34px] border rounded px-2 py-1 flex flex-wrap gap-1 focus-within:border-duetto-primary transition">
        {value.map((email) => (
          <span key={email} className="inline-flex items-center gap-1 bg-duetto-primary/10 text-duetto-primary text-xs px-2 py-0.5 rounded-full max-w-full">
            <span className="truncate">{email}</span>
            <button
              type="button"
              onClick={() => quitar(email)}
              className="hover:text-red-600 shrink-0"
              title="Quitar"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onPaste={onPaste}
          // Direcciones de correo: teclado con @ en el móvil y sin mayúsculas ni autocorrección
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="flex-1 min-w-[140px] outline-none text-sm bg-transparent"
          placeholder={value.length === 0 ? placeholder : ''}
        />
      </div>
    </div>
  );
};

const BotonBarra = ({ title, onClick, children, activo = false }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`p-1.5 rounded transition ${activo ? 'bg-duetto-primary/10 text-duetto-primary' : 'text-gray-700 hover:bg-gray-200'}`}
  >
    {children}
  </button>
);

const EditorRich = ({ value, onChange }) => {
  const ref = useRef(null);

  // Inicializamos el HTML del editor una sola vez. Las ediciones posteriores
  // se propagan vía onInput; si reescribimos innerHTML perderíamos el cursor.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd, arg = null) => {
    // document.execCommand está deprecated pero sigue implementado en todos los
    // navegadores y es suficiente para un editor de correo básico.
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
    ref.current?.focus();
  };

  const ponerEnlace = () => {
    const url = window.prompt('URL del enlace:', 'https://');
    if (url) exec('createLink', url);
  };

  return (
    <div>
      <div className="flex items-center gap-0.5 border border-b-0 rounded-t bg-gray-50 px-2 py-1">
        <BotonBarra title="Negrita (Ctrl+B)" onClick={() => exec('bold')}><Bold className="w-3.5 h-3.5" /></BotonBarra>
        <BotonBarra title="Cursiva (Ctrl+I)" onClick={() => exec('italic')}><Italic className="w-3.5 h-3.5" /></BotonBarra>
        <BotonBarra title="Subrayado (Ctrl+U)" onClick={() => exec('underline')}><Underline className="w-3.5 h-3.5" /></BotonBarra>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <BotonBarra title="Lista" onClick={() => exec('insertUnorderedList')}><List className="w-3.5 h-3.5" /></BotonBarra>
        <BotonBarra title="Lista numerada" onClick={() => exec('insertOrderedList')}><ListOrdered className="w-3.5 h-3.5" /></BotonBarra>
        <div className="w-px h-4 bg-gray-300 mx-1" />
        <BotonBarra title="Enlace" onClick={ponerEnlace}><Link2 className="w-3.5 h-3.5" /></BotonBarra>
        <BotonBarra title="Quitar formato" onClick={() => exec('removeFormat')}><RemoveFormatting className="w-3.5 h-3.5" /></BotonBarra>
      </div>
      {/* En el móvil: 16 px para que iOS no haga zoom al enfocarlo (la regla de
          16 px de index.css solo cubre input, select y textarea) y sin tope de
          alto: se desplaza el cuerpo del editor, que ocupa toda la pantalla. */}
      <div
        ref={ref}
        contentEditable
        onInput={() => onChange(ref.current?.innerHTML || '')}
        className="min-h-[200px] md:min-h-[260px] md:max-h-[55vh] md:overflow-y-auto border rounded-b p-3 text-base md:text-sm focus:outline-none focus:border-duetto-primary prose prose-sm max-w-none"
        suppressContentEditableWarning
      />
    </div>
  );
};

// Botón de la barra inferior del correo en el móvil: icono arriba y etiqueta debajo
const BotonBarraMovil = ({ icono: Icono, iconoClase = '', etiqueta, onClick, disabled = false, tono = 'normal', ...resto }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex-1 min-w-0 min-h-[56px] flex flex-col items-center justify-center gap-0.5 px-1 active:bg-gray-100 disabled:opacity-50 ${
      tono === 'principal' ? 'text-duetto-primary' : tono === 'peligro' ? 'text-red-600' : 'text-gray-700'
    }`}
    {...resto}
  >
    <Icono className={`w-5 h-5 shrink-0 ${iconoClase}`} />
    <span className="text-[11px] leading-tight truncate max-w-full">{etiqueta}</span>
  </button>
);

// Fila de la hoja "Más" del correo en el móvil (48 px de alto)
const FilaHojaMovil = ({ icono: Icono = null, iconoRelleno = false, texto, onClick, tono = 'normal', centrado = false, ...resto }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full min-h-[48px] flex items-center gap-3 px-5 text-[15px] active:bg-gray-100 ${
      centrado ? 'justify-center font-medium' : 'text-left'
    } ${tono === 'peligro' ? 'text-red-600' : 'text-gray-800'}`}
    {...resto}
  >
    {Icono && <Icono className="w-5 h-5 shrink-0" fill={iconoRelleno ? 'currentColor' : 'none'} />}
    <span className="truncate">{texto}</span>
  </button>
);

const FilaDato = ({ label, children }) => (
  <div className="flex justify-between items-start gap-3 py-1 text-xs">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-900 text-right break-words">{children || '—'}</span>
  </div>
);

const formatoEUR = (v) => (v == null ? '' : Number(v).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }));
const formatoFecha = (v) => (v ? new Date(v).toLocaleDateString('es-ES') : '');

const DetallePresupuestoPreview = ({ datos, onAdjuntar, adjuntando, etiquetaBoton = 'Adjuntar este documento' }) => {
  const menus = datos.menus_personalizados || [];
  const totalPlatos = menus.reduce((acc, m) => acc + (m.platos_ids?.length || m.platos?.length || 0), 0);
  const jornadas = datos.jornadas || [];
  const bebidas = datos.bebidas || [];
  const incompleto = [];
  if (!datos.fecha_evento) incompleto.push('fecha del evento');
  if (!datos.num_personas) incompleto.push('nº de personas');
  if (!datos.lugar_evento && !datos.direccion_exacta) incompleto.push('lugar');
  if (menus.length === 0) incompleto.push('menús');
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-500">Presupuesto</div>
          <div className="text-lg font-bold text-gray-900">
            #{datos.numero || datos.num_presupuesto || '—'}
            {datos.estado && (
              <span className="ml-2 text-[10px] uppercase font-semibold bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">{datos.estado}</span>
            )}
          </div>
          <div className="text-sm text-gray-700 mt-0.5">{datos.cliente_nombre || '—'}</div>
        </div>
        <button
          onClick={onAdjuntar}
          disabled={adjuntando}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-duetto-primary text-white text-sm hover:bg-duetto-primary/90 disabled:opacity-50"
        >
          {adjuntando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          {etiquetaBoton}
        </button>
      </div>
      {incompleto.length > 0 && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong>Le falta por incluir:</strong> {incompleto.join(', ')}.
          </div>
        </div>
      )}
      <div className="bg-white border rounded p-3">
        <div className="text-xs font-semibold text-gray-700 mb-2">Datos del evento</div>
        <FilaDato label="Fecha">{formatoFecha(datos.fecha_evento)}</FilaDato>
        <FilaDato label="Horario">{(datos.hora_inicio || '') + (datos.hora_fin ? ` - ${datos.hora_fin}` : '')}</FilaDato>
        <FilaDato label="Personas">{datos.num_personas}</FilaDato>
        <FilaDato label="Lugar">{datos.lugar_evento}</FilaDato>
        <FilaDato label="Dirección">{datos.direccion_exacta}</FilaDato>
        {datos.distancia_km && <FilaDato label="Distancia">{datos.distancia_km} km</FilaDato>}
      </div>
      <div className="bg-white border rounded p-3">
        <div className="text-xs font-semibold text-gray-700 mb-2">Menús ({menus.length})</div>
        {menus.length === 0 ? (
          <div className="text-xs text-gray-500 italic">Sin menús definidos</div>
        ) : (
          <div className="space-y-1.5">
            {menus.map((m, i) => (
              <div key={i} className="flex justify-between text-xs gap-2">
                <span className="text-gray-800 truncate">{m.nombre_menu || `Menú ${i + 1}`}</span>
                <span className="text-gray-500 shrink-0">
                  {(m.platos_ids?.length || m.platos?.length || 0)} platos
                  {m.precio_unitario != null ? ` · ${formatoEUR(m.precio_unitario)}` : ''}
                </span>
              </div>
            ))}
            <div className="text-[11px] text-gray-500 pt-1 border-t">{totalPlatos} platos en total</div>
          </div>
        )}
      </div>
      {(bebidas.length > 0 || jornadas.length > 0) && (
        <div className="bg-white border rounded p-3 text-xs space-y-1">
          {bebidas.length > 0 && <FilaDato label="Bebidas">{bebidas.length} seleccionadas</FilaDato>}
          {jornadas.length > 0 && <FilaDato label="Jornadas">{jornadas.length}</FilaDato>}
        </div>
      )}
      {(() => {
        const subtotal = datos.importe_base ?? datos.subtotal ?? 0;
        const descuento = datos.descuento || 0;
        const baseConDescuento = subtotal - descuento;
        const total = datos.importe_total ?? datos.total ?? 0;
        // Si el IVA viene guardado como porcentaje (0.10) lo inferimos del total.
        let iva = datos.iva;
        if (iva == null || iva < 1) {
          iva = Math.max(0, total - baseConDescuento);
        }
        return (
          <div className="bg-white border rounded p-3">
            <FilaDato label="Subtotal">{formatoEUR(subtotal)}</FilaDato>
            {descuento > 0 && <FilaDato label="Descuento">-{formatoEUR(descuento)}</FilaDato>}
            <FilaDato label="IVA">{formatoEUR(iva)}</FilaDato>
            <div className="flex justify-between pt-2 mt-1 border-t font-bold text-sm">
              <span>Total</span>
              <span className="text-duetto-primary">{formatoEUR(total)}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const DetalleFacturaPreview = ({ datos, onAdjuntar, adjuntando }) => {
  const incompleto = [];
  if (!datos.numero_factura && !datos.numero) incompleto.push('número');
  if (!datos.cliente_nif) incompleto.push('NIF del cliente');
  const lineas = datos.lineas || datos.items || [];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-gray-500">Factura</div>
          <div className="text-lg font-bold text-gray-900">
            #{datos.numero_factura || datos.numero || '—'}
            {datos.tipo_factura && (
              <span className="ml-2 text-[10px] uppercase font-semibold bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">{datos.tipo_factura}</span>
            )}
            {datos.estado_pago && (
              <span className={`ml-2 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${datos.estado_pago === 'pagado' ? 'bg-green-100 text-green-800' : datos.estado_pago === 'parcial' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>{datos.estado_pago}</span>
            )}
          </div>
          <div className="text-sm text-gray-700 mt-0.5">{datos.cliente_nombre || '—'}</div>
        </div>
        <button
          onClick={onAdjuntar}
          disabled={adjuntando}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-duetto-primary text-white text-sm hover:bg-duetto-primary/90 disabled:opacity-50"
        >
          {adjuntando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
          Adjuntar este
        </button>
      </div>
      {incompleto.length > 0 && (
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong>Le falta por incluir:</strong> {incompleto.join(', ')}.
          </div>
        </div>
      )}
      <div className="bg-white border rounded p-3">
        <div className="text-xs font-semibold text-gray-700 mb-2">Datos</div>
        <FilaDato label="Fecha factura">{formatoFecha(datos.fecha_factura || datos.fecha_emision)}</FilaDato>
        <FilaDato label="Fecha operación">{formatoFecha(datos.fecha_operacion)}</FilaDato>
        <FilaDato label="Cliente NIF">{datos.cliente_nif}</FilaDato>
        <FilaDato label="Dirección">{datos.cliente_direccion}</FilaDato>
        {datos.numero_presupuesto && <FilaDato label="Presupuesto asociado">#{datos.numero_presupuesto}</FilaDato>}
      </div>
      {lineas.length > 0 && (
        <div className="bg-white border rounded p-3">
          <div className="text-xs font-semibold text-gray-700 mb-2">Conceptos ({lineas.length})</div>
          <div className="space-y-1">
            {lineas.slice(0, 8).map((l, i) => (
              <div key={i} className="flex justify-between text-xs gap-2">
                <span className="text-gray-800 truncate">{l.descripcion || l.concepto || `Línea ${i + 1}`}</span>
                <span className="text-gray-500 shrink-0">{formatoEUR(l.total || l.importe)}</span>
              </div>
            ))}
            {lineas.length > 8 && <div className="text-[11px] text-gray-500">+{lineas.length - 8} más…</div>}
          </div>
        </div>
      )}
      <div className="bg-white border rounded p-3">
        <FilaDato label="Base imponible">{formatoEUR(datos.base_imponible || datos.subtotal)}</FilaDato>
        <FilaDato label="IVA">{formatoEUR(datos.iva)}</FilaDato>
        {datos.irpf != null && datos.irpf !== 0 && <FilaDato label="IRPF">{formatoEUR(datos.irpf)}</FilaDato>}
        <div className="flex justify-between pt-2 mt-1 border-t font-bold text-sm">
          <span>Total</span>
          <span className="text-duetto-primary">{formatoEUR(datos.importe_total || datos.total)}</span>
        </div>
      </div>
    </div>
  );
};

const Emails = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // Móvil (< 768 px, el `md` de Tailwind): lectura con barra inferior y editor a pantalla completa
  const esMovil = useEsMovil();
  const [cuentas, setCuentas] = useState([]);
  // IAmail es la vista por defecto al entrar en Emails (petición del usuario, 30-sep-2026).
  const [carpeta, setCarpeta] = useState('iamail');
  // Navegación a presupuesto/cliente/factura: desde IAmail siempre en pestaña
  // nueva para no perder el hilo del panel; en el resto de carpetas, in situ.
  // En el móvil siempre dentro de la app (la PWA no abre pestañas): si hay un
  // correo abierto, su entrada del historial se sustituye por la ruta nueva y
  // al volver con atrás se llega a /emails.
  const irA = (ruta) => {
    if (esMovil) {
      const enCapa = Array.isArray(window.history.state?.duettoCapas) && window.history.state.duettoCapas.length > 0;
      navigate(ruta, enCapa ? { replace: true } : undefined);
      return;
    }
    if (carpeta === 'iamail') {
      window.open(ruta, '_blank', 'noopener');
    } else {
      navigate(ruta);
    }
  };
  const [categoria, setCategoria] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [error, setError] = useState(null);
  const [formAbierto, setFormAbierto] = useState(false);
  const [hojaMas, setHojaMas] = useState(false); // móvil: hoja "Más" de la barra inferior del correo
  const [analisisDesplegado, setAnalisisDesplegado] = useState(false); // móvil: análisis IA completo
  const [subiendoDrive, setSubiendoDrive] = useState(() => new Set()); // claves `${emailId}-${idx}`
  const [driveEstado, setDriveEstado] = useState(null); // { conectado: bool, email? }
  const [analisis, setAnalisis] = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [presupuestoVinculadoInfo, setPresupuestoVinculadoInfo] = useState(null);
  const [iaPanel, setIaPanel] = useState(null);
  const [cargandoIa, setCargandoIa] = useState(false);
  const [iaRangoDias, setIaRangoDias] = useState(2);
  const [intencionExpandida, setIntencionExpandida] = useState(null); // 'solicitud_presupuesto' etc.
  const [emailsIntencion, setEmailsIntencion] = useState({ propios: [], en_copia: [] });
  const [verTareas, setVerTareas] = useState(false); // tarjeta "Con acciones" desplegada
  const [tareasOcultas, setTareasOcultas] = useState(() => new Set()); // descartadas en esta vista
  const [tareasAnadidas, setTareasAnadidas] = useState(() => new Set()); // ya pasadas a pendientes
  const [tareaOcupada, setTareaOcupada] = useState(null);
  const [verFilaSecundaria, setVerFilaSecundaria] = useState(false); // "Ver comercial" / "Ver administración"
  const [verContactos, setVerContactos] = useState(false);
  const [leidosPanel, setLeidosPanel] = useState(() => new Set()); // abiertos desde el panel antes de recargar
  const otroAutoRef = useRef(null); // rango+buzones para los que ya se abrió "Otros" solo
  const [cargandoIntencion, setCargandoIntencion] = useState(false);
  const [verEnCopia, setVerEnCopia] = useState(false); // tarjeta "Comercial, en copia"
  const [emailsEnCopia, setEmailsEnCopia] = useState([]);
  const [cargandoEnCopia, setCargandoEnCopia] = useState(false);
  const [componer, setComponer] = useState(null); // { para, cc, asunto, body_html, thread_id, in_reply_to_message_id, cuenta_id, factura_id?, factura_numero?, adjuntos?, firma_id?, enviando }
  const [cierreComposer, setCierreComposer] = useState(false); // "¿Guardar borrador?" al cerrar con cambios
  const [abriendoBorrador, setAbriendoBorrador] = useState(null); // id del borrador que se está abriendo
  // Vista previa de un adjunto del editor o de la factura adjunta: { titulo, url?, contenidoBase64?, mime? }
  const [visorAdjunto, setVisorAdjunto] = useState(null);
  const [adjuntarMenuAbierto, setAdjuntarMenuAbierto] = useState(false);
  const [pickerAdjuntos, setPickerAdjuntos] = useState(null); // { tipo, items, cargando, filtro, modo }
  const [adjuntandoRef, setAdjuntandoRef] = useState(false);
  const [detalleSeleccionado, setDetalleSeleccionado] = useState(null); // { id, tipo, cargando, datos }
  const [aviso, setAviso] = useState(null); // { texto, tono } donde tono = 'ok' | 'info'
  const [pendientesVersion, setPendientesVersion] = useState(0); // sube para refrescar el panel de pendientes
  const [pendienteHilo, setPendienteHilo] = useState(null); // pendiente abierto del hilo del correo abierto
  const [modalPendienteAbierto, setModalPendienteAbierto] = useState(false);
  const [contadoresFiltro, setContadoresFiltro] = useState(null); // { spam: {total, no_leidos}, ... }
  const [listasFiltroAbierto, setListasFiltroAbierto] = useState(false);
  // Ayuda IA: solo para quien la tiene activada en el servidor (GET /ayuda-ia/estado)
  const [ayudaIaActivaServidor, setAyudaIaActivaServidor] = useState(false);
  const [ayudaIaAbierta, setAyudaIaAbierta] = useState(false);
  const fileInputRef = useRef(null);
  const redactarPendienteRef = useRef(null); // deep-link ?redactar=1 en espera de que carguen las cuentas
  const accionTrasCargarRef = useRef(null); // { emailId, tipo: 'responder', borradorHtml?, facturaId? }

  const cuentasConectadas = useMemo(() => cuentas.length > 0, [cuentas]);
  // Buzones elegidos: [] es "Todos los buzones" (vista unificada); si no, los ids.
  const [buzones, setBuzones] = useState(() => leerSeleccionBuzones(leerLS(LS_BUZON)));
  const [mostrarCopias, setMostrarCopias] = useState(false);
  const [cuentaEditando, setCuentaEditando] = useState(null);
  const cuentasPorId = useMemo(() => Object.fromEntries(cuentas.map((c) => [c.id, c])), [cuentas]);
  const cuentasEnvio = useMemo(() => cuentas.filter((c) => c.tiene_smtp !== false), [cuentas]);
  const filtroBuzon = useMemo(() => {
    const ids = buzones.filter((id) => cuentasPorId[id]);
    return ids.length ? { cuentas_ids: ids.join(',') } : {};
  }, [buzones, cuentasPorId]);
  const variosBuzones = cuentas.length > 1;
  useEffect(() => { guardarLS(LS_BUZON, serializarSeleccionBuzones(buzones)); }, [buzones]);
  useEffect(() => {
    // Quita los buzones recordados que ya no son accesibles; si no queda
    // ninguno (o quedan todos), vuelve a la vista unificada.
    if (cuentas.length === 0 || buzones.length === 0) return;
    const normal = normalizarSeleccionBuzones(buzones, cuentas);
    if (normal.join(',') !== buzones.join(',')) setBuzones(normal);
  }, [cuentas, buzones]);
  const cambiarBuzones = (ids) => { setBuzones(ids); setSeleccionado(null); };
  // Ojo por buzon en el lateral: [] es "todos visibles"; si no, los ids visibles.
  const buzonVisible = (id) => buzones.length === 0 || buzones.includes(id);
  const alternarBuzon = (id) => {
    const todos = cuentas.map((c) => c.id);
    const visibles = buzones.length === 0 ? todos : buzones.filter((b) => cuentasPorId[b]);
    const siguiente = visibles.includes(id) ? visibles.filter((b) => b !== id) : [...visibles, id];
    if (siguiente.length === 0) {
      toast({ title: 'Al menos un buzón tiene que quedar visible', variant: 'destructive' });
      return;
    }
    cambiarBuzones(siguiente.length === todos.length ? [] : siguiente);
  };
  // Buzones a mostrar en la etiqueta de un correo: con varios buzones a la
  // vista siempre; con un solo buzón elegido, solo si el mensaje está en varios.
  const idsBuzones = (e) => {
    if (!e) return [];
    const ids = e.en_buzones?.length ? e.en_buzones : [e.cuenta_id];
    if (buzones.length !== 1 && variosBuzones) return ids;
    return ids.length > 1 ? ids : [];
  };
  // Cuenta desde la que se responde: la que recibió el correo (o la del
  // buzón en "Para" si el mensaje está en varios), si tiene envío configurado.
  const cuentaParaResponder = (d) => {
    const candidatas = [d?.cuenta_respuesta, d?.cuenta_id];
    const valida = candidatas.find((id) => id && cuentasPorId[id] && cuentasPorId[id].tiene_smtp !== false);
    return valida || (cuentasEnvio[0] || cuentas[0] || {}).id || d?.cuenta_id;
  };

  // Cierra el correo abierto (columna de detalle o drawer de IAmail).
  const cerrarDetalle = () => {
    setSeleccionado(null);
    setDetalle(null);
    setAnalisis(null);
  };

  // Móvil: el botón atrás cierra el correo abierto (y la hoja "Más" o el visor de
  // un adjunto si están encima) en vez de salir de IAmail. El del editor va
  // junto a intentarCerrarComposer.
  useCapaAtras('correo', esMovil && !!seleccionado, cerrarDetalle);
  useCapaAtras('hoja-mas', esMovil && hojaMas, () => setHojaMas(false));
  useCapaAtras('visor', esMovil && !!visorAdjunto, () => setVisorAdjunto(null));
  // Al cambiar de correo: análisis plegado y hoja "Más" cerrada
  useEffect(() => {
    setAnalisisDesplegado(false);
    setHojaMas(false);
    setAyudaIaAbierta(false);
  }, [seleccionado]);
  // Ayuda IA: se pregunta una vez al entrar si está activada para este usuario
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const { data } = await ayudaIaAPI.estado();
        if (!cancelado) setAyudaIaActivaServidor(data?.activo === true);
      } catch (_err) {
        if (!cancelado) setAyudaIaActivaServidor(false);
      }
    })();
    return () => { cancelado = true; };
  }, []);
  // Solo con un correo abierto que no sea un borrador
  const ayudaIaActiva = ayudaIaActivaServidor && !!detalle && !detalle.es_borrador;
  // Abrir un correo no cambia la ruta: que mire la novedad de Ayuda IA cuando el botón aparece
  useEffect(() => {
    if (ayudaIaActiva) setTimeout(() => revisarNovedades(), 300);
  }, [ayudaIaActiva]);
  // Abrir un correo no cambia la ruta: que miren si hay novedad del móvil que enseñar
  const correoAbiertoMovil = esMovil && !!seleccionado;
  useEffect(() => {
    if (correoAbiertoMovil) revisarNovedades();
  }, [correoAbiertoMovil]);
  // El panel carga después de entrar: si trae un correo en copia para
  // administración, que se mire la novedad de "En copia, pero para ti".
  const hayCopiaParaAdmin = (iaPanel?.nuevos_sin_leer || []).some((x) => x.en_copia && x.para_administracion);
  useEffect(() => {
    if (hayCopiaParaAdmin) revisarNovedades();
  }, [hayCopiaParaAdmin]);

  // Autoguardado del compositor: si se cierra sin querer, el texto se recupera.
  // Es la red de seguridad; el borrador que cuenta es el del servidor.
  useEffect(() => {
    if (!componer || componer.enviando) return;
    const timer = setTimeout(() => guardarComposerLocal(componer), 800);
    return () => clearTimeout(timer);
  }, [componer]);

  const cargarCuentas = useCallback(async () => {
    try {
      const { data } = await emailsAPI.listarCuentas();
      setCuentas(data || []);
    } catch (e) {
      setCuentas([]);
    }
  }, []);

  const cargarEmails = useCallback(async () => {
    if (!cuentasConectadas) {
      setEmails([]);
      setTotal(0);
      return;
    }
    setCargando(true);
    setError(null);
    try {
      const params = { carpeta, limit: 100, ...filtroBuzon };
      if (categoria) params.categoria_ia = categoria;
      if (busqueda.trim()) params.q = busqueda.trim();
      if (mostrarCopias) params.mostrar_copias = true;
      const { data } = await emailsAPI.listar(params);
      setEmails(data.emails || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cargando correos');
      setEmails([]);
    } finally {
      setCargando(false);
    }
  }, [cuentasConectadas, carpeta, categoria, busqueda, filtroBuzon, mostrarCopias]);

  const cargarResumen = useCallback(async () => {
    if (!cuentasConectadas) return;
    try {
      const { data } = await emailsAPI.resumenDia(filtroBuzon);
      setResumen(data);
    } catch (e) {
      setResumen(null);
    }
  }, [cuentasConectadas, filtroBuzon]);

  const cargarContadoresFiltro = useCallback(async () => {
    if (!cuentasConectadas) return;
    try {
      const { data } = await emailsAPI.contadoresFiltro(filtroBuzon);
      setContadoresFiltro(data);
    } catch (e) {
      setContadoresFiltro(null);
    }
  }, [cuentasConectadas, filtroBuzon]);

  const cargarDetalle = useCallback(async (id) => {
    if (!id) {
      setDetalle(null);
      setAnalisis(null);
      return;
    }
    try {
      const { data } = await emailsAPI.obtener(id);
      setDetalle(data);
      setAnalisis(data.analisis_ia || null);
      setEmails((prev) => prev.map((e) => (e.id === id ? { ...e, leido: true } : e)));
    } catch (e) {
      setDetalle(null);
      setAnalisis(null);
    }
  }, []);

  // Sondeo del análisis pendiente: el análisis lo hace Claude en tandas de 5 min.
  const sondeoAnalisisRef = useRef(null);
  const pararSondeoAnalisis = useCallback(() => {
    if (sondeoAnalisisRef.current) {
      clearInterval(sondeoAnalisisRef.current);
      sondeoAnalisisRef.current = null;
    }
  }, []);

  const sondearAnalisis = useCallback((id) => {
    pararSondeoAnalisis();
    let vueltas = 0;
    const intervalo = setInterval(async () => {
      vueltas += 1;
      try {
        const { data } = await emailsAPI.obtener(id);
        // Si mientras tanto se cambió de correo o se paró el sondeo, no se toca nada.
        if (sondeoAnalisisRef.current !== intervalo) return;
        if (data?.analisis_ia?.intencion && !data.analisis_ia_reanalizar) {
          pararSondeoAnalisis();
          setAnalisis(data.analisis_ia);
          setDetalle((prev) => (prev && prev.id === id
            ? { ...prev, analisis_ia: data.analisis_ia, analisis_ia_modelo: data.analisis_ia_modelo, analisis_ia_reanalizar: false }
            : prev));
          return;
        }
      } catch (_err) {
        // Se reintenta en la siguiente vuelta.
      }
      if (vueltas >= 12 && sondeoAnalisisRef.current === intervalo) pararSondeoAnalisis();
    }, 30000);
    sondeoAnalisisRef.current = intervalo;
  }, [pararSondeoAnalisis]);

  const lanzarAnalisis = useCallback(async (id, forzar = false) => {
    if (!id) return;
    setAnalizando(true);
    try {
      const { data } = await emailsAPI.analizar(id, forzar);
      if (data.no_analizable) {
        // Enviados, borradores, papelera y filtrados: la tanda no los analiza.
        pararSondeoAnalisis();
        setAnalisis({ ...(data.analisis || {}), no_analizable: true, mensaje: data.mensaje });
      } else if (data.pendiente) {
        setAnalisis({ pendiente: true, mensaje: data.mensaje, ...(data.analisis || {}) });
        sondearAnalisis(id);
      } else {
        pararSondeoAnalisis();
        setAnalisis(data.analisis || null);
      }
    } catch (e) {
      setAnalisis({ error: e.response?.data?.detail || 'No se pudo analizar' });
    } finally {
      setAnalizando(false);
    }
  }, [sondearAnalisis, pararSondeoAnalisis]);

  // Al cambiar de correo (o al desmontar) se para el sondeo del análisis.
  useEffect(() => pararSondeoAnalisis, [detalle?.id, pararSondeoAnalisis]);

  // Al abrir un email, si aún no tiene análisis, dispararlo automáticamente.
  useEffect(() => {
    if (detalle && !detalle.analisis_ia && !analizando) {
      lanzarAnalisis(detalle.id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalle?.id]);

  const refrescarDriveEstado = useCallback(async () => {
    try {
      const { data } = await emailsAPI.driveEstado();
      setDriveEstado(data);
      return data;
    } catch (_err) {
      setDriveEstado({ conectado: false });
      return { conectado: false };
    }
  }, []);

  useEffect(() => { cargarCuentas(); }, [cargarCuentas]);
  useEffect(() => { cargarEmails(); }, [cargarEmails]);
  useEffect(() => { cargarResumen(); }, [cargarResumen]);
  useEffect(() => { cargarContadoresFiltro(); }, [cargarContadoresFiltro, carpeta]);
  useEffect(() => { cargarDetalle(seleccionado); }, [seleccionado, cargarDetalle]);
  useEffect(() => { refrescarDriveEstado(); }, [refrescarDriveEstado]);

  // Cuando el correo tiene un presupuesto vinculado, traemos su número/fecha
  // para mostrarlo en la cabecera. Si se quita el vínculo, limpiamos.
  useEffect(() => {
    const pid = detalle?.presupuesto_id || null;
    if (!pid) {
      setPresupuestoVinculadoInfo(null);
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        const { data } = await presupuestosAPI.getOne(pid);
        if (!cancelado) setPresupuestoVinculadoInfo(data || { id: pid });
      } catch {
        if (!cancelado) setPresupuestoVinculadoInfo({ id: pid });
      }
    })();
    return () => { cancelado = true; };
  }, [detalle?.presupuesto_id]);

  // Pendiente abierto del hilo del correo que se está leyendo. Se recarga
  // también cuando sube pendientesVersion (algo ha cambiado en el libro).
  useEffect(() => {
    const threadId = detalle?.thread_id || null;
    if (!threadId) {
      setPendienteHilo(null);
      return undefined;
    }
    let cancelado = false;
    (async () => {
      try {
        const { data } = await pendientesAPI.porHilo(threadId);
        if (!cancelado) setPendienteHilo(data || null);
      } catch (e) {
        if (!cancelado) setPendienteHilo(null);
      }
    })();
    return () => { cancelado = true; };
  }, [detalle?.thread_id, pendientesVersion]);

  // Si entramos con ?email=<id> (enlace desde la ficha de un evento),
  // abrimos directamente ese correo y limpiamos el query param.
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const carpetaParam = searchParams.get('carpeta');
    const redactarParam = searchParams.get('redactar');
    const carpetaValida = carpetaParam && CARPETAS.some((c) => c.id === carpetaParam);
    if (redactarParam === '1') {
      // El compositor necesita al menos una cuenta cargada; guardamos los
      // parámetros y disparamos abrirNuevoMensaje cuando cuentas esté listo.
      const escape = (t) => (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const cuerpo = searchParams.get('cuerpo') || '';
      const cuerpoHtml = cuerpo
        ? '<p>' + escape(cuerpo).split('\n\n').map((p) => p.replace(/\n/g, '<br>')).join('</p><p>') + '</p>'
        : '';
      const paraRaw = searchParams.get('para') || '';
      redactarPendienteRef.current = {
        para: paraRaw ? [paraRaw] : [],
        asunto: searchParams.get('asunto') || '',
        cuerpoHtml,
      };
    }
    if (emailParam || carpetaValida || redactarParam) {
      if (emailParam) setSeleccionado(emailParam);
      // Los enlaces directos a un correo o al compositor siguen abriendo la
      // bandeja clásica, salvo que pidan otra carpeta.
      if (carpetaValida) setCarpeta(carpetaParam);
      else if (emailParam || redactarParam) setCarpeta('bandeja');
      const nuevos = new URLSearchParams(searchParams);
      nuevos.delete('email');
      nuevos.delete('carpeta');
      nuevos.delete('redactar');
      nuevos.delete('para');
      nuevos.delete('asunto');
      nuevos.delete('cuerpo');
      setSearchParams(nuevos, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dispara el compositor del deep-link ?redactar=1 en cuanto haya cuentas cargadas.
  useEffect(() => {
    if (cuentas.length > 0 && redactarPendienteRef.current) {
      const params = redactarPendienteRef.current;
      redactarPendienteRef.current = null;
      abrirNuevoMensaje(params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentas]);

  // Lista de un chip de intención: mismo filtro que su contador (propios y en copia).
  const abrirIntencion = useCallback(async (intencion) => {
    setIntencionExpandida(intencion);
    setCargandoIntencion(true);
    try {
      const { data } = await emailsAPI.iaPanelCorreos({ intencion, dias: iaRangoDias, ...filtroBuzon });
      setEmailsIntencion({ propios: data?.propios || [], en_copia: data?.en_copia || [] });
    } catch (e) {
      setEmailsIntencion({ propios: [], en_copia: [] });
    } finally {
      setCargandoIntencion(false);
    }
  }, [iaRangoDias, filtroBuzon]);

  const cargarIaPanel = useCallback(async () => {
    if (!cuentasConectadas) return;
    setCargandoIa(true);
    try {
      const { data } = await emailsAPI.iaPanel({ dias: iaRangoDias, ...filtroBuzon });
      setIaPanel(data);
      setLeidosPanel(new Set());
      // "Otros" se abre solo la primera vez que se carga cada rango/buzón si
      // tiene correos propios: ahí se esconde trabajo de administración.
      const clave = `${iaRangoDias}|${filtroBuzon.cuentas_ids || ''}`;
      if (otroAutoRef.current !== clave) {
        otroAutoRef.current = clave;
        if (contadorIntencion(data?.por_intencion?.otro).propios > 0) abrirIntencion('otro');
      }
    } catch (e) {
      setIaPanel(null);
    } finally {
      setCargandoIa(false);
    }
  }, [cuentasConectadas, iaRangoDias, filtroBuzon, abrirIntencion]);

  useEffect(() => {
    if (carpeta === 'iamail') cargarIaPanel();
  }, [carpeta, cargarIaPanel]);

  const toggleIntencion = useCallback((intencion) => {
    if (intencionExpandida === intencion) {
      setIntencionExpandida(null);
      setEmailsIntencion({ propios: [], en_copia: [] });
      return;
    }
    abrirIntencion(intencion);
  }, [intencionExpandida, abrirIntencion]);

  // Abre un correo desde cualquier lista del panel. Se pinta ya como leído
  // (el backend lo marca al abrirlo) y sale de "sin leer" en la siguiente recarga.
  const abrirCorreoPanel = useCallback((id) => {
    setSeleccionado(id);
    setLeidosPanel((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const anadirTareaAPendientes = useCallback(async (t) => {
    const titulo = (accionSugeridaDe(t) || t.asunto || 'Revisar correo').slice(0, 150);
    setTareaOcupada(t.id);
    try {
      await pendientesAPI.crear({ titulo, email_id: t.id, detalle: t.analisis_ia?.resumen || null });
      setTareasAnadidas((prev) => {
        const next = new Set(prev);
        next.add(t.id);
        return next;
      });
      setPendientesVersion((v) => v + 1);
      toast({ title: 'Añadido a pendientes', description: titulo });
    } catch (err) {
      toast({
        title: 'No se pudo añadir a pendientes',
        description: err.response?.data?.detail || 'Inténtalo de nuevo',
        variant: 'destructive',
      });
    } finally {
      setTareaOcupada(null);
    }
  }, [toast]);

  const descartarTarea = useCallback(async (t) => {
    setTareaOcupada(t.id);
    try {
      await emailsAPI.actualizar(t.id, { estado_gestion: 'resuelto' });
      setTareasOcultas((prev) => {
        const next = new Set(prev);
        next.add(t.id);
        return next;
      });
      setPendientesVersion((v) => v + 1);
      toast({ title: 'Correo descartado', description: 'Marcado como resuelto' });
      cargarIaPanel();
    } catch (err) {
      toast({
        title: 'No se pudo descartar',
        description: err.response?.data?.detail || 'Inténtalo de nuevo',
        variant: 'destructive',
      });
    } finally {
      setTareaOcupada(null);
    }
  }, [toast, cargarIaPanel]);

  const toggleEnCopia = useCallback(async () => {
    if (verEnCopia) {
      setVerEnCopia(false);
      setEmailsEnCopia([]);
      return;
    }
    setVerEnCopia(true);
    setCargandoEnCopia(true);
    try {
      const { data } = await emailsAPI.listar({ en_copia: true, carpeta: 'bandeja', desde_dias: iaRangoDias, limit: 100, ...filtroBuzon });
      setEmailsEnCopia(data.emails || []);
    } catch (e) {
      setEmailsEnCopia([]);
    } finally {
      setCargandoEnCopia(false);
    }
  }, [verEnCopia, iaRangoDias, filtroBuzon]);

  // Al cambiar el rango temporal o el buzón, las listas desplegadas dejan de ser válidas.
  useEffect(() => {
    setIntencionExpandida(null);
    setEmailsIntencion({ propios: [], en_copia: [] });
    setVerEnCopia(false);
    setEmailsEnCopia([]);
    setVerTareas(false);
    setVerFilaSecundaria(false);
  }, [iaRangoDias, filtroBuzon]);

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      await emailsAPI.sync();
      await cargarEmails();
      await cargarResumen();
      await cargarContadoresFiltro();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error sincronizando');
    } finally {
      setSincronizando(false);
    }
  };

  const accionAdjunto = async (emailId, idx, nombre, mime, modo) => {
    if (modo === 'ver') {
      // El visor común lo descarga con la sesión y, en el móvil, abre el PDF
      // aparte (dentro de un iframe sale en blanco o con una sola página).
      setVisorAdjunto({ titulo: nombre || `adjunto-${idx}`, url: `/emails/${emailId}/adjuntos/${idx}`, mime });
      return;
    }
    try {
      const { data } = await emailsAPI.descargarAdjunto(emailId, idx);
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre || `adjunto-${idx}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'No se pudo descargar el adjunto';
      try {
        if (e.response?.data instanceof Blob) {
          const texto = await e.response.data.text();
          const parsed = JSON.parse(texto);
          if (parsed?.detail) msg = parsed.detail;
        } else if (e.response?.data?.detail) {
          msg = e.response.data.detail;
        }
      } catch (_) {}
      setError(msg);
    }
  };

  const mostrarAviso = (texto, tono = 'ok') => {
    setAviso({ texto, tono });
    setTimeout(() => setAviso((cur) => (cur && cur.texto === texto ? null : cur)), 3500);
  };

  const iniciarConexionDrive = useCallback(() => {
    const token = getAuthToken();
    if (!token) {
      mostrarAviso('Inicia sesión primero', 'info');
      return;
    }
    const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const url = `${backend}/api/emails/drive/conectar?token=${encodeURIComponent(token)}`;
    // Ventana emergente para la autorización; el backend postMessage al cerrar.
    const popup = window.open(url, 'conectar_drive', 'width=520,height=640');
    const listener = (ev) => {
      if (ev?.data?.type === 'drive_conectado') {
        window.removeEventListener('message', listener);
        refrescarDriveEstado();
        mostrarAviso('Google Drive conectado correctamente');
      }
    };
    window.addEventListener('message', listener);
    // Plan B por si la ventana se cierra manualmente sin postMessage.
    const poll = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(poll);
        window.removeEventListener('message', listener);
        refrescarDriveEstado();
      }
    }, 1500);
  }, [refrescarDriveEstado]);

  const moverAdjuntoADrive = async (emailId, idx) => {
    const clave = `${emailId}-${idx}`;
    if (subiendoDrive.has(clave)) return;
    // Chequeo rápido: si sabemos que no está conectado, ofrece conectar antes.
    if (driveEstado && !driveEstado.conectado) {
      mostrarAviso('Drive no está conectado. Pulsa "Conectar Google Drive".', 'info');
      iniciarConexionDrive();
      return;
    }
    setSubiendoDrive((prev) => {
      const next = new Set(prev);
      next.add(clave);
      return next;
    });
    try {
      const { data } = await emailsAPI.subirAdjuntoADrive(emailId, idx);
      mostrarAviso('Archivo en Google Drive');
      setDetalle((prev) => {
        if (!prev || prev.id !== emailId) return prev;
        const nuevos = (prev.adjuntos || []).map((a, i) => (
          i === idx ? { ...a, drive_file_id: data.drive_file_id, drive_url: data.url, drive_nombre: data.nombre } : a
        ));
        return { ...prev, adjuntos: nuevos, adjuntos_en_drive: true };
      });
      setEmails((prev) => prev.map((e) => (
        e.id === emailId ? { ...e, adjuntos_en_drive: true } : e
      )));
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || 'No se pudo subir a Drive';
      if (status === 409) {
        // Drive no conectado: dispara el flow.
        mostrarAviso('Autoriza tu cuenta de Google para subir a Drive', 'info');
        iniciarConexionDrive();
      } else {
        mostrarAviso(msg, 'info');
      }
    } finally {
      setSubiendoDrive((prev) => {
        const next = new Set(prev);
        next.delete(clave);
        return next;
      });
    }
  };

  // Quita un correo de todas las listas del panel IAmail sin esperar a la recarga.
  const quitarDelPanel = (id) => {
    setIaPanel((prev) => prev ? {
      ...prev,
      nuevos_sin_leer: (prev.nuevos_sin_leer || []).filter((e) => e.id !== id),
      tareas: (prev.tareas || []).filter((e) => e.id !== id),
      nuevos_total: Math.max(0, (prev.nuevos_total || 0) - ((prev.nuevos_sin_leer || []).some((e) => e.id === id) ? 1 : 0)),
      sin_leer_total: Math.max(0, (prev.sin_leer_total || 0) - ((prev.nuevos_sin_leer || []).some((e) => e.id === id && e.leido === false) ? 1 : 0)),
    } : prev);
    setEmailsIntencion((prev) => ({
      propios: (prev.propios || []).filter((e) => e.id !== id),
      en_copia: (prev.en_copia || []).filter((e) => e.id !== id),
    }));
    setEmailsEnCopia((prev) => prev.filter((e) => e.id !== id));
  };

  // "Listo" desde el panel: el usuario da el correo por gestionado sin abrirlo.
  const marcarListoPanel = async (email) => {
    try {
      await emailsAPI.actualizar(email.id, { estado_gestion: 'resuelto' });
      quitarDelPanel(email.id);
      if (detalle?.id === email.id || seleccionado === email.id) cerrarDetalle();
      setPendientesVersion((v) => v + 1);
      mostrarAviso('Marcado como listo');
      cargarIaPanel();
    } catch (e) {
      mostrarAviso('No se pudo marcar como listo', 'info');
    }
  };

  const togglePropiedad = async (email, prop) => {
    const nuevoValor = !email[prop];
    try {
      const { data } = await emailsAPI.actualizar(email.id, { [prop]: nuevoValor });
      // Buzón de otra persona: archivar o borrar no mueve nada en su buzón
      // real; el correo queda como gestionado en el ERP (regla del 16-oct-2026).
      if (data?.solo_erp) {
        const cambiosErp = { estado_gestion: 'resuelto', tarea_descartada: true };
        setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, ...cambiosErp } : e)));
        if (detalle?.id === email.id) setDetalle({ ...detalle, ...cambiosErp });
        if (carpeta === 'iamail') quitarDelPanel(email.id);
        setPendientesVersion((v) => v + 1);
        mostrarAviso(data.aviso || 'No es tu buzón: queda como gestionado en el ERP', 'info');
        cargarIaPanel();
        return;
      }
      // Decidir si el email sigue perteneciendo a la carpeta abierta ahora mismo
      const fueraDeCarpetaActual = (
        (carpeta === 'bandeja' && ((prop === 'papelera' && nuevoValor) || (prop === 'archivado' && nuevoValor))) ||
        (carpeta === 'papelera' && prop === 'papelera' && !nuevoValor) ||
        (carpeta === 'archivados' && prop === 'archivado' && !nuevoValor) ||
        (carpeta === 'destacados' && prop === 'destacado' && !nuevoValor)
      );
      // En IAmail el correo se ve en un drawer: al archivarlo o enviarlo a la
      // papelera cerramos el drawer y refrescamos el panel.
      const saleDeIaPanel = (
        carpeta === 'iamail' && nuevoValor && (prop === 'papelera' || prop === 'archivado')
      );
      if (saleDeIaPanel) {
        setEmails((prev) => prev.filter((e) => e.id !== email.id));
        quitarDelPanel(email.id);
        if (detalle?.id === email.id || seleccionado === email.id) cerrarDetalle();
        cargarIaPanel();
      } else if (fueraDeCarpetaActual) {
        setEmails((prev) => prev.filter((e) => e.id !== email.id));
        if (detalle?.id === email.id) cerrarDetalle();
      } else {
        setEmails((prev) => prev.map((e) => (e.id === email.id ? { ...e, [prop]: nuevoValor } : e)));
        if (detalle?.id === email.id) setDetalle({ ...detalle, [prop]: nuevoValor });
      }
      // Archivar o enviar a la papelera cierra los pendientes del hilo en backend.
      if (prop === 'archivado' || prop === 'papelera') setPendientesVersion((v) => v + 1);
      if (prop === 'papelera' && nuevoValor) mostrarAviso('Movido a la papelera');
      else if (prop === 'papelera' && !nuevoValor) mostrarAviso('Restaurado de la papelera');
      else if (prop === 'archivado' && nuevoValor) mostrarAviso('Archivado (se ve en la carpeta "Archivados")');
      else if (prop === 'archivado' && !nuevoValor) mostrarAviso('Desarchivado');
    } catch (e) {
      mostrarAviso(e.response?.data?.detail || 'No se pudo actualizar el correo', 'info');
    }
  };

  // ==================== Filtro de spam, publicidad y boletines ====================
  // Tras mover un correo: fuera de la lista si ya no pertenece a la carpeta
  // abierta; en IAmail se cierra el drawer y se refresca el panel.
  const trasMoverFiltro = (correo, saleDeCarpeta, cambios) => {
    if (carpeta === 'iamail') {
      cerrarDetalle();
      cargarIaPanel();
    } else if (saleDeCarpeta) {
      setEmails((prev) => prev.filter((e) => e.id !== correo.id));
      setTotal((t) => Math.max(0, t - 1));
      if (detalle?.id === correo.id) cerrarDetalle();
    } else {
      setEmails((prev) => prev.map((e) => (e.id === correo.id ? { ...e, ...cambios } : e)));
      if (detalle?.id === correo.id) setDetalle((d) => (d ? { ...d, ...cambios } : d));
    }
    cargarContadoresFiltro();
    cargarResumen();
  };

  const moverABandejaFiltro = async (correo) => {
    try {
      const { data } = await emailsAPI.moverABandeja(correo.id);
      const destino = data.lista === 'siempre_bandeja' ? '"Siempre en la bandeja"' : 'la lista blanca';
      mostrarAviso(data.entrada ? `Devuelto a la bandeja. ${data.entrada} añadido a ${destino}` : 'Devuelto a la bandeja');
      trasMoverFiltro(correo, CARPETAS_FILTRO.includes(carpeta), { carpeta_auto: null, carpeta_manual: 'bandeja', leido: data.leido });
    } catch (e) {
      mostrarAviso(e.response?.data?.detail || 'No se pudo devolver el correo a la bandeja', 'info');
    }
  };

  const marcarSpamFiltro = async (correo) => {
    try {
      const { data } = await emailsAPI.marcarSpam(correo.id);
      mostrarAviso(data.entrada ? `Movido a Spam. ${data.entrada} añadido a la lista negra` : 'Movido a Spam');
      trasMoverFiltro(correo, carpeta !== 'spam', {
        carpeta_auto: { carpeta: 'spam', regla: 'usuario', etiqueta: 'Spam: marcado a mano' },
        leido: true,
      });
    } catch (e) {
      mostrarAviso(e.response?.data?.detail || 'No se pudo marcar como spam', 'info');
    }
  };

  // ==================== Componer / responder ====================
  // Abre el editor guardando su huella inicial (para saber si se ha cambiado algo).
  const abrirComposer = (datos) => {
    // El editor es un modal (no cambia la ruta): que miren si hay novedad que enseñar
    setTimeout(() => revisarNovedades(), 300);
    setCierreComposer(false);
    setComponer({ ...datos, huella_inicial: huellaComposer(datos) });
  };

  const abrirResponder = ({ borradorHtml = '', facturaId = null, facturaNumero = null, todos = false } = {}) => {
    if (!detalle) return;
    const asunto = /^re:/i.test(detalle.asunto || '') ? detalle.asunto : `Re: ${detalle.asunto || ''}`;
    // Si el remitente es interno, mejor responder al cliente externo (destinatarios / emails del body)
    const ES_INTERNO = /@duettocatering\.com$/i;
    let para = detalle.remitente ? [detalle.remitente] : [];
    if (para[0] && ES_INTERNO.test(para[0])) {
      const externos = (detalle.destinatarios || []).filter((d) => d && !ES_INTERNO.test(d));
      if (externos.length > 0) para = [externos[0]];
    }
    // Responder a todos: el resto de destinatarios y copias pasan a CC,
    // descartando nuestras propias cuentas y los que ya están en "Para".
    // Se responde desde la cuenta que recibió el correo (o la del buzón en
    // "Para" si llegó a varios), aunque el usuario tenga otra principal.
    const cuentaId = cuentaParaResponder(detalle);
    const cuentaEmail = (cuentasPorId[cuentaId] || {}).email || '';
    let cc = [];
    if (todos) {
      // Fuera solo la dirección desde la que se envía: los compañeros que
      // iban en copia (otros buzones nuestros) siguen en copia.
      const misCorreos = new Set([cuentaEmail.toLowerCase()]);
      const paraSet = new Set(para.map((p) => p.toLowerCase()));
      const ccTodos = [...(detalle.destinatarios || []), ...(detalle.cc || [])]
        .map((d) => (d || '').trim().replace(/^<|>$/g, ''))
        .filter((d) => d && !misCorreos.has(d.toLowerCase()) && !paraSet.has(d.toLowerCase()));
      cc = Array.from(new Set(ccTodos.map((d) => d.toLowerCase()))).map((low) => ccTodos.find((d) => d.toLowerCase() === low));
    }
    const cita = citaRespuestaHtml(detalle);
    let guardado = null;
    try {
      const raw = localStorage.getItem(`email_borrador_${detalle.cuenta_id || 'x'}_${detalle.message_id || 'nuevo'}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Ignorar borradores de más de 7 días
        if (parsed && parsed.ts && Date.now() - parsed.ts < 7 * 24 * 3600 * 1000 && (parsed.body_html || '').replace(/<[^>]+>/g, '').trim()) {
          guardado = parsed;
        }
      }
    } catch (e) { /* ignorar */ }
    abrirComposer({
      para: guardado?.para?.length ? guardado.para : para,
      cc: guardado?.cc?.length ? guardado.cc : cc,
      mostrar_cc: cc.length > 0,
      asunto: guardado?.asunto || asunto,
      body_html: guardado ? guardado.body_html : ((borradorHtml || '<p></p>') + cita),
      thread_id: detalle.thread_id || null,
      in_reply_to_message_id: detalle.message_id || null,
      cuenta_id: cuentaId,
      clave_borrador: `email_borrador_${detalle.cuenta_id || 'x'}_${detalle.message_id || 'nuevo'}`,
      factura_id: facturaId,
      factura_numero: facturaNumero,
      firma_id: guardado?.firma_id || firmaPorCuenta(cuentaEmail),
      presupuesto_id: detalle.presupuesto_id || null,
      borrador_recuperado: !!guardado,
      modo: todos ? 'responder_todos' : 'responder',
      abierto_en: Date.now(),
      enviando: false,
    });
  };

  // Reenviar: parte siempre del correo original (no recupera borradores) y
  // arrastra los adjuntos del mensaje descargándolos del backend.
  const abrirReenviar = async () => {
    if (!detalle) return;
    const escape = escaparHtml;
    const asunto = /^(fwd?|rv):/i.test(detalle.asunto || '') ? detalle.asunto : `Fwd: ${detalle.asunto || ''}`;
    const de = detalle.remitente_nombre ? `${detalle.remitente_nombre} <${detalle.remitente}>` : (detalle.remitente || '');
    const cabecera = '<p></p><br><div style="color:#555;font-size:13px">---------- Mensaje reenviado ----------<br>'
      + `De: ${escape(de)}<br>`
      + `Fecha: ${escape(new Date(detalle.fecha).toLocaleString('es-ES'))}<br>`
      + `Asunto: ${escape(detalle.asunto || '')}<br>`
      + ((detalle.destinatarios || []).length ? `Para: ${escape((detalle.destinatarios || []).join(', '))}<br>` : '')
      + ((detalle.cc || []).length ? `Cc: ${escape((detalle.cc || []).join(', '))}<br>` : '')
      + '</div><br>';
    const cuerpoOriginal = cuerpoOriginalHtml(detalle);
    const cuentaReenvio = cuentaParaResponder(detalle);
    const cuentaEmail = (cuentasPorId[cuentaReenvio] || {}).email || '';
    abrirComposer({
      para: [],
      cc: [],
      bcc: [],
      asunto,
      body_html: cabecera + cuerpoOriginal,
      thread_id: null,
      in_reply_to_message_id: null,
      cuenta_id: cuentaReenvio,
      factura_id: null,
      factura_numero: null,
      firma_id: firmaPorCuenta(cuentaEmail),
      presupuesto_id: detalle.presupuesto_id || null,
      borrador_recuperado: false,
      modo: 'reenviar',
      clave_borrador: `email_borrador_${detalle.cuenta_id || 'x'}_fwd_${detalle.message_id || detalle.id}`,
      adjuntos: [],
      adjuntando_originales: (detalle.adjuntos || []).length,
      abierto_en: Date.now(),
      enviando: false,
    });
    // Adjuntos originales: se descargan del backend y se añaden en base64.
    const adjuntos = detalle.adjuntos || [];
    for (let idx = 0; idx < adjuntos.length; idx += 1) {
      const a = adjuntos[idx];
      try {
        if (a.tamano && a.tamano > 20 * 1024 * 1024) {
          mostrarAviso(`${a.nombre} supera 20 MB y no se reenvía`, 'info');
          continue;
        }
        const { data } = await emailsAPI.descargarAdjunto(detalle.id, idx);
        const base64 = await blobABase64(data);
        agregarAdjunto({
          nombre: a.nombre || `adjunto-${idx}`,
          mime_type: a.mime_type || data.type || 'application/octet-stream',
          tamano: data.size,
          contenido_base64: base64,
          origen: 'reenvio',
        });
      } catch (e) {
        mostrarAviso(`No se pudo recuperar el adjunto ${a.nombre || idx}`, 'info');
      } finally {
        setComponer((c) => (c ? { ...c, adjuntando_originales: Math.max(0, (c.adjuntando_originales || 1) - 1) } : c));
      }
    }
  };

  // Abre el compositor para un correo nuevo (no una respuesta).
  // Si no se pasa contenido, intenta recuperar el borrador guardado ("nuevo").
  const abrirNuevoMensaje = ({ para = [], asunto = '', cuerpoHtml = '' } = {}) => {
    // Correo nuevo: la última cuenta elegida al escribir o la primera con envío.
    const ultima = leerLS(LS_ULTIMA_CUENTA);
    const cuenta = (ultima && cuentasEnvio.find((c) => c.id === ultima)) || cuentasEnvio[0] || cuentas[0];
    if (!cuenta) { alert('No hay ninguna cuenta de correo conectada'); return; }
    const tieneContenido = (para && para.length) || (asunto && asunto.trim()) || (cuerpoHtml && cuerpoHtml.trim());
    let guardado = null;
    if (!tieneContenido) {
      try {
        const raw = localStorage.getItem(`email_borrador_${cuenta.id}_nuevo`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.ts && Date.now() - parsed.ts < 7 * 24 * 3600 * 1000 && (parsed.body_html || '').replace(/<[^>]+>/g, '').trim()) {
            guardado = parsed;
          }
        }
      } catch (e) { /* ignorar */ }
    }
    abrirComposer({
      para: guardado?.para?.length ? guardado.para : para,
      cc: guardado?.cc || [],
      asunto: guardado?.asunto || asunto,
      body_html: guardado ? guardado.body_html : (cuerpoHtml || '<p></p>'),
      thread_id: null,
      in_reply_to_message_id: null,
      cuenta_id: cuenta.id,
      clave_borrador: `email_borrador_${cuenta.id}_nuevo`,
      factura_id: null,
      factura_numero: null,
      firma_id: guardado?.firma_id || firmaPorCuenta(cuenta.email),
      presupuesto_id: null,
      borrador_recuperado: !!guardado,
      modo: 'nuevo',
      abierto_en: Date.now(),
      enviando: false,
    });
  };

  // Acción diferida: una acción rápida de IAmail pidió abrir el compositor,
  // pero antes hacía falta cargar el detalle del correo.
  useEffect(() => {
    const pendiente = accionTrasCargarRef.current;
    if (pendiente && detalle && pendiente.emailId === detalle.id) {
      accionTrasCargarRef.current = null;
      if (pendiente.tipo === 'responder') {
        abrirResponder({ borradorHtml: pendiente.borradorHtml || '', facturaId: pendiente.facturaId || null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalle?.id]);

  // Escape cierra el drawer de IAmail (si no hay ningún modal por encima; con
  // Ayuda IA abierta, Escape cierra solo el panel, que lo escucha él).
  useEffect(() => {
    if (!(carpeta === 'iamail' && seleccionado && !componer && !pickerAdjuntos && !visorAdjunto && !ayudaIaAbierta)) return undefined;
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') cerrarDetalle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carpeta, seleccionado, componer, pickerAdjuntos, visorAdjunto, ayudaIaAbierta]);

  // ==================== Adjuntos ====================
  const agregarAdjunto = (adj) => {
    setComponer((c) => (c ? { ...c, adjuntos: [...(c.adjuntos || []), adj] } : c));
  };

  const quitarAdjunto = (idx) => {
    setComponer((c) => (c ? { ...c, adjuntos: (c.adjuntos || []).filter((_, i) => i !== idx) } : c));
  };

  const adjuntarDesdeEquipo = async (files) => {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      if (file.size > 20 * 1024 * 1024) {
        alert(`${file.name} supera 20 MB, demasiado grande para adjuntar por email.`);
        continue;
      }
      const base64 = await blobABase64(file);
      agregarAdjunto({
        nombre: file.name,
        mime_type: file.type || 'application/octet-stream',
        tamano: file.size,
        contenido_base64: base64,
        origen: 'archivo',
      });
    }
  };

  const entidadesIA = () => detalle?.analisis_ia?.entidades_detectadas || {};
  const accionesPresupuestoIds = () => {
    const acciones = detalle?.analisis_ia?.acciones_sugeridas || [];
    return acciones.map((a) => a?.params?.presupuesto_id).filter(Boolean);
  };
  const accionesFacturaIds = () => {
    const acciones = detalle?.analisis_ia?.acciones_sugeridas || [];
    return acciones.map((a) => a?.params?.factura_id).filter(Boolean);
  };

  const resolverClienteDesdeDetalle = async () => {
    if (!detalle) return null;
    const ent = entidadesIA();
    const clienteId = detalle.cliente_id || ent.cliente_id || null;
    if (clienteId) {
      try {
        const r = await clientesAPI.getOne(clienteId);
        return r.data || { id: clienteId };
      } catch {
        return { id: clienteId };
      }
    }
    return null;
  };

  const abrirPicker = async (tipo, modo = 'adjuntar') => {
    setAdjuntarMenuAbierto(false);
    setDetalleSeleccionado(null);
    setPickerAdjuntos({ tipo, modo, items: [], cargando: true, filtro: '', cliente: null, highlightId: null });
    try {
      const cliente = await resolverClienteDesdeDetalle();
      const clienteId = cliente?.id || null;
      const ent = entidadesIA();
      const highlightId = tipo === 'presupuesto'
        ? (detalle?.presupuesto_id || ent.presupuesto_id || accionesPresupuestoIds()[0] || null)
        : (detalle?.factura_id || detalle?.factura_soportada_id || ent.factura_id || accionesFacturaIds()[0] || null);

      let items = [];
      if (tipo === 'presupuesto') {
        // Traer los del cliente + top recientes
        const peticiones = [presupuestosAPI.getAll({ limit: 50 })];
        if (clienteId) peticiones.unshift(presupuestosAPI.getAll({ cliente_id: clienteId, limit: 50 }));
        const resultados = await Promise.all(peticiones);
        const mapa = new Map();
        for (const resp of resultados) {
          const lista = resp.data?.presupuestos || resp.data || [];
          for (const p of lista) {
            if (!mapa.has(p.id)) {
              mapa.set(p.id, {
                id: p.id,
                numero: p.numero || p.num_presupuesto,
                cliente: p.cliente_nombre || p.cliente?.nombre || '',
                cliente_id: p.cliente_id || null,
                fecha: p.fecha_evento || p.created_at,
                total: p.total,
              });
            }
          }
        }
        items = Array.from(mapa.values());
      } else if (tipo === 'factura') {
        const resp = await facturasAPI.listar({});
        const raw = resp.data?.facturas || resp.data || [];
        items = raw.map((f) => ({
          id: f.id,
          numero: f.numero_factura || f.numero,
          cliente: f.cliente_nombre || f.nombre_cliente || '',
          cliente_id: f.cliente_id || null,
          fecha: f.fecha_factura || f.fecha_emision,
          total: f.importe_total != null ? f.importe_total : f.total,
        }));
      }

      // Ordenar: sugerido primero, luego mismo cliente, luego resto por fecha desc
      items.sort((a, b) => {
        const aSug = highlightId && a.id === highlightId ? 1 : 0;
        const bSug = highlightId && b.id === highlightId ? 1 : 0;
        if (aSug !== bSug) return bSug - aSug;
        const aCli = clienteId && a.cliente_id === clienteId ? 1 : 0;
        const bCli = clienteId && b.cliente_id === clienteId ? 1 : 0;
        if (aCli !== bCli) return bCli - aCli;
        const fa = a.fecha ? new Date(a.fecha).getTime() : 0;
        const fb = b.fecha ? new Date(b.fecha).getTime() : 0;
        return fb - fa;
      });

      setPickerAdjuntos({ tipo, modo, items, cargando: false, filtro: '', cliente, highlightId });
      // Previsualizar automáticamente el sugerido
      if (highlightId) {
        const sugerido = items.find((i) => i.id === highlightId);
        if (sugerido) cargarDetalleAdjunto(sugerido, tipo);
      }
    } catch (e) {
      console.error('Error cargando picker', e, e?.response?.data);
      alert(`No se pudieron cargar los documentos${e?.response?.status ? ` (HTTP ${e.response.status})` : ''}`);
      setPickerAdjuntos(null);
    }
  };

  const cargarDetalleAdjunto = async (item, tipo) => {
    setDetalleSeleccionado({ id: item.id, tipo, cargando: true, datos: null });
    try {
      if (tipo === 'presupuesto') {
        const r = await presupuestosAPI.getOne(item.id);
        setDetalleSeleccionado({ id: item.id, tipo, cargando: false, datos: r.data });
      } else {
        // Factura: getOne directo
        const r = await api.get(`/facturas/${item.id}`);
        setDetalleSeleccionado({ id: item.id, tipo, cargando: false, datos: r.data });
      }
    } catch (e) {
      console.error(e);
      setDetalleSeleccionado({ id: item.id, tipo, cargando: false, datos: null, error: true });
    }
  };

  const adjuntarDocumento = async (item) => {
    if (!pickerAdjuntos) return;
    setAdjuntandoRef(true);
    try {
      const esPresupuesto = pickerAdjuntos.tipo === 'presupuesto';
      const resp = esPresupuesto
        ? await presupuestosAPI.descargarPdf(item.id)
        : await facturasAPI.descargarPdf(item.id);
      const blob = resp.data;
      const mimeReal = (blob && blob.type) || resp.headers?.['content-type'] || 'application/pdf';
      const esPdf = mimeReal.toLowerCase().startsWith('application/pdf');
      if (!esPdf) {
        const seguir = window.confirm(
          `El servidor devolvió un documento ${mimeReal.split(';')[0]} en lugar de PDF (normalmente porque WeasyPrint no está disponible en este entorno). ¿Adjuntar de todas formas?`
        );
        if (!seguir) { setAdjuntandoRef(false); return; }
      }
      const base64 = await blobABase64(blob);
      const extension = esPdf
        ? 'pdf'
        : mimeReal.includes('html')
          ? 'html'
          : (mimeReal.split('/')[1] || 'bin').split(';')[0];
      const nombre = esPresupuesto
        ? `Presupuesto_${item.numero || item.id.slice(0, 8)}.${extension}`
        : `Factura_${item.numero || item.id.slice(0, 8)}.${extension}`;
      agregarAdjunto({
        nombre,
        mime_type: mimeReal.split(';')[0],
        tamano: blob.size,
        contenido_base64: base64,
        origen: pickerAdjuntos.tipo,
        ref_id: item.id,
      });
      setPickerAdjuntos(null);
    } catch (e) {
      console.error(e);
      alert('No se pudo generar el PDF del documento seleccionado');
    } finally {
      setAdjuntandoRef(false);
    }
  };

  // "De:" del editor: cambia la cuenta de envío y, si no se tocó a mano, la firma.
  const cambiarCuentaComposer = (cuentaId) => {
    const cuenta = cuentasPorId[cuentaId];
    guardarLS(LS_ULTIMA_CUENTA, cuentaId);
    setComponer((c) => (c ? {
      ...c,
      cuenta_id: cuentaId,
      firma_id: c.firma_manual ? c.firma_id : firmaPorCuenta(cuenta?.email || ''),
    } : c));
  };

  const enviarComposicion = async () => {
    if (!componer) return;
    if (!componer.para?.[0]) { alert('Falta el destinatario'); return; }
    setComponer({ ...componer, enviando: true });
    try {
      // Desde un borrador del servidor: al enviarse bien, el servidor lo borra.
      await emailsAPI.enviar(datosComposer(componer));
      try { localStorage.removeItem(claveBorradorComposer(componer)); } catch (e) {}
      guardarLS(LS_ULTIMA_CUENTA, componer.cuenta_id);
      const eraBorrador = Boolean(componer.borrador_id || componer.borrador_message_id);
      setComponer(null);
      setCierreComposer(false);
      if (eraBorrador) {
        if (componer.borrador_id) setEmails((prev) => prev.filter((e) => e.id !== componer.borrador_id));
        cargarContadoresFiltro();
      }
      // Responder deja el pendiente del hilo esperando al cliente.
      setPendientesVersion((v) => v + 1);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'No se pudo enviar el email');
      setComponer({ ...componer, enviando: false });
    }
  };

  // ==================== Borradores del servidor ====================
  // Abrir un borrador de la carpeta Borradores: en vez de leerlo, se continúa
  // en el editor con sus destinatarios, el hilo citado y sus adjuntos.
  const abrirBorrador = async (correo) => {
    if (!correo?.id || abriendoBorrador) return;
    setAbriendoBorrador(correo.id);
    try {
      const { data } = await emailsAPI.abrirBorrador(correo.id);
      abrirComposer({
        para: data.para || [],
        cc: data.cc || [],
        bcc: data.bcc || [],
        mostrar_cc: (data.cc || []).length > 0,
        mostrar_bcc: (data.bcc || []).length > 0,
        asunto: data.asunto || '',
        body_html: cuerpoBorradorHtml(data.body_html),
        thread_id: data.thread_id || null,
        in_reply_to_message_id: data.in_reply_to || null,
        references: data.references || [],
        cuenta_id: data.cuenta_id,
        clave_borrador: `email_borrador_srv_${data.id}`,
        factura_id: data.factura_id || null,
        factura_numero: null,
        // La firma que llevaba el borrador (quitada del cuerpo): no sale dos veces.
        firma_id: data.firma_id || 'ninguna',
        firma_manual: true,
        presupuesto_id: data.presupuesto_id || null,
        borrador_id: data.id,
        borrador_message_id: data.message_id || null,
        borrador_recuperado: false,
        adjuntos: (data.adjuntos || []).map((a) => ({ ...a, origen: 'borrador' })),
        modo: 'borrador',
        abierto_en: Date.now(),
        enviando: false,
      });
      if ((data.omitidos || []).length) {
        mostrarAviso(`Adjuntos demasiado grandes, no se han cargado: ${data.omitidos.join(', ')}`, 'info');
      }
    } catch (e) {
      mostrarAviso(e.response?.data?.detail || 'No se pudo abrir el borrador', 'info');
    } finally {
      setAbriendoBorrador(null);
    }
  };

  // Guardar en la carpeta de borradores del correo (solo el dueño del buzón).
  // Si se estaba editando un borrador, el servidor lo sustituye.
  const guardarBorradorServidor = async ({ cerrar = false } = {}) => {
    if (!componer || componer.enviando || componer.guardando) return;
    const actual = componer;
    setComponer((c) => (c ? { ...c, guardando: true } : c));
    try {
      const { data } = await emailsAPI.guardarBorrador(datosComposer(actual));
      try { localStorage.removeItem(claveBorradorComposer(actual)); } catch (e) {}
      if (cerrar) {
        setComponer(null);
      } else {
        setComponer((c) => {
          if (!c) return c;
          const siguiente = {
            ...c,
            guardando: false,
            borrador_id: data.borrador_id || null,
            borrador_message_id: data.message_id || c.borrador_message_id || null,
          };
          return { ...siguiente, huella_inicial: huellaComposer(siguiente) };
        });
      }
      setCierreComposer(false);
      mostrarAviso('Borrador guardado en tu correo');
      if (carpeta === 'borradores') cargarEmails();
      cargarContadoresFiltro();
    } catch (e) {
      setComponer((c) => (c ? { ...c, guardando: false } : c));
      mostrarAviso(e.response?.data?.detail || 'No se pudo guardar el borrador', 'info');
    }
  };

  // Eliminar un borrador del servidor (desde la lista o desde el editor).
  const eliminarBorrador = async (id) => {
    if (!id) return;
    if (!window.confirm('¿Eliminar este borrador? Se borra también de tu correo.')) return;
    try {
      await emailsAPI.eliminarBorrador(id);
      setEmails((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      if (componer?.borrador_id === id) {
        try { localStorage.removeItem(claveBorradorComposer(componer)); } catch (e) {}
        setComponer(null);
        setCierreComposer(false);
      }
      cargarContadoresFiltro();
      mostrarAviso('Borrador eliminado');
    } catch (e) {
      mostrarAviso(e.response?.data?.detail || 'No se pudo eliminar el borrador', 'info');
    }
  };

  // Cerrar el editor: si hay cambios, se pregunta antes (guardar, descartar o seguir).
  const intentarCerrarComposer = () => {
    if (!componer || componer.enviando || componer.guardando) return;
    if (composerModificado(componer)) {
      setCierreComposer(true);
      return;
    }
    setComponer(null);
  };

  // Móvil: atrás cierra el editor (si hay cambios, pregunta antes y el editor
  // sigue abierto; el hook repone la entrada para el siguiente atrás).
  useCapaAtras('editor', esMovil && !!componer, intentarCerrarComposer);
  const editorAbiertoMovil = esMovil && !!componer;
  useEffect(() => {
    if (editorAbiertoMovil) revisarNovedades();
  }, [editorAbiertoMovil]);
  // iOS: el teclado no encoge la ventana sino la parte visible (visualViewport).
  // Con el editor abierto en el móvil se sigue su alto y su desplazamiento para
  // que la cabecera con Enviar no se quede debajo ni fuera de la vista.
  const [vistaVisible, setVistaVisible] = useState(null); // { alto, arriba } en px
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!editorAbiertoMovil || !vv) {
      setVistaVisible(null);
      return undefined;
    }
    const ajustar = () => setVistaVisible({ alto: Math.round(vv.height), arriba: Math.round(vv.offsetTop) });
    ajustar();
    vv.addEventListener('resize', ajustar);
    vv.addEventListener('scroll', ajustar);
    return () => {
      vv.removeEventListener('resize', ajustar);
      vv.removeEventListener('scroll', ajustar);
    };
  }, [editorAbiertoMovil]);

  const descartarComposer = () => {
    try { localStorage.removeItem(claveBorradorComposer(componer)); } catch (e) {}
    setCierreComposer(false);
    setComponer(null);
  };

  // ==================== Acciones IA con backend ====================
  const ejecutarGenerarFactura = async (presupuestoId) => {
    // Multievento facturado por jornada: no se emite desde aquí (cada jornada
    // se factura desde su botón). El servidor también lo rechaza.
    try {
      const { data: p } = await api.get(`/presupuestos/${presupuestoId}`);
      if (p?.es_multi_evento && p?.facturacion_multi_evento === 'por_jornada') {
        alert('En facturación por jornada, factura cada jornada desde su botón (Facturación y cobros)');
        return;
      }
    } catch (e) {
      // Sin poder leerlo se sigue: el servidor vuelve a comprobarlo
    }
    // Otros correos "para administración" sin gestionar del mismo evento
    // (otra razón social, retener la factura...): se avisan en la confirmación.
    // Si la consulta falla, se sigue como siempre.
    let avisoCorreos = '';
    try {
      const { data } = await emailsAPI.paraAdministracion(presupuestoId);
      const otros = (data?.correos || []).filter((c) => c.id !== detalle?.id);
      if (otros.length) {
        const lineas = otros.map((c) => {
          const f = c.fecha ? new Date(c.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) : '';
          return `- ${c.remitente_nombre || c.remitente || 'Sin remitente'}${f ? `, ${f}` : ''}: ${c.accion || c.asunto || ''}`;
        });
        avisoCorreos = `Correos para administración sin gestionar (${otros.length}):\n${lineas.join('\n')}\n\nRevisa estos correos antes de emitir: pueden cambiar a quién o cómo se factura.\n\n`;
      }
    } catch (e) {
      // Sin aviso: no bloquea la emisión
    }
    if (!window.confirm(`${avisoCorreos}¿Generar factura desde el presupuesto seleccionado?`)) return;
    try {
      const resp = await api.post(`/presupuestos/${presupuestoId}/generar-factura`, {});
      const fid = resp.data?.factura?.id || resp.data?.id || resp.data?.factura_id;
      alert('Factura generada correctamente');
      if (fid) {
        irA(`/finanzas?factura=${fid}`);
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'No se pudo generar la factura');
    }
  };

  const ejecutarCrearPresupuesto = async (params) => {
    try {
      const cliente_id = analisis?.entidades_detectadas?.cliente_id;
      if (!cliente_id) { alert('No se identificó el cliente: vincúlalo manualmente'); return; }
      const hoy = new Date().toISOString().slice(0, 10);
      const body = {
        cliente_id,
        fecha_evento: params?.fecha_evento || hoy,
        num_personas: Number(params?.personas || params?.num_personas || 0),
        lugar_evento: params?.lugar || '(pendiente)',
        hora_inicio: '10:00',
        hora_fin: '14:00',
        observaciones: `Borrador creado desde email "${detalle?.asunto || ''}"\n${params?.notas || ''}`,
        estado: 'borrador',
      };
      const resp = await api.post('/presupuestos', body);
      const pid = resp.data?.id;
      if (pid) irA(`/presupuestos/${pid}`);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'No se pudo crear el presupuesto');
    }
  };

  const ejecutarVincularPresupuesto = async (presupuestoId) => {
    if (!detalle?.id) return;
    try {
      await emailsAPI.actualizar(detalle.id, { presupuesto_id: presupuestoId });
      setDetalle({ ...detalle, presupuesto_id: presupuestoId });
      setPickerAdjuntos(null);
      mostrarAviso('Email vinculado al presupuesto/evento');
    } catch (e) {
      console.error(e);
      mostrarAviso('No se pudo vincular', 'info');
    }
  };

  const ejecutarDesvincularPresupuesto = async () => {
    if (!detalle?.id) return;
    try {
      await emailsAPI.actualizar(detalle.id, { presupuesto_id: null });
      setDetalle({ ...detalle, presupuesto_id: null });
      mostrarAviso('Vinculación eliminada');
    } catch (e) {
      mostrarAviso('No se pudo desvincular', 'info');
    }
  };

  const marcarGestionado = async (emailObj, resolver = true) => {
    if (!emailObj?.id) return;
    const cambios = resolver
      ? { estado_gestion: 'resuelto', tarea_descartada: true, leido: true }
      : { estado_gestion: 'pendiente', tarea_descartada: false };
    try {
      await emailsAPI.actualizar(emailObj.id, cambios);
      setDetalle((prev) => (prev && prev.id === emailObj.id ? { ...prev, ...cambios } : prev));
      setEmails((prev) => prev.map((e) => e.id === emailObj.id ? { ...e, ...cambios } : e));
      mostrarAviso(resolver ? 'Correo marcado como gestionado' : 'Correo reabierto');
      setPendientesVersion((v) => v + 1);
      cargarIaPanel();
    } catch (e) {
      console.error('Error marcando como gestionado', e);
      mostrarAviso('No se pudo actualizar el estado de gestión', 'info');
    }
  };

  const ejecutarAccionRapida = async (acc, emailObj) => {
    try {
      if ((acc.tipo === 'archivar_email' || acc.tipo === 'mover_a_papelera') && emailObj?.id) {
        const cambio = acc.tipo === 'archivar_email' ? { archivado: true } : { papelera: true };
        const { data } = await emailsAPI.actualizar(emailObj.id, cambio);
        if (data?.solo_erp) {
          // Buzón de otra persona: solo queda como gestionado en el ERP.
          setEmails((prev) => prev.map((e) => (e.id === emailObj.id ? { ...e, estado_gestion: 'resuelto', tarea_descartada: true } : e)));
          mostrarAviso(data.aviso || 'No es tu buzón: queda como gestionado en el ERP', 'info');
        } else {
          setEmails((prev) => prev.filter((e) => e.id !== emailObj.id));
        }
        cargarIaPanel();
      } else if (acc.tipo === 'marcar_destacado' && emailObj?.id) {
        await emailsAPI.actualizar(emailObj.id, { destacado: true });
        setEmails((prev) => prev.map((e) => e.id === emailObj.id ? { ...e, destacado: true } : e));
        cargarIaPanel();
      } else if (acc.tipo === 'descargar_factura' && acc.params?.factura_id) {
        const resp = await facturasAPI.descargarPdf(acc.params.factura_id);
        const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      } else if (acc.tipo === 'navegar_a_presupuesto' && acc.params?.presupuesto_id) {
        irA(`/presupuestos/${acc.params.presupuesto_id}`);
      } else if (acc.tipo === 'navegar_a_cliente' && acc.params?.cliente_id) {
        irA(`/clientes?cliente_id=${acc.params.cliente_id}`);
      } else if (acc.tipo === 'navegar_a_factura' && acc.params?.factura_id) {
        irA(`/finanzas?factura=${acc.params.factura_id}`);
      } else if (acc.tipo === 'subir_adjunto_a_drive' && emailObj?.id && Number.isInteger(acc.params?.adjunto_indice)) {
        const resp = await emailsAPI.subirAdjuntoADrive(emailObj.id, acc.params.adjunto_indice);
        const url = resp?.data?.url;
        const nombre = resp?.data?.nombre || 'adjunto';
        if (url) window.open(url, '_blank', 'noopener');
        alert(`"${nombre}" subido a Drive correctamente.`);
      } else if ((acc.tipo === 'responder_con_borrador' || acc.tipo === 'enviar_factura_existente') && emailObj?.id) {
        // Abre el correo y, en cuanto llega el detalle, lanza el compositor.
        accionTrasCargarRef.current = {
          emailId: emailObj.id,
          tipo: 'responder',
          borradorHtml: acc.params?.borrador_html || '',
          facturaId: acc.tipo === 'enviar_factura_existente' ? (acc.params?.factura_id || null) : null,
        };
        setSeleccionado(emailObj.id);
      } else {
        // Acción que requiere el detalle abierto: abrir el email.
        setSeleccionado(emailObj.id);
      }
    } catch (e) {
      console.error('Error ejecutando acción rápida IA', e);
      if (e?.response?.status === 409) {
        mostrarAviso('Autoriza tu cuenta de Google para subir a Drive', 'info');
        iniciarConexionDrive();
      } else {
        alert(e?.response?.data?.detail || 'No se pudo ejecutar la acción');
      }
    }
  };

  // Acciones rápidas sobre el pendiente del hilo, desde el detalle del correo.
  const accionPendienteHilo = async (cambios) => {
    if (!pendienteHilo?.id) return;
    try {
      await pendientesAPI.actualizar(pendienteHilo.id, cambios);
      setPendientesVersion((v) => v + 1);
      mostrarAviso('Pendiente actualizado');
    } catch (e) {
      mostrarAviso(e.response?.data?.detail || 'No se pudo actualizar el pendiente', 'info');
    }
  };

  // "No es mío" desde el detalle: quita este pendiente de la lista y ya. No se
  // bloquea al remitente: otro correo suyo sí puede ser nuestro.
  const noEsMioPendienteHilo = async () => {
    if (!pendienteHilo?.id) return;
    try {
      await pendientesAPI.actualizar(pendienteHilo.id, { es_mio: false });
      setPendientesVersion((v) => v + 1);
      mostrarAviso('Quitado de tus pendientes');
    } catch (e) {
      mostrarAviso(e.response?.data?.detail || 'No se pudo actualizar el pendiente', 'info');
    }
  };

  // Móvil: el resumen del análisis IA en una línea (la tarjeta plegada)
  const lineaResumenIA = () => {
    if (analizando && !analisis) return 'Consultando el análisis...';
    if (!analisis) return 'Sin análisis';
    if (analisis.error) return analisis.error;
    const hayAnalisis = !(analisis.pendiente || analisis.no_analizable) || analisis.intencion;
    if (hayAnalisis && analisis.resumen) return analisis.resumen;
    if (analisis.pendiente) return 'Análisis pendiente';
    if (analisis.no_analizable) return analisis.mensaje || 'Este correo no se analiza';
    return 'Sin resumen';
  };

  // Móvil: cada acción de la hoja "Más" la cierra antes de hacer lo suyo
  const enHojaMas = (accion) => () => {
    setHojaMas(false);
    accion();
  };

  // Detalle de un correo. Se reutiliza en la columna derecha y en el drawer de IAmail.
  // En el móvil las acciones van en una barra inferior fija (y el resto en la hoja "Más").
  const renderDetalle = ({ enDrawer = false } = {}) => (
    <div className="max-w-3xl mx-auto p-3 pb-28 md:p-6 md:pb-6">
      {!enDrawer && (
        <div className="md:hidden sticky top-0 z-10 bg-gray-50 pb-2">
          <button
            onClick={cerrarDetalle}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 py-1.5 rounded-lg border bg-white text-sm text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
        </div>
      )}
      <div className="bg-white rounded-xl border p-4 md:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 break-words">{detalle.asunto || '(sin asunto)'}</h2>
          <div className="hidden md:flex items-center gap-2 flex-wrap justify-end">
            {/* Borrador (abierto desde el hilo, un panel o un enlace): se edita y se
                envía en el editor; no se responde ni se reenvía como un recibido. */}
            {detalle.es_borrador ? (
              <>
                <button
                  type="button"
                  onClick={() => abrirBorrador(detalle)}
                  disabled={abriendoBorrador === detalle.id}
                  data-uso="emails.borrador_abrir"
                  data-uso-detalle="detalle"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-duetto-primary text-white hover:bg-duetto-primary/90 text-sm disabled:opacity-60"
                  title="Abrir el borrador en el editor para cambiarlo y enviarlo"
                >
                  {abriendoBorrador === detalle.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
                  Editar y enviar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const id = detalle.id;
                    await eliminarBorrador(id);
                    setDetalle((d) => (d && d.id === id ? null : d));
                  }}
                  data-uso="emails.borrador_eliminar"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 text-sm"
                  title="Eliminar este borrador (también de tu correo)"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar borrador
                </button>
              </>
            ) : (
            <>
            <button
              onClick={() => abrirResponder()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-duetto-primary text-white hover:bg-duetto-primary/90 text-sm"
              title="Responder"
              data-uso="emails.responder"
            >
              <Reply className="w-4 h-4" />
              Responder
            </button>
            {((detalle.destinatarios || []).length + (detalle.cc || []).length) > 0 && (
              <button
                onClick={() => abrirResponder({ todos: true })}
                data-uso="emails.responder"
                data-uso-detalle="todos"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-duetto-primary text-duetto-primary hover:bg-duetto-primary/10 text-sm"
                title="Responder al remitente y a todos los destinatarios"
              >
                <ReplyAll className="w-4 h-4" />
                Responder a todos
              </button>
            )}
            <button
              onClick={abrirReenviar}
              data-uso="emails.reenviar"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-duetto-primary text-duetto-primary hover:bg-duetto-primary/10 text-sm"
              title="Reenviar este correo con sus adjuntos"
            >
              <Forward className="w-4 h-4" />
              Reenviar
            </button>
            {ayudaIaActiva && (
              <button
                type="button"
                onClick={() => setAyudaIaAbierta(true)}
                data-guia="iamail-ayuda-ia"
                data-uso="emails.ayuda_ia"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm"
                title="Pide a la IA los pasos para resolver este correo; no hace nada sin tu OK"
              >
                <Sparkles className="w-4 h-4" />
                Ayuda IA
              </button>
            )}
            {detalle.estado_gestion === 'resuelto' ? (
              <button
                onClick={() => marcarGestionado(detalle, false)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm"
                title="Reabrir: vuelve a marcarlo como pendiente"
              >
                <RefreshCcw className="w-4 h-4" />
                Reabrir
              </button>
            ) : (
              <button
                onClick={() => marcarGestionado(detalle, true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm"
                title="Marcar como gestionado"
              >
                <CheckCircle2 className="w-4 h-4" />
                Marcar gestionado
              </button>
            )}
            <button
              onClick={() => togglePropiedad(detalle, 'destacado')}
              className={`p-2 rounded hover:bg-gray-100 ${detalle.destacado ? 'text-amber-500' : 'text-gray-400'}`}
              title="Destacar"
            >
              <Star className="w-4 h-4" fill={detalle.destacado ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={() => abrirPicker('presupuesto', 'vincular')}
              className={`p-2 rounded hover:bg-gray-100 ${detalle.presupuesto_id ? 'text-duetto-primary' : 'text-gray-500'}`}
              title={detalle.presupuesto_id ? 'Cambiar evento vinculado' : 'Vincular a un evento (presupuesto)'}
            >
              <Link2 className="w-4 h-4" />
            </button>
            {!detalle.es_enviado && !detalle.es_borrador && !detalle.carpeta_auto?.carpeta && (
              <button
                onClick={() => marcarSpamFiltro(detalle)}
                className="p-2 rounded hover:bg-gray-100 text-gray-500"
                title="Es spam: mover a Spam y añadir el remitente a la lista negra"
                data-testid="btn-es-spam"
              >
                <Ban className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => togglePropiedad(detalle, 'archivado')}
              className={`p-2 rounded hover:bg-gray-100 ${detalle.archivado ? 'text-duetto-primary' : 'text-gray-500'}`}
              title={detalle.archivado ? 'Desarchivar' : 'Archivar (carpeta Archivados)'}
            >
              <Archive className="w-4 h-4" />
            </button>
            <button
              onClick={() => togglePropiedad(detalle, 'papelera')}
              className={`p-2 rounded hover:bg-gray-100 text-red-500`}
              title={detalle.papelera ? 'Restaurar de la papelera' : 'Mover a la papelera'}
            >
              <Trash2 className="w-4 h-4" />
            </button>
            </>
            )}
          </div>
        </div>
        {detalle.carpeta_auto?.carpeta && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <ChipFiltro correo={detalle} clase="text-xs" />
              <span className="text-xs text-amber-800">
                Movido por el filtro automático
                {detalle.carpeta_auto.fecha ? ` el ${new Date(detalle.carpeta_auto.fecha).toLocaleDateString('es-ES')}` : ''}.
                No se ha borrado nada.
              </span>
            </div>
            <button
              onClick={() => moverABandejaFiltro(detalle)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-duetto-primary text-duetto-primary bg-white hover:bg-duetto-primary/10 text-sm"
              title="Devuelve el correo a la bandeja y añade el remitente a la lista blanca"
              data-testid="btn-mover-bandeja"
            >
              <Inbox className="w-4 h-4" />
              {detalle.carpeta_auto.carpeta === 'spam' ? 'No es spam: mover a bandeja'
                : detalle.carpeta_auto.carpeta === 'publicidad' ? 'No es publicidad: mover a bandeja'
                  : 'Mover a bandeja'}
            </button>
          </div>
        )}
        <div className="text-sm text-gray-600 border-b pb-3 mb-4 space-y-1">
          <div><span className="text-gray-500">De:</span> {detalle.remitente_nombre ? `${detalle.remitente_nombre} <${detalle.remitente}>` : detalle.remitente}</div>
          {detalle.destinatarios?.length > 0 && <div><span className="text-gray-500">Para:</span> {detalle.destinatarios.join(', ')}</div>}
          {detalle.cc?.length > 0 && <div><span className="text-gray-500">Cc:</span> {detalle.cc.join(', ')}</div>}
          <div><span className="text-gray-500">Fecha:</span> {new Date(detalle.fecha).toLocaleString('es-ES')}</div>
          {/* Móvil: adjuntos arriba, en chips con scroll horizontal (la lista con Drive sigue abajo) */}
          {esMovil && detalle.adjuntos?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto -mx-1 px-1 py-1" data-guia="iamail-adjuntos-movil">
              {detalle.adjuntos.map((a, idx) => {
                const nombreAdj = a.nombre || `adjunto-${idx}`;
                const nombreCorto = nombreAdj.length > 22 ? `${nombreAdj.slice(0, 21)}…` : nombreAdj;
                const previsualizable = esPrevisualizable(a.mime_type);
                const IconoAdj = (a.mime_type || '').startsWith('image/') ? IconoImagen : FileText;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => accionAdjunto(detalle.id, idx, a.nombre, a.mime_type, previsualizable ? 'ver' : 'descargar')}
                    data-uso={previsualizable ? 'emails.adjunto_ver' : 'emails.adjunto_descargar'}
                    data-uso-detalle="movil"
                    className="shrink-0 inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-full border border-gray-300 bg-white text-sm text-gray-700 active:bg-gray-100"
                    title={previsualizable ? `Ver ${nombreAdj}` : `Descargar ${nombreAdj}`}
                  >
                    <IconoAdj className="w-4 h-4 text-duetto-primary shrink-0" />
                    <span className="whitespace-nowrap">{nombreCorto}</span>
                    {a.tamano ? <span className="text-xs text-gray-500 whitespace-nowrap">{Math.round(a.tamano / 1024)} KB</span> : null}
                  </button>
                );
              })}
            </div>
          )}
          {(detalle.categoria_ia || detalle.adjuntos_en_drive || detalle.contestado || detalle.en_copia || detalle.estado_gestion === 'esperando' || detalle.estado_gestion === 'resuelto') && (
            <div className="flex items-center gap-2 flex-wrap">
              {detalle.categoria_ia && (
                <>
                  <span className="text-gray-500">Clasificación IA:</span>{' '}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${CATEGORIAS_IA.find((c) => c.id === detalle.categoria_ia)?.color || 'bg-gray-100'}`}>
                    {CATEGORIAS_IA.find((c) => c.id === detalle.categoria_ia)?.label || detalle.categoria_ia}
                  </span>
                </>
              )}
              {detalle.adjuntos_en_drive && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-duetto-primary/10 text-duetto-primary">
                  <CheckCircle2 className="w-3 h-3" /> Factura en Drive
                </span>
              )}
              {detalle.contestado && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700">
                  <Reply className="w-3 h-3" /> Contestado
                </span>
              )}
              {detalle.estado_gestion === 'esperando' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700">
                  Esperando respuesta
                </span>
              )}
              {detalle.estado_gestion === 'resuelto' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                  <CheckCircle2 className="w-3 h-3" /> Gestionado
                </span>
              )}
              <EtiquetasCopia correo={detalle} clase="text-xs" />
              <EtiquetaBuzones ids={variosBuzones ? (detalle.en_buzones?.length ? detalle.en_buzones : [detalle.cuenta_id]) : []} cuentasPorId={cuentasPorId} clase="text-xs" />
            </div>
          )}
          {!detalle.presupuesto_id && analisis && (() => {
            const candidatosMap = new Map();
            const pushCand = (p, origen) => {
              if (!p?.id || candidatosMap.has(p.id)) return;
              candidatosMap.set(p.id, {
                id: p.id,
                numero: p.numero || p.num_presupuesto,
                fecha: p.fecha_evento,
                lugar: p.lugar || p.lugar_evento,
                estado: p.estado,
                origen,
              });
            };
            // Prioridad: 1) IA top pick, 2) mismo rango de fechas, 3) recientes del cliente.
            const topId = analisis?.entidades_detectadas?.presupuesto_id;
            const porFecha = analisis?.presupuestos_por_fecha_mencionada || [];
            const recientes = analisis?.presupuestos_recientes || [];
            if (topId) {
              const top = [...porFecha, ...recientes].find((p) => p.id === topId);
              if (top) pushCand(top, 'ia');
              // Si la IA sugiere un id que NO está en el contexto, lo ignoramos
              // en lugar de mostrar un UUID parcial sin información.
            }
            porFecha.forEach((p) => pushCand(p, 'fecha'));
            recientes.slice(0, 3).forEach((p) => pushCand(p, 'reciente'));
            // Filtra candidatos sin ningún dato útil (ni número, ni fecha, ni lugar).
            const candidatos = Array.from(candidatosMap.values())
              .filter((c) => c.numero || c.fecha || c.lugar)
              .slice(0, 3);
            if (candidatos.length === 0) return null;
            return (
              <div className="flex items-start gap-2 pt-1 flex-wrap">
                <span className="text-gray-500 shrink-0 mt-0.5">Sugerencias de evento:</span>
                <div className="flex flex-wrap gap-1.5">
                  {candidatos.map((c) => {
                    const fechaStr = c.fecha ? new Date(c.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';
                    const etiqueta = [c.numero ? `#${c.numero}` : null, fechaStr, c.lugar].filter(Boolean).join(' · ') || 'Presupuesto sin nº';
                    return (
                      <button
                        key={c.id}
                        onClick={() => ejecutarVincularPresupuesto(c.id)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-duetto-primary/40 bg-duetto-primary/5 text-duetto-primary text-xs hover:bg-duetto-primary/15"
                        title={c.origen === 'ia' ? 'Sugerido por la IA' : c.origen === 'fecha' ? 'Coincide con fecha mencionada' : 'Presupuesto reciente del cliente'}
                      >
                        <Link2 className="w-3 h-3" /> {etiqueta}
                        {c.origen === 'ia' && <Sparkles className="w-3 h-3" />}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => abrirPicker('presupuesto', 'vincular')}
                    className="text-xs text-gray-500 hover:text-duetto-primary underline px-1"
                  >
                    Otro…
                  </button>
                </div>
              </div>
            );
          })()}
          {detalle.presupuesto_id && (() => {
            const info = presupuestoVinculadoInfo;
            const numero = info?.numero || info?.num_presupuesto;
            const fechaEv = info?.fecha_evento ? new Date(info.fecha_evento).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) : null;
            const etiqueta = numero ? `#${numero}` : (info ? `#${detalle.presupuesto_id.slice(0, 8)}` : 'cargando…');
            const url = `/presupuestos/${detalle.presupuesto_id}`;
            return (
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <span className="text-gray-500">Evento vinculado:</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-duetto-primary/10 text-duetto-primary text-xs font-medium">
                  <Link2 className="w-3 h-3" /> {etiqueta}
                  {fechaEv && <span className="text-duetto-primary/70">· {fechaEv}</span>}
                </span>
                {detalle.presupuesto_vinculo_origen === 'auto' && (
                  <span
                    className="text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-700"
                    title="Vinculado automáticamente por cliente y fecha"
                  >
                    auto
                  </span>
                )}
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(ev) => {
                    // En el móvil se abre dentro de la app (la PWA no abre pestañas)
                    if (esMovil) { ev.preventDefault(); irA(url); }
                  }}
                  className="inline-flex items-center gap-1 text-xs text-duetto-primary hover:underline"
                  title="Abrir el presupuesto en una nueva pestaña"
                >
                  <ExternalLink className="w-3 h-3" /> {esMovil ? 'Abrir' : 'Abrir en nueva pestaña'}
                </a>
                <button
                  onClick={() => abrirPicker('presupuesto', 'vincular')}
                  className="text-xs text-gray-600 hover:text-duetto-primary underline"
                  title="Cambiar el evento vinculado"
                >
                  Cambiar
                </button>
                <button
                  onClick={ejecutarDesvincularPresupuesto}
                  className="text-xs text-gray-500 hover:text-red-500"
                  title="Quitar vinculación"
                >
                  Desvincular
                </button>
              </div>
            );
          })()}
          {detalle.thread_id && (
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className="text-gray-500">Pendiente:</span>
              {pendienteHilo ? (
                <>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">
                    <Clock className="w-3 h-3" /> {pendienteHilo.titulo}
                  </span>
                  {!pendienteHilo.sugerencia_descarte && pendienteHilo.fecha_limite && (
                    <span className="text-xs text-gray-500">vence {formatoFechaCorta(pendienteHilo.fecha_limite)}</span>
                  )}
                  {pendienteHilo.sugerencia_descarte ? (
                    <>
                      <span className="text-xs text-amber-700">
                        La IA cree que no es tuyo
                        {pendienteHilo.sugerencia_motivo ? `: ${pendienteHilo.sugerencia_motivo}` : ''}
                      </span>
                      <button
                        onClick={() => accionPendienteHilo({ es_mio: true })}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                        title="Sí es tuyo: pasa a la lista normal de pendientes"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Sí es mío
                      </button>
                      {pendienteHilo.remitente && (
                        <button
                          onClick={noEsMioPendienteHilo}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-gray-50 text-gray-500 hover:bg-gray-100 border-gray-200"
                          title="Este remitente no me genera trabajo a mí"
                        >
                          No es mío
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => accionPendienteHilo({ estado: 'hecho' })}
                        data-uso="emails.hecho"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
                        title="Marcar el pendiente como hecho"
                      >
                        <CheckCircle2 className="w-3 h-3" /> Hecho
                      </button>
                      {pendienteHilo.remitente && (
                        <button
                          onClick={noEsMioPendienteHilo}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-gray-50 text-gray-500 hover:bg-gray-100 border-gray-200"
                          title="Este remitente no me genera trabajo a mí"
                        >
                          No es mío
                        </button>
                      )}
                      <button
                        onClick={() => accionPendienteHilo({ posponer: { motivo: 'fecha', hasta: fechaManana() } })}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200"
                        title="Posponer a mañana"
                      >
                        Mañana
                      </button>
                      <button
                        onClick={() => accionPendienteHilo({ posponer: { motivo: 'fecha', hasta: fechaLunesQueViene() } })}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200"
                        title="Posponer al lunes que viene"
                      >
                        Lunes que viene
                      </button>
                    </>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setModalPendienteAbierto(true)}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-duetto-primary/10 text-duetto-primary hover:bg-duetto-primary/20 border-duetto-primary/30"
                  title="Crear un pendiente a partir de este correo"
                >
                  <Plus className="w-3 h-3" /> Convertir en pendiente
                </button>
              )}
            </div>
          )}
        </div>

        {/* Panel de análisis IA contextual. En el móvil, plegado en una línea hasta que se toca */}
        {esMovil && !analisisDesplegado ? (
          <button
            type="button"
            onClick={() => setAnalisisDesplegado(true)}
            data-guia="iamail-resumen-movil"
            data-uso="emails.analisis_desplegar"
            data-uso-detalle="movil"
            className="w-full mb-4 flex items-center gap-3 text-left min-h-[44px] px-3 py-2.5 border border-duetto-secondary/40 rounded-lg bg-gradient-to-br from-green-50 to-white active:bg-green-50"
          >
            <Sparkles className="w-4 h-4 text-duetto-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-800 truncate">{lineaResumenIA()}</div>
              {analisis?.accion_interna && (
                <div className="text-xs text-violet-800 truncate mt-0.5">Pendiente nuestro: {analisis.accion_interna}</div>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
          </button>
        ) : (
        <div className="mb-4 border border-duetto-secondary/40 rounded-lg bg-gradient-to-br from-green-50 to-white">
          <div className="flex items-center justify-between px-4 py-2 border-b border-duetto-secondary/30">
            <div className="flex items-center gap-2 text-sm font-semibold text-duetto-primary">
              <Sparkles className="w-4 h-4" /> Análisis IA
            </div>
            <div className="flex items-center gap-3">
            {esMovil && (
              <button
                type="button"
                onClick={() => setAnalisisDesplegado(false)}
                className="text-xs text-gray-500 inline-flex items-center gap-1 min-h-[44px]"
              >
                <ChevronDown className="w-3 h-3 rotate-180" /> Plegar
              </button>
            )}
            <button
              onClick={() => lanzarAnalisis(detalle.id, true)}
              disabled={analizando || Boolean(analisis?.no_analizable)}
              className="text-xs text-duetto-primary hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed inline-flex items-center gap-1"
              title={analisis?.no_analizable
                ? analisis.mensaje
                : 'Se vuelve a analizar en la próxima tanda (como mucho 5 minutos)'}
            >
              <RefreshCcw className={`w-3 h-3 ${analizando ? 'animate-spin' : ''}`} /> {esMovil ? 'Volver a analizar' : 'Volver a analizar (próxima tanda)'}
            </button>
            </div>
          </div>
          <div className="p-4 text-sm">
            {analizando && !analisis && (
              <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Consultando el análisis…</div>
            )}
            {analisis?.error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{analisis.error}</div>
            )}
            {analisis?.pendiente && (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2 mb-3 inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> Análisis pendiente: se hace en tandas de 5 minutos
              </div>
            )}
            {analisis?.no_analizable && (
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                {analisis.mensaje}
              </div>
            )}
            {analisis && !analisis.error && (!(analisis.pendiente || analisis.no_analizable) || analisis.intencion) && (
              <>
                {analisis.resumen && (
                  <div className="text-gray-800 mb-3 leading-relaxed">{analisis.resumen}</div>
                )}
                {analisis.accion_interna && (
                  <div className="text-sm mb-3 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200">
                    Pendiente nuestro: {analisis.accion_interna}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-3">
                  {analisis.intencion && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-duetto-primary text-white">
                      {analisis.intencion.replace(/_/g, ' ')}
                    </span>
                  )}
                  {analisis.entidades_detectadas?.fecha_evento && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">
                      Evento: {analisis.entidades_detectadas.fecha_evento}
                    </span>
                  )}
                  {analisis.entidades_detectadas?.numero_personas && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                      {analisis.entidades_detectadas.numero_personas} pax
                    </span>
                  )}
                </div>
                {analisis.acciones_sugeridas?.length > 0 ? (
                  <div>
                    <div className="text-xs uppercase text-gray-500 font-semibold mb-2">Acciones sugeridas</div>
                    <ul className="space-y-2">
                      {analisis.acciones_sugeridas.map((acc, idx) => {
                        const LISTAS = new Set([
                          'navegar_a_presupuesto', 'navegar_a_cliente', 'navegar_a_factura',
                          'descargar_factura', 'archivar_email', 'mover_a_papelera', 'marcar_destacado',
                          'responder_con_borrador', 'enviar_factura_existente',
                          'generar_factura_presupuesto', 'crear_presupuesto_desde_email',
                          'vincular_a_presupuesto', 'subir_adjunto_a_drive',
                        ]);
                        const listo = LISTAS.has(acc.tipo);
                        const IconAccion = ['navegar_a_presupuesto','navegar_a_cliente','navegar_a_factura'].includes(acc.tipo) ? ExternalLink
                          : acc.tipo === 'descargar_factura' ? Download
                          : acc.tipo === 'archivar_email' ? Archive
                          : acc.tipo === 'mover_a_papelera' ? Trash2
                          : acc.tipo === 'marcar_destacado' ? Star
                          : acc.tipo === 'subir_adjunto_a_drive' ? Upload
                          : Zap;
                        const handler = async () => {
                          try {
                            if (acc.tipo === 'navegar_a_presupuesto' && acc.params?.presupuesto_id) {
                              irA(`/presupuestos/${acc.params.presupuesto_id}`);
                            } else if (acc.tipo === 'navegar_a_cliente' && acc.params?.cliente_id) {
                              irA(`/clientes?cliente_id=${acc.params.cliente_id}`);
                            } else if (acc.tipo === 'navegar_a_factura' && acc.params?.factura_id) {
                              irA(`/finanzas?factura=${acc.params.factura_id}`);
                            } else if (acc.tipo === 'descargar_factura' && acc.params?.factura_id) {
                              const resp = await facturasAPI.descargarPdf(acc.params.factura_id);
                              const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
                              const url = window.URL.createObjectURL(blob);
                              window.open(url, '_blank', 'noopener');
                              setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                            } else if (acc.tipo === 'archivar_email' && detalle?.id) {
                              if (!detalle.archivado) await togglePropiedad(detalle, 'archivado');
                            } else if (acc.tipo === 'mover_a_papelera' && detalle?.id) {
                              if (!detalle.papelera) await togglePropiedad(detalle, 'papelera');
                            } else if (acc.tipo === 'marcar_destacado' && detalle?.id) {
                              if (!detalle.destacado) await togglePropiedad(detalle, 'destacado');
                            } else if (acc.tipo === 'responder_con_borrador') {
                              abrirResponder({ borradorHtml: acc.params?.borrador_html || '' });
                            } else if (acc.tipo === 'enviar_factura_existente' && acc.params?.factura_id) {
                              const fnum = (analisis?.facturas_por_fecha_mencionada || analisis?.facturas_recientes || [])
                                .find?.((f) => f.id === acc.params.factura_id)?.numero_factura;
                              abrirResponder({ borradorHtml: acc.params?.borrador_html || '', facturaId: acc.params.factura_id, facturaNumero: fnum });
                            } else if (acc.tipo === 'generar_factura_presupuesto' && acc.params?.presupuesto_id) {
                              await ejecutarGenerarFactura(acc.params.presupuesto_id);
                            } else if (acc.tipo === 'crear_presupuesto_desde_email') {
                              await ejecutarCrearPresupuesto(acc.params || {});
                            } else if (acc.tipo === 'vincular_a_presupuesto' && acc.params?.presupuesto_id) {
                              await ejecutarVincularPresupuesto(acc.params.presupuesto_id);
                            } else if (acc.tipo === 'subir_adjunto_a_drive' && detalle?.id && Number.isInteger(acc.params?.adjunto_indice)) {
                              const resp = await emailsAPI.subirAdjuntoADrive(detalle.id, acc.params.adjunto_indice);
                              const url = resp?.data?.url;
                              const nombre = resp?.data?.nombre || 'adjunto';
                              if (url) {
                                window.open(url, '_blank', 'noopener');
                              }
                              alert(`"${nombre}" subido a Drive correctamente.`);
                            }
                          } catch (e) {
                            console.error('Error ejecutando acción IA', e);
                            if (e?.response?.status === 409) {
                              mostrarAviso('Autoriza tu cuenta de Google para subir a Drive', 'info');
                              iniciarConexionDrive();
                            } else {
                              alert(e?.response?.data?.detail || 'No se pudo ejecutar la acción');
                            }
                          }
                        };
                        return (
                          <li key={idx} className="flex items-start gap-3 p-3 bg-white border rounded-lg">
                            <IconAccion className="w-4 h-4 text-duetto-primary mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900">{acc.titulo || acc.tipo}</div>
                              {acc.descripcion && (
                                <div className="text-xs text-gray-600 mt-0.5">{acc.descripcion}</div>
                              )}
                            </div>
                            <button
                              disabled={!listo}
                              onClick={listo ? handler : undefined}
                              className={listo
                                ? 'text-xs px-3 py-1.5 rounded bg-duetto-primary text-white hover:bg-duetto-primary/90'
                                : 'text-xs px-2 py-1 rounded border border-gray-300 text-gray-400 cursor-not-allowed'}
                              title={listo ? '' : 'Próximamente'}
                            >
                              {listo ? 'Ejecutar' : 'Próximamente'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 italic">Sin acciones sugeridas para este correo.</div>
                )}
              </>
            )}
          </div>
        </div>
        )}

        {detalle.body_html ? (
          <div
            className="prose max-w-none text-[15px] md:text-sm email-html"
            dangerouslySetInnerHTML={{ __html: sanitizarHtmlEmail(resolverImagenesCid(detalle.body_html, detalle.id)) }}
          />
        ) : (
          <pre className="whitespace-pre-wrap text-[15px] md:text-sm text-gray-800 font-sans">{detalle.body_text || detalle.snippet}</pre>
        )}
        {detalle.adjuntos?.length > 0 && (
          <div className="mt-5 pt-4 border-t">
            <div className="text-xs uppercase text-gray-500 font-semibold mb-2">Adjuntos</div>
            <ul className="space-y-1 text-sm">
              {detalle.adjuntos.map((a, idx) => {
                const claveDrive = `${detalle.id}-${idx}`;
                const subiendo = subiendoDrive.has(claveDrive);
                const yaEnDrive = !!a.drive_file_id;
                const esPdf = (a.mime_type || '').toLowerCase() === 'application/pdf';
                return (
                  <li key={idx} className="flex items-center justify-between gap-2 text-gray-700">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="truncate">{a.nombre}</span>
                      {a.tamano ? <span className="text-xs text-gray-500 shrink-0">({Math.round(a.tamano / 1024)} KB)</span> : null}
                      {yaEnDrive && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-duetto-primary/10 text-duetto-primary shrink-0">
                          <CheckCircle2 className="w-3 h-3" /> En Drive
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {esPrevisualizable(a.mime_type) && (
                        <button
                          type="button"
                          onClick={() => accionAdjunto(detalle.id, idx, a.nombre, a.mime_type, 'ver')}
                          className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                          title="Ver aquí"
                        >
                          Ver
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => accionAdjunto(detalle.id, idx, a.nombre, a.mime_type, 'descargar')}
                        className="text-xs px-2 py-1 rounded border border-duetto-primary text-duetto-primary hover:bg-duetto-primary hover:text-white transition"
                        title="Descargar"
                      >
                        Descargar
                      </button>
                      {esPdf && (
                        yaEnDrive ? (
                          <a
                            href={a.drive_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-2 py-1 rounded border border-duetto-primary/40 bg-duetto-primary/5 text-duetto-primary hover:bg-duetto-primary/15 transition inline-flex items-center gap-1"
                            title="Abrir en Drive"
                          >
                            <ExternalLink className="w-3 h-3" /> Abrir Drive
                          </a>
                        ) : (
                          <button
                            type="button"
                            onClick={() => moverAdjuntoADrive(detalle.id, idx)}
                            disabled={subiendo}
                            className="text-xs px-2 py-1 rounded border border-duetto-primary text-duetto-primary hover:bg-duetto-primary hover:text-white transition inline-flex items-center gap-1 disabled:opacity-50"
                            title="Subir a la carpeta de facturas de Drive"
                          >
                            {subiendo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            A Drive
                          </button>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* Móvil: barra inferior fija con las acciones del correo, al alcance del pulgar.
          Queda por debajo del editor (z-50), que se abre a pantalla completa encima. */}
      {esMovil && (
        <div
          className="fixed bottom-0 inset-x-0 z-40 bg-white border-t flex items-stretch"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-guia="iamail-barra-movil"
        >
          {detalle.es_borrador ? (
            <>
              <BotonBarraMovil
                icono={abriendoBorrador === detalle.id ? Loader2 : Pencil}
                iconoClase={abriendoBorrador === detalle.id ? 'animate-spin' : ''}
                etiqueta="Editar y enviar"
                tono="principal"
                onClick={() => abrirBorrador(detalle)}
                disabled={abriendoBorrador === detalle.id}
                data-uso="emails.borrador_abrir"
                data-uso-detalle="movil"
              />
              <BotonBarraMovil
                icono={Trash2}
                etiqueta="Eliminar borrador"
                tono="peligro"
                onClick={async () => {
                  const id = detalle.id;
                  await eliminarBorrador(id);
                  setDetalle((d) => (d && d.id === id ? null : d));
                }}
                data-uso="emails.borrador_eliminar"
                data-uso-detalle="movil"
              />
            </>
          ) : (
            <>
              <BotonBarraMovil
                icono={Reply}
                etiqueta="Responder"
                tono="principal"
                onClick={() => abrirResponder()}
                data-uso="emails.responder"
                data-uso-detalle="movil"
              />
              {((detalle.destinatarios || []).length + (detalle.cc || []).length) > 0 ? (
                <BotonBarraMovil
                  icono={ReplyAll}
                  etiqueta="Resp. a todos"
                  onClick={() => abrirResponder({ todos: true })}
                  data-uso="emails.responder"
                  data-uso-detalle="todos"
                />
              ) : (
                <BotonBarraMovil
                  icono={Forward}
                  etiqueta="Reenviar"
                  onClick={abrirReenviar}
                  data-uso="emails.reenviar"
                  data-uso-detalle="movil"
                />
              )}
              {detalle.estado_gestion === 'resuelto' ? (
                <BotonBarraMovil
                  icono={RefreshCcw}
                  etiqueta="Reabrir"
                  onClick={() => marcarGestionado(detalle, false)}
                  data-uso-detalle="movil"
                />
              ) : (
                <BotonBarraMovil
                  icono={CheckCircle2}
                  etiqueta="Gestionado"
                  onClick={() => marcarGestionado(detalle, true)}
                  data-uso-detalle="movil"
                />
              )}
              <BotonBarraMovil
                icono={detalle.archivado ? ArchiveRestore : Archive}
                etiqueta={detalle.archivado ? 'Desarchivar' : 'Archivar'}
                onClick={() => togglePropiedad(detalle, 'archivado')}
                data-uso-detalle="movil"
              />
              <BotonBarraMovil
                icono={MoreHorizontal}
                etiqueta="Más"
                onClick={() => setHojaMas(true)}
                data-uso-detalle="movil"
              />
            </>
          )}
        </div>
      )}

      {/* Móvil: hoja "Más" con el resto de acciones del correo */}
      {esMovil && hojaMas && !detalle.es_borrador && (
        <div
          className="fixed inset-0 z-[45] bg-black/40 flex flex-col justify-end"
          onClick={() => setHojaMas(false)}
        >
          <div
            className="bg-white rounded-t-2xl pt-2 shadow-2xl"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Más acciones del correo"
            data-guia="iamail-hoja-mas"
          >
            <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-gray-300" />
            {ayudaIaActiva && (
              <FilaHojaMovil
                icono={Sparkles}
                texto="Ayuda IA"
                onClick={enHojaMas(() => setAyudaIaAbierta(true))}
                data-uso="emails.ayuda_ia"
                data-uso-detalle="movil"
              />
            )}
            {((detalle.destinatarios || []).length + (detalle.cc || []).length) > 0 && (
              <FilaHojaMovil
                icono={Forward}
                texto="Reenviar"
                onClick={enHojaMas(abrirReenviar)}
                data-uso="emails.reenviar"
                data-uso-detalle="movil"
              />
            )}
            <FilaHojaMovil
              icono={Star}
              iconoRelleno={!!detalle.destacado}
              texto={detalle.destacado ? 'Quitar destacado' : 'Destacar'}
              onClick={enHojaMas(() => togglePropiedad(detalle, 'destacado'))}
              data-uso-detalle="movil"
            />
            <FilaHojaMovil
              icono={Link2}
              texto={detalle.presupuesto_id ? 'Cambiar el evento vinculado' : 'Vincular a un evento'}
              onClick={enHojaMas(() => abrirPicker('presupuesto', 'vincular'))}
              data-uso-detalle="movil"
            />
            {detalle.thread_id && !pendienteHilo && (
              <FilaHojaMovil
                icono={Plus}
                texto="Convertir en pendiente"
                onClick={enHojaMas(() => setModalPendienteAbierto(true))}
                data-uso-detalle="movil"
              />
            )}
            {!detalle.es_enviado && !detalle.es_borrador && !detalle.carpeta_auto?.carpeta && (
              <FilaHojaMovil
                icono={Ban}
                texto="Es spam"
                onClick={enHojaMas(() => marcarSpamFiltro(detalle))}
                data-uso-detalle="movil"
              />
            )}
            <FilaHojaMovil
              icono={Trash2}
              tono="peligro"
              texto={detalle.papelera ? 'Restaurar de la papelera' : 'Mover a la papelera'}
              onClick={enHojaMas(() => togglePropiedad(detalle, 'papelera'))}
              data-uso-detalle="movil"
            />
            <div className="border-t mt-1 pt-1">
              <FilaHojaMovil
                texto="Cancelar"
                centrado
                onClick={() => setHojaMas(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ==================== Panel IAmail: filas y chips ====================
  // Con administracion@ a la vista (o "Todos los buzones") manda la fila de
  // Administración; con solo buzones comerciales (Alfonso en a.palomares@),
  // la de Comercial.
  const adminPrimero = useMemo(() => {
    const ids = buzones.filter((id) => cuentasPorId[id]);
    if (!ids.length) return true;
    return ids.some((id) => (cuentasPorId[id]?.email || '').toLowerCase().startsWith('administracion@'));
  }, [buzones, cuentasPorId]);

  const filasIntencion = useMemo(() => {
    const por = iaPanel?.por_intencion || {};
    const claves = Object.keys(por).filter((k) => {
      const c = contadorIntencion(por[k]);
      return c.propios + c.en_copia > 0;
    });
    const orden = (lista, base) => [...lista].sort((a, b) => {
      const ia = base.indexOf(a);
      const ib = base.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return contadorIntencion(por[b]).propios - contadorIntencion(por[a]).propios;
    });
    const comercial = orden(claves.filter((k) => INTENCIONES_COMERCIAL.includes(k)), INTENCIONES_COMERCIAL);
    const administracion = orden(claves.filter((k) => !INTENCIONES_COMERCIAL.includes(k)), ORDEN_ADMINISTRACION);
    const suma = (lista) => lista.reduce((acc, k) => acc + contadorIntencion(por[k]).propios, 0);
    const filaAdmin = { id: 'administracion', titulo: 'Administración', claves: administracion, propios: suma(administracion) };
    const filaComercial = { id: 'comercial', titulo: 'Comercial', claves: comercial, propios: suma(comercial) };
    return adminPrimero ? [filaAdmin, filaComercial] : [filaComercial, filaAdmin];
  }, [iaPanel, adminPrimero]);

  if (!cuentasConectadas) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-white border rounded-xl p-10 text-center">
          <Mail className="w-12 h-12 mx-auto text-duetto-primary mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Conecta tu correo corporativo</h2>
          <p className="text-gray-600 mb-6">
            Añade una cuenta IMAP/SMTP (por ejemplo la de tu hosting cPanel) y el ERP
            sincronizará los correos, los clasificará con IA y los enlazará a presupuestos y clientes.
          </p>
          <button
            onClick={() => setFormAbierto(true)}
            className="inline-flex items-center gap-2 px-5 py-3 bg-duetto-primary text-white rounded-lg hover:bg-duetto-secondary transition"
          >
            <Plus className="w-4 h-4" /> Añadir cuenta
          </button>
          {error && (
            <div className="mt-6 flex items-center gap-2 justify-center text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
        </div>
        <FormularioCuenta abierto={formAbierto} cuenta={cuentaEditando} onCerrar={() => { setFormAbierto(false); setCuentaEditando(null); }} onGuardada={() => cargarCuentas()} />
      </div>
    );
  }

  const tareasVisibles = (iaPanel?.tareas || []).filter((t) => !tareasOcultas.has(t.id));

  const chipIntencionCorreo = (e) => {
    const k = e.intencion || e.analisis_ia?.intencion || e.categoria_ia;
    if (!k) return null;
    const cat = CATEGORIAS_IA.find((c) => c.id === k);
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] ${cat?.color || 'bg-gray-100 text-gray-700'}`}>
        {INTENCION_LABEL[k] || cat?.label || k}
      </span>
    );
  };

  // Fila común de todas las listas del panel: negrita si no está leído,
  // "Para ti" / "En copia", buzón, intención y enlaces a presupuesto o cliente.
  const renderCorreoPanel = (e, opciones = {}) => {
    const { gris = false, resumen, extra = null, sinIntencion = false, siempreBuzon = false, guiaCopiaAdmin = false } = opciones;
    const noLeido = e.leido === false && !leidosPanel.has(e.id);
    const texto = resumen !== undefined ? resumen : (e.resumen_ia || e.analisis_ia?.resumen || e.snippet);
    const buzonesCorreo = siempreBuzon ? (e.en_buzones?.length ? e.en_buzones : [e.cuenta_id]) : idsBuzones(e);
    // "Nuevo para ti" trae `para_administracion` y `accion_administracion`
    // arriba (sin analisis_ia): en copia pero con algo para administración.
    const copiaParaAdmin = Boolean(e.en_copia && e.para_administracion);
    return (
      <li key={e.id} className={`py-2 ${gris ? 'opacity-70' : ''}`}>
        <button
          type="button"
          onClick={() => abrirCorreoPanel(e.id)}
          data-uso="emails.abrir"
          data-uso-detalle="panel"
          className="w-full text-left hover:bg-gray-50 rounded p-2 -m-2 transition"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className={`text-sm truncate ${noLeido ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>
                {e.remitente_nombre || e.remitente}
                <span className="text-gray-400 font-normal"> · {fechaPanel(e.fecha)}</span>
              </div>
              <div className={`text-sm truncate ${noLeido ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                {e.asunto || '(sin asunto)'}
              </div>
              {texto && <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{texto}</div>}
              {copiaParaAdmin && e.accion_administracion && (
                <div className="text-xs text-amber-800 mt-0.5 truncate" title={e.accion_administracion}>
                  Para administración: {e.accion_administracion}
                </div>
              )}
            </div>
            {noLeido && <span className="w-2 h-2 rounded-full bg-duetto-primary mt-1.5 shrink-0" />}
          </div>
        </button>
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          <span className="inline-flex items-center gap-0.5 mr-1">
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); togglePropiedad(e, 'archivado'); }}
              className="p-1 rounded text-gray-400 hover:text-duetto-primary hover:bg-gray-100"
              title="Archivar sin abrir"
              data-testid={`panel-archivar-${e.id}`}
            >
              <Archive className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); togglePropiedad(e, 'papelera'); }}
              className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
              title="Mover a la papelera sin abrir"
              data-testid={`panel-papelera-${e.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); marcarListoPanel(e); }}
              className="p-1 rounded text-gray-400 hover:text-emerald-700 hover:bg-emerald-50"
              title="Listo: dar por gestionado sin abrir"
              data-testid={`panel-listo-${e.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
            </button>
          </span>
          {copiaParaAdmin ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800 border border-amber-200"
              title="Vas en copia, pero la IA cree que pide algo a administración"
              data-guia={guiaCopiaAdmin ? 'iamail-copia-para-admin' : undefined}
            >
              En copia · para administración
            </span>
          ) : e.en_copia ? (
            <EtiquetasCopia correo={e} />
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-100">
              Para ti
            </span>
          )}
          {!sinIntencion && chipIntencionCorreo(e)}
          <EtiquetaBuzones ids={buzonesCorreo} cuentasPorId={cuentasPorId} />
          {e.presupuesto_id && (
            <a
              href={`/presupuestos/${e.presupuesto_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(ev) => { if (esMovil) { ev.preventDefault(); irA(`/presupuestos/${e.presupuesto_id}`); } }}
              className="inline-flex items-center gap-0.5 text-[10px] text-duetto-primary hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> Presupuesto
            </a>
          )}
          {e.cliente_id && (
            <a
              href={`/clientes?cliente_id=${e.cliente_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(ev) => { if (esMovil) { ev.preventDefault(); irA(`/clientes?cliente_id=${e.cliente_id}`); } }}
              className="inline-flex items-center gap-0.5 text-[10px] text-duetto-primary hover:underline"
            >
              <ExternalLink className="w-3 h-3" /> Cliente
            </a>
          )}
        </div>
        {extra}
      </li>
    );
  };

  const renderChipIntencion = (k) => {
    const { propios, en_copia: copia } = contadorIntencion(iaPanel?.por_intencion?.[k]);
    const activo = intencionExpandida === k;
    return (
      <button
        key={k}
        type="button"
        onClick={() => toggleIntencion(k)}
        className={`px-3 py-1 rounded-full text-xs transition ${
          activo ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {INTENCION_LABEL[k] || k} <span className="font-semibold ml-1">{propios}</span>
        {copia > 0 && (
          <span className={`ml-1 ${activo ? 'text-white/80' : 'text-gray-400'}`}>+ {copia} en copia</span>
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-4rem)] bg-gray-50 relative">
      <Toaster />
      {aviso && (
        <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-[70] px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2 ${aviso.tono === 'info' ? 'bg-amber-600 text-white' : 'bg-duetto-primary text-white'}`}>
          <CheckCircle2 className="w-4 h-4" /> {aviso.texto}
        </div>
      )}
      {/* Barra móvil de carpetas (scroll horizontal) */}
      <div className="md:hidden flex items-center gap-1.5 overflow-x-auto px-2 py-2 border-b bg-white shrink-0">
        <button
          onClick={sincronizar}
          disabled={sincronizando}
          className="shrink-0 p-2 rounded-full border border-duetto-primary text-duetto-primary disabled:opacity-60"
          title="Sincronizar"
        >
          <RefreshCcw className={`w-4 h-4 ${sincronizando ? 'animate-spin' : ''}`} />
        </button>
        <button
          onClick={() => abrirNuevoMensaje()}
          disabled={cuentas.length === 0}
          data-testid="btn-redactar-movil"
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-duetto-primary text-white disabled:opacity-60"
          title="Redactar un correo nuevo"
        >
          <PenSquare className="w-3.5 h-3.5" /> Redactar
        </button>
        {variosBuzones && (
          <SelectorBuzones
            cuentas={cuentas}
            seleccion={buzones}
            onChange={cambiarBuzones}
            variante="barra"
            testid="selector-buzon-movil"
          />
        )}
        {CARPETAS.map((c) => {
          const Icon = c.icon;
          const activo = carpeta === c.id && !categoria;
          const base = 'shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs';
          let cls;
          if (activo) {
            cls = `${base} bg-duetto-primary text-white`;
          } else if (c.destacado) {
            cls = `${base} bg-green-50 text-duetto-primary font-semibold border border-duetto-secondary/40`;
          } else {
            cls = `${base} bg-gray-100 text-gray-700`;
          }
          return (
            <button
              key={c.id}
              onClick={() => { setCarpeta(c.id); setCategoria(null); setSeleccionado(null); }}
              className={cls}
            >
              <Icon className="w-3.5 h-3.5" /> {c.label}
              {(c.filtro || c.contador) && contadoresFiltro?.[c.id]?.total > 0 && (
                <span className="opacity-70">{contadoresFiltro[c.id].total}</span>
              )}
            </button>
          );
        })}
      </div>
      {/* Columna izquierda: carpetas y categorías IA */}
      <aside className="hidden md:block w-60 border-r bg-white p-3 overflow-y-auto">
        <button
          onClick={sincronizar}
          disabled={sincronizando}
          className="w-full mb-3 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg border border-duetto-primary text-duetto-primary hover:bg-duetto-primary hover:text-white transition disabled:opacity-60"
        >
          <RefreshCcw className={`w-4 h-4 ${sincronizando ? 'animate-spin' : ''}`} /> Sincronizar
        </button>
        <button
          onClick={() => abrirNuevoMensaje()}
          disabled={cuentas.length === 0}
          data-testid="btn-redactar"
          className="w-full mb-3 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg bg-duetto-primary text-white hover:opacity-90 transition disabled:opacity-60"
          title="Redactar un correo nuevo"
        >
          <PenSquare className="w-4 h-4" /> Redactar
        </button>
        {variosBuzones && (
          <div className="block mb-3">
            <span className="px-1 text-xs uppercase text-gray-500 font-semibold">Buzones</span>
            <SelectorBuzones
              cuentas={cuentas}
              seleccion={buzones}
              onChange={cambiarBuzones}
              variante="lateral"
              testid="selector-buzon"
            />
          </div>
        )}
        <nav className="space-y-1">
          {CARPETAS.map((c) => {
            const Icon = c.icon;
            const activo = carpeta === c.id && !categoria;
            const base = 'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition';
            let cls;
            if (c.destacado) {
              cls = activo
                ? `${base} bg-gradient-to-r from-duetto-primary to-duetto-secondary text-white font-semibold`
                : `${base} text-duetto-primary font-semibold bg-green-50 hover:bg-green-100 border border-duetto-secondary/40`;
            } else {
              cls = activo ? `${base} bg-duetto-primary text-white` : `${base} text-gray-700 hover:bg-gray-100`;
            }
            return (
              <button
                key={c.id}
                onClick={() => { setCarpeta(c.id); setCategoria(null); setSeleccionado(null); }}
                className={cls}
                data-testid={c.filtro || c.contador ? `carpeta-${c.id}` : undefined}
                data-guia={c.guia}
              >
                <Icon className="w-4 h-4" /> <span className="flex-1 text-left">{c.label}</span>
                {(c.filtro || c.contador) && contadoresFiltro?.[c.id]?.total > 0 && (
                  <span className={`text-xs ${activo ? 'text-white/80' : 'text-gray-400'}`}>{contadoresFiltro[c.id].total}</span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="mt-5 px-2 text-xs uppercase text-gray-500 font-semibold">IA · Hoy</div>
        <div className="mt-2 space-y-1">
          {CATEGORIAS_IA.map((cat) => {
            const count = resumen?.por_categoria?.[cat.id] || 0;
            const activo = categoria === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => { setCarpeta('bandeja'); setCategoria(activo ? null : cat.id); setSeleccionado(null); }}
                className={`w-full flex items-center justify-between px-3 py-1.5 rounded-md text-xs transition ${activo ? 'ring-2 ring-duetto-primary' : 'hover:bg-gray-100'}`}
              >
                <span className={`px-2 py-0.5 rounded-full ${cat.color}`}>{cat.label}</span>
                <span className="text-gray-500">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-5 px-2 text-xs uppercase text-gray-500 font-semibold">Cuentas</div>
        <div className="mt-2 space-y-1 text-xs text-gray-700">
          {cuentas.map((c) => (
            <div key={c.id} className={`flex items-center gap-1 px-2 py-1 rounded-md ${buzonVisible(c.id) ? '' : 'opacity-50'}`}>
              {variosBuzones && (
                <button
                  type="button"
                  onClick={() => alternarBuzon(c.id)}
                  className={`p-1 shrink-0 ${buzonVisible(c.id) ? 'text-duetto-primary hover:text-duetto-secondary' : 'text-gray-400 hover:text-gray-600'}`}
                  title={buzonVisible(c.id) ? 'Ocultar los correos de este buzón' : 'Mostrar los correos de este buzón'}
                  data-testid={`ojo-buzon-${c.id}`}
                >
                  {buzonVisible(c.id) ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate" title={c.email}>{c.email}</div>
                {!c.es_propietario && c.propietario_nombre && (
                  <div className="text-[10px] text-gray-400 truncate">De {c.propietario_nombre}</div>
                )}
              </div>
              {(c.puede_configurar || c.puede_gestionar_acceso) && (
                <button
                  type="button"
                  onClick={() => { setCuentaEditando(c); setFormAbierto(true); }}
                  className="p-1 text-gray-400 hover:text-duetto-primary shrink-0"
                  title="Editar la cuenta y quién la ve"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => { setCuentaEditando(null); setFormAbierto(true); }} className="w-full inline-flex items-center gap-1 px-2 py-1 text-xs text-duetto-primary hover:underline">
            <Plus className="w-3 h-3" /> Añadir otra
          </button>
          <button
            onClick={() => setListasFiltroAbierto(true)}
            className="w-full inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-duetto-primary hover:underline"
            data-testid="btn-listas-filtro"
          >
            <ListFilter className="w-3 h-3" /> Filtro de spam y publicidad
          </button>
        </div>
      </aside>

      {/* Vista IAmail: dashboard a pantalla completa */}
      {carpeta === 'iamail' && (
        <section className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-5xl mx-auto p-3 md:p-6 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-duetto-primary" /> IAmail
                </h1>
                <p className="text-sm text-gray-600">
                  Resumen inteligente de tu correo, últimos {iaRangoDias === 1 ? 'datos de hoy' : `${iaRangoDias} días`}.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={iaRangoDias}
                  onChange={(e) => setIaRangoDias(parseInt(e.target.value, 10) || 2)}
                  className="text-sm border rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  title="Rango temporal del resumen"
                >
                  <option value={1}>Hoy</option>
                  <option value={2}>48 horas</option>
                  <option value={7}>7 días</option>
                  <option value={14}>14 días</option>
                </select>
                <button
                  onClick={cargarIaPanel}
                  disabled={cargandoIa}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-duetto-primary text-duetto-primary hover:bg-duetto-primary hover:text-white transition disabled:opacity-50"
                >
                  <RefreshCcw className={`w-4 h-4 ${cargandoIa ? 'animate-spin' : ''}`} /> Actualizar
                </button>
                {driveEstado && !driveEstado.conectado && (
                  <button
                    onClick={iniciarConexionDrive}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                    title="Autoriza tu cuenta de Google para poder subir adjuntos a Drive"
                  >
                    <Upload className="w-4 h-4" /> Conectar Google Drive
                  </button>
                )}
                {driveEstado && driveEstado.conectado && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                    title={`Drive conectado con ${driveEstado.email || ''}`}
                  >
                    <Database className="w-3.5 h-3.5" /> Drive: {driveEstado.email || 'conectado'}
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`¿Desconectar Google Drive (${driveEstado.email || 'cuenta actual'})? Podrás conectar otra cuenta después.`)) return;
                        try {
                          await emailsAPI.driveDesconectar();
                          await refrescarDriveEstado();
                          mostrarAviso('Google Drive desconectado');
                        } catch (e) {
                          mostrarAviso('No se pudo desconectar Drive', 'info');
                        }
                      }}
                      className="ml-1 text-blue-500 hover:text-red-600"
                      title="Desconectar esta cuenta de Drive"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            </div>

            {/* Nuevo para ti: lo primero que se mira. Un correo solo sale de aquí
                cuando el usuario hace algo con él (archivar, papelera o Listo);
                leerlo no lo quita, solo le quita la negrita. */}
            {iaPanel && (
              <div className="bg-white border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-duetto-primary" />
                  <h2 className="font-semibold text-gray-900">Nuevo para ti</h2>
                  {(iaPanel.nuevos_total || 0) > 0 && (
                    <span className="text-xs text-gray-500">
                      {iaPanel.nuevos_total}
                      {(iaPanel.sin_leer_total || 0) > 0 ? ` · ${iaPanel.sin_leer_total} sin leer` : ''}
                    </span>
                  )}
                  <span className="text-[11px] text-gray-400 ml-auto">Sale de aquí al archivar, enviar a la papelera o marcar Listo</span>
                </div>
                {!(iaPanel.nuevos_sin_leer || []).length ? (
                  <div className="text-sm text-gray-500 italic">Nada nuevo pendiente de gestionar.</div>
                ) : (
                  <ul className="divide-y">
                    {(() => {
                      // La mini guía apunta al primer correo en copia para administración.
                      const lista = iaPanel.nuevos_sin_leer || [];
                      const primeroCopiaAdmin = lista.find((x) => x.en_copia && x.para_administracion)?.id;
                      return lista.map((e) => renderCorreoPanel(e, {
                        siempreBuzon: true,
                        guiaCopiaAdmin: e.id === primeroCopiaAdmin,
                      }));
                    })()}
                  </ul>
                )}
              </div>
            )}

            {/* Libro de pendientes: vive por encima del resumen IA porque es
                lo único que no caduca por antigüedad. */}
            <PendientesPanel
              version={pendientesVersion}
              onAbrirCorreo={(id) => setSeleccionado(id)}
            />

            {cargandoIa && !iaPanel && (
              <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Cargando resumen IA…
              </div>
            )}

            {iaPanel && (
              <>
                {/* Contadores. Criterio común (ver `ia_panel` en email_routes.py):
                    "Recibidos" son los propios del rango (dirigidos a nosotros o
                    en copia pero para administración); los que solo vamos en
                    copia se suman aparte en gris. "Con acciones" y "Sin responder"
                    usan el mismo corte de propios. */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white border rounded-xl px-3 py-2">
                    <div className="text-[11px] text-gray-500 uppercase font-semibold">
                      {iaRangoDias === 1 ? 'Recibidos hoy' : `Recibidos ${iaRangoDias} días`}
                    </div>
                    <div className="text-xl font-bold text-duetto-primary">
                      {iaPanel.total_propios ?? iaPanel.total ?? 0}
                      {(iaPanel.total_en_copia || 0) > 0 && (
                        <span className="text-xs font-normal text-gray-400 ml-1">+ {iaPanel.total_en_copia} en copia</span>
                      )}
                    </div>
                  </div>
                  <div className="bg-white border rounded-xl px-3 py-2">
                    <div className="text-[11px] text-gray-500 uppercase font-semibold">Sin leer</div>
                    <div className="text-xl font-bold text-duetto-primary">{iaPanel.sin_leer_total || 0}</div>
                  </div>
                  {tareasVisibles.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setVerTareas((v) => !v)}
                      className={`text-left bg-white border rounded-xl px-3 py-2 transition hover:border-amber-400 ${verTareas ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
                      title={verTareas ? 'Ocultar los correos con acciones' : 'Ver los correos con acciones'}
                    >
                      <div className="text-[11px] text-gray-500 uppercase font-semibold flex items-center gap-1">
                        Con acciones
                        <ChevronDown className={`w-3 h-3 transition ${verTareas ? '' : '-rotate-90'}`} />
                      </div>
                      <div className="text-xl font-bold text-amber-600">{tareasVisibles.length}</div>
                    </button>
                  ) : (
                    <div className="bg-white border rounded-xl px-3 py-2">
                      <div className="text-[11px] text-gray-500 uppercase font-semibold">Con acciones</div>
                      <div className="text-xl font-bold text-amber-600">0</div>
                    </div>
                  )}
                  <div className="bg-white border rounded-xl px-3 py-2">
                    <div className="text-[11px] text-gray-500 uppercase font-semibold">Sin responder &gt;24 h</div>
                    <div className="text-xl font-bold text-red-600">{iaPanel.respuestas_pendientes?.length || 0}</div>
                  </div>
                </div>

                {/* Con acciones: correos propios con una acción propuesta por la IA */}
                {verTareas && tareasVisibles.length > 0 && (
                  <div className="bg-white border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-800">Con acciones ({tareasVisibles.length})</div>
                      <button
                        type="button"
                        onClick={() => setVerTareas(false)}
                        className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> Cerrar
                      </button>
                    </div>
                    <ul className="divide-y">
                      {tareasVisibles.map((t) => {
                        const yaEnPendientes = t.en_pendientes || tareasAnadidas.has(t.id);
                        const ocupada = tareaOcupada === t.id;
                        return renderCorreoPanel(t, {
                          resumen: accionSugeridaDe(t) || t.analisis_ia?.resumen || '',
                          extra: (
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              {yaEnPendientes ? (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-gray-50 text-gray-500 border border-gray-200">
                                  <CheckCircle2 className="w-3 h-3" /> En pendientes
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  disabled={ocupada}
                                  onClick={() => anadirTareaAPendientes(t)}
                                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-duetto-primary/10 text-duetto-primary hover:bg-duetto-primary/20 border-duetto-primary/30 disabled:opacity-50"
                                >
                                  <Plus className="w-3 h-3" /> Añadir a pendientes
                                </button>
                              )}
                              <button
                                type="button"
                                disabled={ocupada}
                                onClick={() => descartarTarea(t)}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-gray-50 text-gray-600 hover:bg-gray-100 border-gray-200 disabled:opacity-50"
                                title="Marcar el correo como resuelto: sale del panel"
                              >
                                <X className="w-3 h-3" /> Descartar
                              </button>
                            </div>
                          ),
                        });
                      })}
                    </ul>
                  </div>
                )}

                {/* Distribución por intención en dos filas (Administración y
                    Comercial). La secundaria va plegada según los buzones. */}
                {filasIntencion.some((f) => f.claves.length > 0) && (
                  <div className="bg-white border rounded-xl p-4">
                    <div className="text-xs uppercase text-gray-500 font-semibold mb-2">
                      Por intención · {iaRangoDias === 1 ? 'hoy' : `últimos ${iaRangoDias} días`} · pulsa para ver
                    </div>
                    {filasIntencion.map((fila, posicion) => {
                      if (!fila.claves.length) return null;
                      const plegada = posicion === 1 && !verFilaSecundaria;
                      return (
                        <div key={fila.id} className={posicion === 1 ? 'mt-3 pt-3 border-t' : ''}>
                          {posicion === 1 ? (
                            <button
                              type="button"
                              onClick={() => setVerFilaSecundaria((v) => !v)}
                              className="text-xs text-gray-600 hover:text-duetto-primary inline-flex items-center gap-1 mb-2"
                            >
                              <ChevronDown className={`w-3 h-3 transition ${plegada ? '-rotate-90' : ''}`} />
                              {plegada ? `Ver ${fila.titulo.toLowerCase()} (${fila.propios})` : `Ocultar ${fila.titulo.toLowerCase()}`}
                            </button>
                          ) : (
                            <div className="text-[11px] text-gray-500 font-semibold mb-1.5">{fila.titulo}</div>
                          )}
                          {!plegada && (
                            <div className="flex flex-wrap gap-2">
                              {fila.claves.map((k) => renderChipIntencion(k))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {intencionExpandida && (
                      <div className="mt-4 border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-medium text-gray-800">
                            {INTENCION_LABEL[intencionExpandida] || intencionExpandida}
                            <span className="text-xs text-gray-500 font-normal ml-2">
                              {cargandoIntencion
                                ? 'cargando…'
                                : `${emailsIntencion.propios.length} para ti${emailsIntencion.en_copia.length ? ` + ${emailsIntencion.en_copia.length} en copia` : ''}`}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => { setIntencionExpandida(null); setEmailsIntencion({ propios: [], en_copia: [] }); }}
                            className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                          >
                            <X className="w-3 h-3" /> Cerrar
                          </button>
                        </div>
                        {cargandoIntencion ? (
                          <div className="text-sm text-gray-500 italic py-3"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Cargando…</div>
                        ) : emailsIntencion.propios.length + emailsIntencion.en_copia.length === 0 ? (
                          <div className="text-sm text-gray-500 italic py-3">Sin correos en esta intención.</div>
                        ) : (
                          <>
                            {emailsIntencion.propios.length > 0 && (
                              <ul className="divide-y">
                                {emailsIntencion.propios.map((e) => renderCorreoPanel(e, { sinIntencion: true }))}
                              </ul>
                            )}
                            {emailsIntencion.en_copia.length > 0 && (
                              <>
                                <div className="mt-3 mb-1 flex items-center gap-2 text-[11px] uppercase font-semibold text-gray-400">
                                  <span>En copia (comercial)</span>
                                  <span className="flex-1 border-t border-gray-200" />
                                </div>
                                <ul className="divide-y">
                                  {emailsIntencion.en_copia.map((e) => renderCorreoPanel(e, { gris: true, sinIntencion: true }))}
                                </ul>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Comercial, en copia: correos dirigidos a otro compañero */}
                <div className="bg-white border rounded-xl p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-800">
                      Comercial, en copia
                      <span className="text-gray-500 font-normal ml-1">({iaPanel.en_copia_total || 0})</span>
                    </div>
                    <button
                      type="button"
                      onClick={toggleEnCopia}
                      className="text-xs text-gray-600 hover:text-duetto-primary underline"
                    >
                      {verEnCopia ? 'Cerrar' : 'Ver'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Correos dirigidos a otro compañero. No cuentan como tarea ni como respuesta pendiente,
                    salvo que pidan algo a administración.
                  </p>

                  {verEnCopia && (
                    <div className="mt-3 border-t pt-3">
                      {cargandoEnCopia ? (
                        <div className="text-sm text-gray-500 italic py-3">
                          <Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Cargando…
                        </div>
                      ) : emailsEnCopia.length === 0 ? (
                        <div className="text-sm text-gray-500 italic py-3">Ningún correo en copia en este rango.</div>
                      ) : (
                        <ul className="divide-y">
                          {emailsEnCopia.map((e) => renderCorreoPanel(e, { gris: !e.analisis_ia?.para_administracion }))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Respuestas pendientes: si no hay, una sola línea */}
                {!iaPanel.respuestas_pendientes?.length ? (
                  <div className="bg-white border rounded-xl px-4 py-2 text-sm text-gray-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="font-medium">Respuestas pendientes &gt;24 h:</span>
                    <span className="text-gray-500 italic">ninguna.</span>
                  </div>
                ) : (
                  <div className="bg-white border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <h2 className="font-semibold text-gray-900">Respuestas pendientes &gt;24 h</h2>
                    </div>
                    <ul className="divide-y">
                      {iaPanel.respuestas_pendientes.map((r) => renderCorreoPanel(r, {
                        resumen: r.snippet || '',
                        extra: (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {r.estado_gestion === 'esperando' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-amber-50 text-amber-700">
                                Esperando respuesta
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                accionTrasCargarRef.current = { emailId: r.id, tipo: 'responder', borradorHtml: '', facturaId: null };
                                abrirCorreoPanel(r.id);
                              }}
                              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border bg-duetto-primary/10 text-duetto-primary hover:bg-duetto-primary/20 border-duetto-primary/30"
                              title="Abrir el correo y responder"
                              data-uso="emails.responder"
                              data-uso-detalle="pendientes"
                            >
                              <Reply className="w-3 h-3" /> Responder
                            </button>
                          </div>
                        ),
                      }))}
                    </ul>
                  </div>
                )}

                {/* Contactos activos: al final y plegado */}
                <div className="bg-white border rounded-xl px-4 py-2">
                  <button
                    type="button"
                    onClick={() => setVerContactos((v) => !v)}
                    className="text-xs text-gray-600 hover:text-duetto-primary inline-flex items-center gap-1"
                  >
                    <ChevronDown className={`w-3 h-3 transition ${verContactos ? '' : '-rotate-90'}`} />
                    {verContactos ? 'Ocultar contactos activos' : `Ver contactos activos (${iaPanel.clientes_activos?.length || 0})`}
                  </button>
                  {verContactos && (
                    !iaPanel.clientes_activos?.length ? (
                      <div className="text-sm text-gray-500 italic py-2">Ninguno.</div>
                    ) : (
                      <ul className="space-y-2 py-2">
                        {iaPanel.clientes_activos.map((c) => (
                          <li key={c.email} className="flex items-center justify-between text-sm">
                            <div className="min-w-0">
                              <div className="text-gray-900 truncate">{c.nombre || c.email}</div>
                              {c.nombre && <div className="text-xs text-gray-500 truncate">{c.email}</div>}
                            </div>
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{c.num_mensajes}</span>
                          </li>
                        ))}
                      </ul>
                    )
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Drawer lateral: abrir un correo desde IAmail sin salir del panel */}
      {carpeta === 'iamail' && seleccionado && (
        <div className="fixed inset-0 z-40 bg-black/30" onClick={cerrarDetalle}>
          <div
            className="absolute right-0 top-0 h-full w-full md:w-[760px] lg:w-[860px] bg-gray-50 shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-white shrink-0">
              <button
                onClick={cerrarDetalle}
                className="inline-flex items-center gap-1.5 min-h-[44px] md:min-h-0 px-3 py-1.5 rounded-lg border bg-white text-sm text-gray-700 hover:bg-gray-50"
              >
                <X className="w-4 h-4" /> Cerrar
              </button>
              <div className="flex items-center gap-2">
                {/* En el móvil no se ofrece: la PWA no abre pestañas */}
                <a
                  href={`/emails?email=${seleccionado}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-duetto-primary hover:underline hidden md:inline-flex items-center gap-1"
                  title="Abrir este correo en una pestaña nueva"
                >
                  <ExternalLink className="w-3 h-3" /> Abrir en pestaña nueva
                </a>
                <button
                  onClick={() => { setCarpeta('bandeja'); }}
                  className="text-xs text-gray-600 hover:text-duetto-primary underline min-h-[44px] md:min-h-0 px-2 md:px-0"
                  title="Ver este correo en la Bandeja"
                >
                  Ver en Bandeja
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {detalle ? renderDetalle({ enDrawer: true }) : (
                <div className="p-8 text-center text-sm text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Cargando correo...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Columna central: lista (sólo en carpetas normales) */}
      {carpeta !== 'iamail' && <>
      <section className={`w-full md:w-[380px] border-r bg-white flex-col min-h-0 ${seleccionado ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar en correos"
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
            />
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-500">
            <span>{total} correos</span>
            {variosBuzones && (
              <label className="inline-flex items-center gap-1 cursor-pointer" title="Muestra también las copias de un mensaje que llegó a varios buzones">
                <input type="checkbox" checked={mostrarCopias} onChange={(e) => setMostrarCopias(e.target.checked)} />
                Mostrar copias
              </label>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {cargando && <div className="p-6 text-center text-sm text-gray-500">Cargando…</div>}
          {!cargando && emails.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500">No hay correos</div>
          )}
          {carpeta === 'borradores' && emails.map((e) => (
            // Borrador del servidor: se abre en el editor para continuarlo.
            <div key={e.id} className="flex items-stretch border-b hover:bg-gray-50 transition" data-testid="fila-borrador">
              <button
                type="button"
                onClick={() => abrirBorrador(e)}
                disabled={abriendoBorrador === e.id}
                data-uso="emails.borrador_abrir"
                className="flex-1 min-w-0 text-left px-4 py-3 disabled:opacity-60"
                title="Continuar este borrador"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm truncate text-red-700">
                    Borrador{(e.destinatarios || []).length ? `: ${e.destinatarios.join(', ')}` : ' sin destinatario'}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">
                    {abriendoBorrador === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : formateaFecha(e.fecha)}
                  </span>
                </div>
                <div className="text-sm truncate mt-0.5">{e.asunto || '(sin asunto)'}</div>
                <div className="text-xs text-gray-500 truncate mt-0.5">{e.snippet}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(e.adjuntos || []).length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600">
                      <Paperclip className="w-3 h-3" /> {e.adjuntos.length}
                    </span>
                  )}
                  <EtiquetaBuzones ids={idsBuzones(e)} cuentasPorId={cuentasPorId} />
                </div>
              </button>
              <button
                type="button"
                onClick={() => eliminarBorrador(e.id)}
                className="px-3 text-gray-400 hover:text-red-600 shrink-0"
                title="Eliminar borrador"
                data-uso="emails.borrador_eliminar"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {carpeta !== 'borradores' && emails.map((e) => {
            const activo = seleccionado === e.id;
            return (
              <button
                key={e.id}
                onClick={() => setSeleccionado(e.id)}
                data-uso="emails.abrir"
                className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 transition ${activo ? 'bg-duetto-primary/10' : ''} ${!e.leido ? 'font-semibold' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm truncate flex items-center gap-1.5">
                    {e.destacado && (
                      <Star className="w-3.5 h-3.5 text-amber-500 shrink-0" fill="currentColor" />
                    )}
                    {e.remitente_nombre || e.remitente}
                  </span>
                  <span className="text-xs text-gray-500 shrink-0">{formateaFecha(e.fecha)}</span>
                </div>
                <div className="text-sm truncate mt-0.5">{e.asunto || '(sin asunto)'}</div>
                <div className="text-xs text-gray-500 truncate mt-0.5">{e.snippet}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {e.categoria_ia && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] ${CATEGORIAS_IA.find((c) => c.id === e.categoria_ia)?.color || 'bg-gray-100 text-gray-800'}`}>
                      <Tag className="w-3 h-3 mr-1" />
                      {CATEGORIAS_IA.find((c) => c.id === e.categoria_ia)?.label || e.categoria_ia}
                    </span>
                  )}
                  {e.adjuntos_en_drive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-duetto-primary/10 text-duetto-primary">
                      <CheckCircle2 className="w-3 h-3" /> Factura en Drive
                    </span>
                  )}
                  {e.archivado && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600">
                      <Archive className="w-3 h-3" /> Archivado
                    </span>
                  )}
                  {e.contestado && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-emerald-50 text-emerald-700">
                      <Reply className="w-3 h-3" /> Contestado
                    </span>
                  )}
                  <ChipFiltro correo={e} />
                  <EtiquetasCopia correo={e} />
                  <EtiquetaBuzones ids={idsBuzones(e)} cuentasPorId={cuentasPorId} />
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Columna derecha: detalle */}
      <section className={`flex-1 overflow-y-auto bg-gray-50 ${seleccionado ? 'block' : 'hidden md:block'}`}>
        {!detalle && (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            Selecciona un correo
          </div>
        )}
        {detalle && renderDetalle()}
      </section>
      </>}

      <ModalListasFiltro abierto={listasFiltroAbierto} onCerrar={() => setListasFiltroAbierto(false)} />

      <FormularioCuenta abierto={formAbierto} cuenta={cuentaEditando} onCerrar={() => { setFormAbierto(false); setCuentaEditando(null); }} onGuardada={() => cargarCuentas()} />

      {/* Alta de pendiente desde el detalle del correo ("Convertir en pendiente") */}
      <ModalPendiente
        abierto={modalPendienteAbierto}
        inicial={{
          titulo: (
            analisis?.accion_interna
            || `Contestar a ${detalle?.remitente_nombre || detalle?.remitente || ''}: ${detalle?.asunto || ''}`
          ),
          email_id: detalle?.id || null,
          presupuesto_id: detalle?.presupuesto_id || null,
          cliente_id: detalle?.cliente_id || null,
        }}
        onCerrar={() => setModalPendienteAbierto(false)}
        onCreado={() => {
          setPendientesVersion((v) => v + 1);
          mostrarAviso('Pendiente creado');
        }}
      />

      {/* Editor. En el móvil, a pantalla completa con Enviar arriba (visible también
          con el teclado abierto: la caja mide 100dvh y solo se desplaza el cuerpo). */}
      {componer && (
        <div
          className={esMovil
            ? 'fixed inset-x-0 top-0 h-screen h-dvh z-50 bg-white flex flex-col'
            : 'fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-4'}
          // iOS: con el teclado abierto se ajusta a la parte visible (visualViewport)
          style={esMovil && vistaVisible ? { top: vistaVisible.arriba, height: vistaVisible.alto } : undefined}
          onClick={esMovil ? undefined : intentarCerrarComposer}
        >
          <div
            className={esMovil
              ? 'bg-white w-full max-w-none h-full flex flex-col'
              : 'bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col'}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between border-b ${esMovil ? 'shrink-0 gap-2 px-2 py-1.5' : 'px-4 py-3'}`}>
              {esMovil && (
                <button
                  type="button"
                  onClick={intentarCerrarComposer}
                  className="shrink-0 w-11 h-11 inline-flex items-center justify-center rounded-lg text-gray-600 active:bg-gray-100"
                  aria-label="Cerrar"
                  title="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              <div className={`flex items-center gap-2 ${esMovil ? 'flex-1 min-w-0 flex-wrap' : ''}`}>
                {(() => {
                  const IconoModo = componer.modo === 'reenviar' ? Forward
                    : componer.modo === 'responder_todos' ? ReplyAll
                    : componer.modo === 'responder' ? Reply
                    : componer.modo === 'borrador' ? FileText
                    : PenSquare;
                  return <IconoModo className="w-4 h-4 text-duetto-primary" />;
                })()}
                <h3 className="font-semibold text-gray-900">
                  {componer.modo === 'reenviar' ? 'Reenviar'
                    : componer.modo === 'responder_todos' ? 'Responder a todos'
                    : componer.modo === 'responder' ? 'Responder'
                    : componer.modo === 'borrador' ? 'Continuar borrador'
                    : 'Nuevo mensaje'}
                </h3>
                {componer.factura_id && (
                  <button
                    type="button"
                    onClick={() => setVisorAdjunto({
                      titulo: `Factura ${componer.factura_numero || ''}`.trim() + '.pdf',
                      url: `/facturas/${componer.factura_id}/pdf`,
                      mime: 'application/pdf',
                    })}
                    data-uso="emails.adjunto_ver"
                    data-uso-detalle="factura"
                    className="inline-flex items-center gap-1 text-xs bg-duetto-primary/10 text-duetto-primary px-2 py-0.5 rounded hover:bg-duetto-primary/20 underline-offset-2 hover:underline"
                    title="Ver la factura que se va a enviar"
                  >
                    <Paperclip className="w-3 h-3" />
                    Factura {componer.factura_numero || ''} adjunta
                  </button>
                )}
              </div>
              {esMovil ? (
                <button
                  type="button"
                  onClick={enviarComposicion}
                  disabled={componer.enviando || componer.guardando || componer.adjuntando_originales > 0}
                  data-guia="iamail-enviar-movil"
                  data-uso="emails.enviar"
                  data-uso-detalle="movil"
                  className="shrink-0 inline-flex items-center gap-1.5 h-11 px-4 rounded-lg bg-duetto-primary text-white text-sm font-medium disabled:opacity-50"
                >
                  {componer.enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {componer.enviando ? 'Enviando...' : 'Enviar'}
                </button>
              ) : (
                <button onClick={intentarCerrarComposer} className="p-1 rounded hover:bg-gray-100" title="Cerrar">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className={esMovil ? 'flex-1 min-h-0 p-3 space-y-3 overflow-y-auto overscroll-contain' : 'p-4 space-y-3 overflow-y-auto'}>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 w-16 shrink-0">De</label>
                <select
                  value={componer.cuenta_id || ''}
                  onChange={(e) => cambiarCuentaComposer(e.target.value)}
                  className="flex-1 min-w-0 border rounded px-2 py-1.5 text-sm focus:border-duetto-primary outline-none bg-white"
                  data-testid="composer-de"
                  title="Cuenta desde la que se envía"
                >
                  {!cuentasEnvio.some((c) => c.id === componer.cuenta_id) && cuentasPorId[componer.cuenta_id] && (
                    <option value={componer.cuenta_id} disabled>
                      {cuentasPorId[componer.cuenta_id].email} (sin envío configurado)
                    </option>
                  )}
                  {cuentasEnvio.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre_cuenta ? `${c.nombre_cuenta} <${c.email}>` : c.email}
                    </option>
                  ))}
                </select>
              </div>
              <RecipientChips
                label="Para"
                value={componer.para || []}
                onChange={(lista) => setComponer({ ...componer, para: lista })}
                placeholder="destinatario@ejemplo.com (Enter o coma para añadir)"
              />
              <div className="flex items-center justify-end gap-3 text-xs -mt-1">
                {!componer.mostrar_cc && (
                  <button
                    type="button"
                    onClick={() => setComponer({ ...componer, mostrar_cc: true })}
                    className="text-duetto-primary hover:underline"
                  >
                    + CC
                  </button>
                )}
                {!componer.mostrar_bcc && (
                  <button
                    type="button"
                    onClick={() => setComponer({ ...componer, mostrar_bcc: true })}
                    className="text-duetto-primary hover:underline"
                  >
                    + CCO
                  </button>
                )}
              </div>
              {(componer.mostrar_cc || (componer.cc || []).length > 0) && (
                <RecipientChips
                  label="CC"
                  value={componer.cc || []}
                  onChange={(lista) => setComponer({ ...componer, cc: lista })}
                  placeholder="(varios, separa con coma)"
                />
              )}
              {(componer.mostrar_bcc || (componer.bcc || []).length > 0) && (
                <RecipientChips
                  label="CCO"
                  value={componer.bcc || []}
                  onChange={(lista) => setComponer({ ...componer, bcc: lista })}
                  placeholder="(copia oculta)"
                />
              )}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 w-16 shrink-0">Asunto</label>
                <input
                  type="text"
                  value={componer.asunto}
                  onChange={(e) => setComponer({ ...componer, asunto: e.target.value })}
                  className="flex-1 border rounded px-2 py-1.5 text-sm focus:border-duetto-primary outline-none"
                />
              </div>
              <EditorRich
                // El editor solo lee `value` al montarse: se recrea en cada apertura
                // (responder -> responder a todos, borrador IA...) para no quedarse
                // con el contenido anterior y pisar el nuevo al escribir.
                key={componer.abierto_en || 'compositor'}
                value={componer.body_html}
                onChange={(html) => setComponer((c) => (c ? { ...c, body_html: html } : c))}
              />
              {(componer.adjuntos || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {componer.adjuntos.map((a, idx) => (
                    <div key={`${a.nombre}-${idx}`} className="inline-flex items-center gap-2 px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs">
                      <Paperclip className="w-3 h-3 text-gray-500" />
                      <button
                        type="button"
                        onClick={() => {
                          if (!a.contenido_base64) {
                            mostrarAviso('Este adjunto todavía se está cargando. Prueba de nuevo en un momento.', 'info');
                            return;
                          }
                          setVisorAdjunto({ titulo: a.nombre, contenidoBase64: a.contenido_base64, mime: a.mime_type });
                        }}
                        data-uso="emails.adjunto_ver"
                        data-guia={idx === 0 ? 'adjunto-ver' : undefined}
                        className="font-medium text-duetto-primary truncate max-w-[200px] hover:underline text-left"
                        title={`Ver ${a.nombre}`}
                      >
                        {a.nombre}
                      </button>
                      <span className="text-gray-500">{formatoTamano(a.tamano)}</span>
                      <button
                        onClick={() => quitarAdjunto(idx)}
                        className="ml-1 p-0.5 rounded hover:bg-gray-200"
                        title="Quitar"
                      >
                        <X className="w-3 h-3 text-gray-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {componer.adjuntando_originales > 0 && (
                <div className="text-xs text-gray-500 inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Recuperando adjuntos del correo original...
                </div>
              )}
              {componer.firma_id && componer.firma_id !== 'ninguna' && (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="text-xs text-gray-500 mb-2">Vista previa de firma (el logo y el texto legal se añaden al enviar)</div>
                  <p className="text-sm font-bold" style={{ color: '#1A9169' }}>
                    {FIRMAS_EMAIL[componer.firma_id]?.nombre || ''} <span className="font-normal text-gray-600">· Duetto Catering</span>
                  </p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    Tel. 676 156 256<br />
                    Email administracion@duettocatering.com<br />
                    Web duettocatering.com · @duettocatering
                  </p>
                </div>
              )}
            </div>
            <div
              className={esMovil
                ? 'shrink-0 px-3 py-2 border-t flex flex-wrap items-center gap-2'
                : 'px-4 py-3 border-t flex items-center justify-between gap-2'}
              style={esMovil ? { paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' } : undefined}
            >
              <div className={`flex items-center text-sm ${esMovil ? 'gap-2' : 'gap-3'}`}>
                <div className="relative">
                  <button
                    onClick={() => setAdjuntarMenuAbierto((v) => !v)}
                    className={`inline-flex items-center gap-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 ${esMovil ? 'h-10 px-3' : 'px-2 py-1'}`}
                    title="Adjuntar"
                  >
                    <Paperclip className="w-4 h-4" />
                    Adjuntar
                  </button>
                  {adjuntarMenuAbierto && (
                    <div className="absolute bottom-full left-0 mb-1 w-56 bg-white border border-gray-200 rounded shadow-lg z-10">
                      <button
                        onClick={() => abrirPicker('presupuesto')}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4 text-duetto-primary" />
                        Presupuesto del ERP
                      </button>
                      <button
                        onClick={() => abrirPicker('factura')}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4 text-duetto-primary" />
                        Factura del ERP
                      </button>
                      <button
                        onClick={() => { setAdjuntarMenuAbierto(false); fileInputRef.current?.click(); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 border-t"
                      >
                        <Upload className="w-4 h-4 text-gray-600" />
                        Archivo del equipo
                      </button>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => { adjuntarDesdeEquipo(e.target.files); e.target.value = ''; }}
                  />
                </div>
                <label className={`text-xs text-gray-500 ${esMovil ? 'sr-only' : ''}`}>Firma</label>
                <select
                  value={componer.firma_id || 'alfonso'}
                  onChange={(e) => setComponer((c) => (c ? { ...c, firma_id: e.target.value, firma_manual: true } : c))}
                  className={`text-sm border border-gray-300 rounded px-2 ${esMovil ? 'h-10 max-w-[44vw]' : 'py-1'}`}
                  aria-label="Firma"
                >
                  <option value="alfonso">Alfonso Palomares</option>
                  <option value="administracion">Administración</option>
                  <option value="ninguna">Sin firma</option>
                </select>
              </div>
              <div className={esMovil ? 'contents' : 'flex flex-wrap items-center justify-end gap-2'}>
                <span className={`text-[11px] text-gray-400 mr-auto ${esMovil ? 'hidden' : ''}`}>
                  {componer.borrador_recuperado ? 'Borrador recuperado.' : ''}
                  {cuentasPorId[componer.cuenta_id]?.es_propietario
                    ? ' Guárdalo en tu correo con Guardar borrador (también se guarda en este navegador).'
                    : ' Se guarda automáticamente en este navegador.'}
                </span>
                {componer.borrador_id ? (
                  <button
                    type="button"
                    onClick={() => eliminarBorrador(componer.borrador_id)}
                    disabled={componer.enviando || componer.guardando}
                    className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-red-500 underline disabled:opacity-50"
                  >
                    <Trash2 className="w-3 h-3" /> Eliminar borrador
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={descartarComposer}
                    className="text-[11px] text-gray-400 hover:text-red-500 underline"
                  >
                    Descartar borrador
                  </button>
                )}
                <button
                  onClick={intentarCerrarComposer}
                  className={`px-3 py-1.5 rounded border border-gray-300 text-sm hover:bg-gray-50 ${esMovil ? 'hidden' : ''}`}
                >
                  Cancelar
                </button>
                {cuentasPorId[componer.cuenta_id]?.es_propietario && (
                  <button
                    type="button"
                    onClick={() => guardarBorradorServidor()}
                    disabled={componer.enviando || componer.guardando || componer.adjuntando_originales > 0}
                    data-testid="btn-guardar-borrador"
                    data-uso="emails.borrador_guardar"
                    className={`flex items-center gap-1.5 rounded border border-duetto-primary text-duetto-primary text-sm hover:bg-duetto-primary/10 disabled:opacity-50 ${esMovil ? 'h-10 px-3' : 'px-3 py-1.5'}`}
                  >
                    {componer.guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    {componer.guardando ? 'Guardando...' : (esMovil ? 'Guardar' : 'Guardar borrador')}
                  </button>
                )}
                <button
                  onClick={enviarComposicion}
                  disabled={componer.enviando || componer.guardando || componer.adjuntando_originales > 0}
                  className={`${esMovil ? 'hidden' : 'flex'} items-center gap-1.5 px-4 py-1.5 rounded bg-duetto-primary text-white hover:bg-duetto-primary/90 text-sm disabled:opacity-50`}
                >
                  {componer.enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {componer.enviando ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {componer && cierreComposer && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setCierreComposer(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            data-testid="dialogo-cerrar-composer"
          >
            <div>
              <h3 className="font-semibold text-gray-900">¿Guardar el borrador?</h3>
              <p className="text-sm text-gray-600 mt-1">
                {cuentasPorId[componer.cuenta_id]?.es_propietario
                  ? 'Has escrito o cambiado algo. Si lo guardas, lo tendrás en Borradores, también en el móvil.'
                  : 'Has escrito o cambiado algo. Este buzón no es tuyo: el borrador solo se queda en este navegador.'}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {cuentasPorId[componer.cuenta_id]?.es_propietario ? (
                <button
                  type="button"
                  onClick={() => guardarBorradorServidor({ cerrar: true })}
                  disabled={componer.guardando}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded bg-duetto-primary text-white text-sm hover:bg-duetto-primary/90 disabled:opacity-50"
                >
                  {componer.guardando && <Loader2 className="w-4 h-4 animate-spin" />}
                  Guardar borrador
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { guardarComposerLocal(componer); setCierreComposer(false); setComponer(null); }}
                  className="w-full px-4 py-2 rounded bg-duetto-primary text-white text-sm hover:bg-duetto-primary/90"
                >
                  Guardar en este navegador
                </button>
              )}
              <button
                type="button"
                onClick={descartarComposer}
                disabled={componer.guardando}
                className="w-full px-4 py-2 rounded border border-red-200 text-red-600 text-sm hover:bg-red-50 disabled:opacity-50"
              >
                {componer.borrador_id ? 'Descartar los cambios' : 'Descartar'}
              </button>
              <button
                type="button"
                onClick={() => setCierreComposer(false)}
                disabled={componer.guardando}
                className="w-full px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Seguir escribiendo
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerAdjuntos && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !adjuntandoRef && setPickerAdjuntos(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {pickerAdjuntos.modo === 'vincular'
                  ? (detalle?.presupuesto_id ? 'Cambiar el evento vinculado' : 'Vincular este correo a un evento (presupuesto)')
                  : `Adjuntar ${pickerAdjuntos.tipo === 'presupuesto' ? 'presupuesto' : 'factura'}`}
              </h3>
              <button onClick={() => !adjuntandoRef && setPickerAdjuntos(null)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            {pickerAdjuntos.cliente && (
              <div className="px-4 py-2 border-b bg-duetto-primary/5 text-xs text-gray-700 flex items-center gap-2">
                <Tag className="w-3 h-3 text-duetto-primary" />
                Mostrando primero los del cliente:
                <span className="font-semibold text-duetto-primary">
                  {pickerAdjuntos.cliente.nombre_comercial
                    || pickerAdjuntos.cliente.nombre_empresa
                    || pickerAdjuntos.cliente.nombre_fiscal
                    || pickerAdjuntos.cliente.nombre
                    || pickerAdjuntos.cliente.id}
                </span>
              </div>
            )}
            <div className="flex flex-1 overflow-hidden">
              <div className="w-2/5 border-r flex flex-col">
                <div className="px-4 py-2 border-b">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por número o cliente..."
                      value={pickerAdjuntos.filtro}
                      onChange={(e) => setPickerAdjuntos((p) => p ? { ...p, filtro: e.target.value } : p)}
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:border-duetto-primary outline-none"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {pickerAdjuntos.cargando ? (
                    <div className="p-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
                    </div>
                  ) : (() => {
                    const filtro = (pickerAdjuntos.filtro || '').toLowerCase();
                    const filtrados = pickerAdjuntos.items.filter((it) => {
                      if (!filtro) return true;
                      return (
                        String(it.numero || '').toLowerCase().includes(filtro) ||
                        String(it.cliente || '').toLowerCase().includes(filtro)
                      );
                    });
                    if (filtrados.length === 0) {
                      return <div className="p-6 text-center text-sm text-gray-500">No hay resultados</div>;
                    }
                    const cid = pickerAdjuntos.cliente?.id || null;
                    const hid = pickerAdjuntos.highlightId || null;
                    const selId = detalleSeleccionado?.id || null;
                    return (
                      <div className="divide-y">
                        {filtrados.map((item) => {
                          const esSugerido = hid && item.id === hid;
                          const mismoCliente = cid && item.cliente_id === cid;
                          const esSel = selId === item.id;
                          return (
                            <button
                              key={item.id}
                              onClick={() => !adjuntandoRef && cargarDetalleAdjunto(item, pickerAdjuntos.tipo)}
                              disabled={adjuntandoRef}
                              className={`w-full flex flex-col gap-0.5 px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50 ${esSel ? 'bg-duetto-primary/15 border-l-4 border-duetto-primary' : esSugerido ? 'bg-duetto-primary/5 border-l-4 border-duetto-primary/40' : mismoCliente ? 'bg-gray-50' : ''}`}
                            >
                              <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                                {pickerAdjuntos.tipo === 'presupuesto' ? 'Pres.' : 'Fact.'} {item.numero || item.id.slice(0, 8)}
                                {esSugerido && (
                                  <span className="inline-flex items-center gap-1 text-[9px] uppercase font-semibold text-duetto-primary">
                                    <Sparkles className="w-3 h-3" /> Sugerido
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 truncate" title={item.cliente}>{item.cliente || 'Sin cliente'}</div>
                              <div className="text-[11px] text-gray-500">
                                {item.fecha ? new Date(item.fecha).toLocaleDateString('es-ES') : ''}
                                {item.total != null ? ` · ${Number(item.total).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}` : ''}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto bg-gray-50">
                {!detalleSeleccionado ? (
                  <div className="h-full flex items-center justify-center p-6 text-sm text-gray-500 text-center">
                    Selecciona un documento a la izquierda para ver sus datos.
                  </div>
                ) : detalleSeleccionado.cargando ? (
                  <div className="p-6 flex items-center justify-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando vista previa...
                  </div>
                ) : detalleSeleccionado.error || !detalleSeleccionado.datos ? (
                  <div className="p-6 text-sm text-red-600">No se pudo cargar la vista previa</div>
                ) : pickerAdjuntos.tipo === 'presupuesto' ? (
                  <DetallePresupuestoPreview
                    datos={detalleSeleccionado.datos}
                    onAdjuntar={() => {
                      const item = pickerAdjuntos.items.find((i) => i.id === detalleSeleccionado.id);
                      if (!item) return;
                      if (pickerAdjuntos.modo === 'vincular') ejecutarVincularPresupuesto(item.id);
                      else adjuntarDocumento(item);
                    }}
                    adjuntando={adjuntandoRef}
                    etiquetaBoton={pickerAdjuntos.modo === 'vincular' ? 'Vincular este evento' : 'Adjuntar este documento'}
                  />
                ) : (
                  <DetalleFacturaPreview
                    datos={detalleSeleccionado.datos}
                    onAdjuntar={() => {
                      const item = pickerAdjuntos.items.find((i) => i.id === detalleSeleccionado.id);
                      if (item) adjuntarDocumento(item);
                    }}
                    adjuntando={adjuntandoRef}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vista previa de un adjunto del editor o de la factura que se va a enviar */}
      <VisorDocumento
        abierto={!!visorAdjunto}
        onCerrar={() => setVisorAdjunto(null)}
        titulo={visorAdjunto?.titulo}
        url={visorAdjunto?.url}
        mime={visorAdjunto?.mime}
        contenidoBase64={visorAdjunto?.contenidoBase64}
      />

      {/* Ayuda IA del correo abierto: panel a la derecha (pantalla completa en el
          móvil) por encima del drawer de IAmail (z-40) y del editor (z-50) */}
      <AyudaIAPanel
        abierto={ayudaIaAbierta && ayudaIaActiva}
        onCerrar={() => setAyudaIaAbierta(false)}
        correo={detalle}
        onCambios={() => {
          // Lo ejecutado puede tocar el correo (gestión, vínculo, borradores),
          // los listados, los contadores, el panel IAmail y los pendientes
          if (seleccionado) cargarDetalle(seleccionado);
          cargarEmails();
          cargarResumen();
          cargarContadoresFiltro();
          if (carpeta === 'iamail') cargarIaPanel();
          setPendientesVersion((v) => v + 1);
        }}
      />
    </div>
  );
};

export default Emails;
